<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from "vue";
import apiClient from "@/apiClient.js";
import { useAuthStore } from "@/stores/AuthStore";
import { getPresenceTabId } from "@/utils/presenceSession";
import dayjs from "dayjs";
import { onBeforeRouteLeave, useRoute, useRouter } from "vue-router";
import TimesheetReviewWorkspace from "@/components/payroll/TimesheetReviewWorkspace.vue";
import TimesheetSaveErrorDialog from "@/components/payroll/TimesheetSaveErrorDialog.vue";
import PayrollExportBlockersDialog from "@/components/payroll/PayrollExportBlockersDialog.vue";
import { separateWorkDateGroup } from "@/utils/timesheetShiftGrouping";
import { missingImportApprovals } from "@/utils/payrollImportCoverage";
import {
  payrollPageFromWorkspace,
  payrollWorkspaceFromPage,
} from "@/utils/payrollWorkspaceRoute";

const defaultPayrollPeriod = () => {
  const currentMonth = dayjs().startOf("month");
  const start = currentMonth.subtract(1, "month").date(18);

  return {
    pay_period_start: start.format("YYYY-MM-DD"),
    pay_period_end: currentMonth.date(17).format("YYYY-MM-DD"),
  };
};

const auth = useAuthStore();
const route = useRoute();
const router = useRouter();
const runs = ref([]);
const activeRunId = ref(null);
const activeRun = ref(null);
const loadingRuns = ref(false);
const loadingRun = ref(false);
const loadingError = ref("");
const exportBlockersOpen = ref(false);
const highlightedExportClaimId = ref(null);
const resolveExportBlocker = async (item) => {
  exportBlockersOpen.value = false;
  let target;
  if (item.type === 'claim') {
    workspace.value = 'claims';
    claimApprovalFilter.value = 'all';
    highlightedExportClaimId.value = Number(item.id);
    target = `payroll-claim-${item.id}`;
  } else {
    workspace.value = 'timesheets';
    if (item.type === 'import') {
      await openConflictingImport(item);
      return;
    }
    evidenceExpanded.value = true;
    target = `payroll-document-${item.id}`;
  }
  await nextTick();
  const element = document.getElementById(target);
  if (!element) {
    showToast('This item is not loaded. Refresh the payroll run and reopen the blocker list.', 'error');
    return;
  }
  element.scrollIntoView({ behavior: 'smooth', block: 'center' });
  element.focus({ preventScroll: true });
};
const timesheetSaveError = ref(null);
const showTimesheetSaveError = (record, messages, details = {}) => {
  timesheetSaveError.value = {
    recordId: record.id, runId: activeRunId.value, file: record.source_file_name || 'Timesheet', messages,
    conflicts: details.conflicts || [], steps: details.resolution_steps || [],
  };
};
const openConflictingImport = async (source) => {
  try {
    const response = await apiClient.get('/api/timesheet-import-records', {
      params: { payroll_run_id: activeRunId.value, record_id: source.id, status: 'all' },
    });
    const record = response.data?.records?.find(item => Number(item.id) === Number(source.id));
    if (!record) throw new Error('The linked import is no longer available in this payroll run.');
    if (!timesheetImportRecords.value.some(item => item.id === record.id)) timesheetImportRecords.value.push(record);
    timesheetSaveError.value = null;
    timesheetReadinessFilter.value = 'all';
    focusedTimesheetImportId.value = Number(source.id);
  } catch (error) {
    showToast(error?.message || 'Unable to open the linked import.', 'error');
  }
};
const createError = ref("");
const creating = ref(false);
const closingRun = ref(false);
const completePayslipInput = ref(null);
const completePayslipFile = ref(null);
const completePayslipError = ref("");
const uploadingCompletePayslips = ref(false);
const additionalPayslipInput = ref(null);
const additionalPayslipFiles = ref([]);
const additionalPayslipsError = ref("");
const addingPayslips = ref(false);
const timesheetInput = ref(null);
const timesheetFiles = ref([]);
const timesheetUploadError = ref("");
const uploadingTimesheets = ref(false);
const reprocessingDocumentIds = ref(new Set());
const deletingTimesheetDocumentIds = ref(new Set());
const sendingAll = ref(false);
const refreshingClaims = ref(false);
const downloadingAccountantWorkbook = ref(false);
const loadingImports = ref(false);
const loadingUnclaimedWork = ref(false);
const unclaimedWorkError = ref("");
const categorizingMissingTimesheetKeys = ref(new Set());
const claimRepairOpen = ref(false);
const loadingClaimRepairs = ref(false);
const applyingClaimRepairs = ref(false);
const claimRepairError = ref("");
const claimRepairPreview = ref({
  summary: { issues: 0, repairable: 0, manual_review: 0 },
  repairs: [],
});
const selectedClaimRepairIds = ref(new Set());
const unclaimedWorkReport = ref({
  summary: {
    scheduled: 0,
    claimed: 0,
    missing_employees: 0,
    missing_timesheets: 0,
    missing_timesheet_claims_received: 0,
    contractor_assignments: 0,
    contractor_payroll_claims: 0,
  },
  records: [],
});
const importStatusFilter = ref("all");
const timesheetReadinessFilter = ref("all");
const retainedReviewIds = ref(new Set());
const importCorrectionResults = reactive({});
const advancedTimesheetTable = ref(true);
const refreshingMatches = ref(false);
const claimApprovalFilter = ref("all");
const focusedTimesheetImportId = ref(null);
const timesheetImagePreview = ref(null);
const timesheetImagePreviewWidth = ref(0);
const timesheetImageZoom = ref(1);
const timesheetImageRotation = ref(0);
const timesheetImagePreviewError = ref(false);
const evidenceExpanded = ref(false);
const timesheetPresence = ref([]);
const activeImportRow = ref(null);
const importSaveState = reactive({});
const sendingIds = ref(new Set());
const viewingIds = ref(new Set());
const savingClaimIds = ref(new Set());
const savingStaffCheckIds = ref(new Set());
const savingImportIds = ref(new Set());
const expandedPayrollSummaryRows = ref(new Set());
const sendMessage = ref("");
const auditOpen = ref(false);
const auditLoading = ref(false);
const auditData = ref(null);
const settingsLoading = ref(false);
const settingsSaving = ref(false);
const settingsError = ref("");
const settingsNotice = ref("");
const workspace = ref(payrollWorkspaceFromPage(route.query.page));
const staffSearch = reactive({
  payslipId: null,
  query: "",
  loading: false,
  results: [],
  error: "",
});
const emailOverrides = reactive({});
const claimEdits = reactive({});
const claimEditSnapshots = new Map();
const importEdits = reactive({});
const availableProducts = ref([]);
const timesheetImportRecords = ref([]);
const timesheetImportSummary = ref({
  total: 0,
  imported: 0,
  needs_review: 0,
  excluded: 0,
  entry_rows: 0,
});
const toast = reactive({
  visible: false,
  message: "",
  tone: "success",
});
let toastTimeout = null;
let timesheetProcessingPollTimeout = null;
let timesheetProcessingPollInFlight = false;
let timesheetCollaborationTimeout = null;
let timesheetCollaborationInFlight = false;
let finalPayrollChecksPollTimeout = null;
let finalPayrollChecksPollInFlight = false;
const importAutosaveTimers = new Map();
const importAutosaveRequests = new Map();
const importAutosaveSnapshots = new Map();
const importEditWatchStops = new Map();
const timesheetImportOrder = new Map();
let timesheetImportOrderRunId = null;
const presenceTabId = getPresenceTabId();
let lastFullTimesheetSyncAt = 0;
let timesheetImageResizeStart = null;

const timesheetRecordIsImage = (record) => {
  const kind = String(record?.source_kind || "").toLowerCase();
  const extension = String(record?.source_file_name || "")
    .split(".")
    .pop()
    .toLowerCase();
  return kind === "image" || ["jpg", "jpeg", "png", "webp", "gif", "tif", "tiff"].includes(kind) ||
    ["jpg", "jpeg", "png", "webp", "gif", "tif", "tiff"].includes(extension);
};
const clampTimesheetImagePreviewWidth = (width) =>
  Math.min(Math.max(Number(width) || 320, 320), Math.max(320, window.innerWidth * 0.8));
const openTimesheetImagePreview = (record) => {
  if (!record?.source_view_url || !timesheetRecordIsImage(record)) return;
  timesheetImagePreview.value = {
    recordId: Number(record.id),
    name: record.source_display_name || record.source_file_name || "Original timesheet",
    url: record.source_view_url,
  };
  timesheetImagePreviewWidth.value = clampTimesheetImagePreviewWidth(window.innerWidth / 3);
  timesheetImageZoom.value = 1;
  timesheetImageRotation.value = 0;
  timesheetImagePreviewError.value = false;
};
const closeTimesheetImagePreview = () => {
  timesheetImagePreview.value = null;
  timesheetImagePreviewError.value = false;
  timesheetImageResizeStart = null;
  document.body.style.cursor = "";
  document.body.style.userSelect = "";
  window.removeEventListener("pointermove", resizeTimesheetImagePreview);
  window.removeEventListener("pointerup", stopTimesheetImagePreviewResize);
  window.removeEventListener("pointercancel", stopTimesheetImagePreviewResize);
};
const adjustTimesheetImageZoom = (amount) => {
  timesheetImageZoom.value = Math.min(
    4,
    Math.max(0.25, Number((timesheetImageZoom.value + amount).toFixed(2))),
  );
};
const rotateTimesheetImage = (degrees) => {
  timesheetImageRotation.value =
    (timesheetImageRotation.value + degrees + 360) % 360;
};
const resetTimesheetImageView = () => {
  timesheetImageZoom.value = 1;
  timesheetImageRotation.value = 0;
};
const resizeTimesheetImagePreview = (event) => {
  if (!timesheetImageResizeStart) return;
  const movement = timesheetImageResizeStart.pointerX - event.clientX;
  timesheetImagePreviewWidth.value = clampTimesheetImagePreviewWidth(
    timesheetImageResizeStart.width + movement,
  );
};
const stopTimesheetImagePreviewResize = () => {
  timesheetImageResizeStart = null;
  document.body.style.cursor = "";
  document.body.style.userSelect = "";
  window.removeEventListener("pointermove", resizeTimesheetImagePreview);
  window.removeEventListener("pointerup", stopTimesheetImagePreviewResize);
  window.removeEventListener("pointercancel", stopTimesheetImagePreviewResize);
};
const startTimesheetImagePreviewResize = (event) => {
  timesheetImageResizeStart = {
    pointerX: event.clientX,
    width: timesheetImagePreviewWidth.value,
  };
  document.body.style.cursor = "col-resize";
  document.body.style.userSelect = "none";
  window.addEventListener("pointermove", resizeTimesheetImagePreview);
  window.addEventListener("pointerup", stopTimesheetImagePreviewResize);
  window.addEventListener("pointercancel", stopTimesheetImagePreviewResize);
};
const handleTimesheetImagePreviewKeydown = (event) => {
  if (event.key === "Escape" && timesheetImagePreview.value) {
    closeTimesheetImagePreview();
  }
};

const captureViewport = () => ({
  left: window.scrollX,
  top: window.scrollY,
});
const restoreViewport = async (position) => {
  await nextTick();
  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(() => {
      window.scrollTo({
        left: position.left,
        top: position.top,
        behavior: "auto",
      });
    });
  });
};
const createForm = ref({
  name: "",
  tax_month: "",
  ...defaultPayrollPeriod(),
  payslips_pdf: null,
  supporting_document: null,
});

watch(
  () => createForm.value.pay_period_start,
  (value) => {
    const start = dayjs(value);
    if (!value || !start.isValid()) {
      return;
    }

    createForm.value.pay_period_end = start
      .add(1, "month")
      .date(17)
      .format("YYYY-MM-DD");
  },
);
const settingsForm = ref({
  pay_run_start_date: "",
  pay_run_finish_date: "",
  claim_tolerance_minutes: "",
  accountant_export_rates: {
    FA: "",
    PTS: "",
    EMT: "",
  },
  cost_estimation_settings: {
    holiday_pay_percent: "",
    employee_pension_percent: "",
    employer_pension_percent: "",
    employer_ni_percent: "",
  },
});

const isSuper = computed(() => auth.hasRole("super"));
const isSettingsPage = computed(() => route.name === "payroll-settings");
const pageTitle = computed(() =>
  isSettingsPage.value ? "Payroll Settings" : "Payroll Runs",
);
const pageDescription = computed(() =>
  isSettingsPage.value
    ? "Manage payroll defaults and tolerances reserved for super users."
    : "Prepare timesheet claims, export accountant payroll, and distribute returned payslips.",
);
const sortedRuns = computed(() => runs.value || []);
const unresolvedCount = computed(
  () =>
    (activeRun.value?.payslips || []).filter(
      (payslip) => !payslip.recipient_email,
    ).length,
);
const readyCount = computed(
  () =>
    (activeRun.value?.payslips || []).filter(
      (payslip) => payslip.status === "ready",
    ).length,
);
const sentCount = computed(
  () =>
    (activeRun.value?.payslips || []).filter(
      (payslip) => payslip.status === "sent",
    ).length,
);
const errorCount = computed(
  () =>
    (activeRun.value?.payslips || []).filter(
      (payslip) => payslip.status === "error",
    ).length,
);
const reviewPayslips = computed(() =>
  (activeRun.value?.payslips || []).filter(
    (payslip) => !payslip.recipient_email || payslip.status === "error",
  ),
);
const matchedCount = computed(
  () =>
    (activeRun.value?.payslips || []).filter((payslip) => payslip.staff)
      .length,
);
const activeClaims = computed(() => activeRun.value?.claims || []);
const missingEmployeeWork = computed(() =>
  (unclaimedWorkReport.value?.records || []).filter(
    (record) =>
      record.employment_type === "employee" &&
      !record.has_claim &&
      !record.missing_timesheet,
  ),
);
const missingTimesheetWork = computed(() =>
  (unclaimedWorkReport.value?.records || []).filter(
    (record) =>
      record.employment_type === "employee" && record.missing_timesheet,
  ),
);
const contractorWork = computed(() =>
  (unclaimedWorkReport.value?.records || []).filter(
    (record) => record.employment_type === "contractor",
  ),
);
const claimRepairRows = computed(() => claimRepairPreview.value?.repairs || []);
const repairableClaimRows = computed(() =>
  claimRepairRows.value.filter((repair) => repair.repairable),
);
const selectedClaimRepairCount = computed(
  () => selectedClaimRepairIds.value.size,
);
const allRepairableClaimsSelected = computed(
  () =>
    repairableClaimRows.value.length > 0 &&
    repairableClaimRows.value.every((repair) =>
      selectedClaimRepairIds.value.has(repair.payroll_run_claim_id),
    ),
);
const stagedImportRecords = computed(() => timesheetImportRecords.value || []);
const importEntryRowsCount = computed(
  () => timesheetImportSummary.value?.entry_rows || 0,
);
const acceptedClaimsCount = computed(
  () =>
    activeClaims.value.filter((claim) =>
      ["accepted", "overridden"].includes(claim.status),
    ).length,
);
const claimReviewCount = computed(
  () =>
    activeClaims.value.filter((claim) =>
      ["needs_review", "rejected"].includes(claim.status),
    ).length,
);
const excludedClaimsCount = computed(
  () =>
    activeClaims.value.filter((claim) => claim.status === "excluded").length,
);
const reviewClaims = computed(() =>
  activeClaims.value.filter((claim) =>
    ["needs_review", "rejected"].includes(claim.status),
  ),
);
const approvedClaims = computed(() =>
  activeClaims.value.filter((claim) =>
    ["accepted", "overridden"].includes(claim.status),
  ),
);
const claimHasLockedReviewDecision = (claim) => {
  if (claim?.review_decision_locked !== undefined) {
    return Boolean(claim.review_decision_locked);
  }
  if (["overridden", "excluded"].includes(claim?.status)) return true;
  if (claim?.status !== "accepted") return false;
  const latestReview = (claim?.events || []).find(
    (event) => event.event === "reviewed",
  );
  return latestReview?.after?.status === "accepted";
};
const claimIsClaimingLess = (claim) => {
  if (
    claimHasLockedReviewDecision(claim) ||
    !["needs_review", "rejected"].includes(claim?.status) ||
    (claim?.payment_basis || "hourly") !== "hourly" ||
    claim?.claimed_hours === null ||
    claim?.claimed_hours === undefined ||
    claim?.expected_total_hours === null ||
    claim?.expected_total_hours === undefined
  ) {
    return false;
  }

  return Number(claim.claimed_hours) < Number(claim.expected_total_hours) - 0.005;
};
const automaticallyApprovedClaims = computed(() =>
  approvedClaims.value.filter((claim) => !claimHasLockedReviewDecision(claim)),
);
const manuallyAcceptedClaims = computed(() =>
  approvedClaims.value.filter((claim) => claimHasLockedReviewDecision(claim)),
);
const lowerClaims = computed(() => reviewClaims.value.filter(claimIsClaimingLess));
const remainingReviewClaims = computed(() =>
  reviewClaims.value.filter((claim) => !claimIsClaimingLess(claim)),
);
const excludedClaims = computed(() =>
  activeClaims.value.filter((claim) => claim.status === "excluded"),
);
const otherClaims = computed(() =>
  activeClaims.value.filter(
    (claim) =>
      ![
        "needs_review",
        "rejected",
        "accepted",
        "overridden",
        "excluded",
      ].includes(claim.status),
  ),
);
const allClaimSections = computed(() =>
  [
    {
      key: "approved",
      title: "Approved",
      description:
        "Claims Neptune approved automatically because the submitted and expected values match.",
      claims: automaticallyApprovedClaims.value,
      tone: "border-emerald-200 bg-emerald-50 dark:border-emerald-900/40 dark:bg-emerald-950/20",
      countTone:
        "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200",
    },
    {
      key: "reviewed",
      title: "Accepted / Overridden",
      description:
        "Claims explicitly decided by a reviewer. These decisions are locked until individually reset.",
      claims: manuallyAcceptedClaims.value,
      tone: "border-sky-200 bg-sky-50 dark:border-sky-900/40 dark:bg-sky-950/20",
      countTone:
        "bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200",
    },
    {
      key: "claiming-less",
      title: "Claiming Less",
      description:
        "The submitted hours are below Neptune's expected hours. Accept the lower submitted value with one click when it is correct.",
      claims: lowerClaims.value,
      tone: "border-violet-200 bg-violet-50 dark:border-violet-900/40 dark:bg-violet-950/20",
      countTone:
        "bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-200",
    },
    {
      key: "review",
      title: "Needs Review",
      description:
        "Claims that still need a decision or correction before they can feed the accountant workbook.",
      claims: remainingReviewClaims.value,
      tone: "border-amber-200 bg-amber-50 dark:border-amber-900/40 dark:bg-amber-950/20",
      countTone:
        "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200",
    },
    {
      key: "excluded",
      title: "Excluded",
      description:
        "Claims intentionally left out of this payroll run.",
      claims: excludedClaims.value,
      tone: "border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-950/40",
      countTone:
        "bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-200",
    },
    {
      key: "other",
      title: "Other Claims",
      description:
        "Claims with a status outside the normal review and approval flow.",
      claims: otherClaims.value,
      tone: "border-sky-200 bg-sky-50 dark:border-sky-900/40 dark:bg-sky-950/20",
      countTone:
        "bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200",
    },
  ],
);
const claimApprovalTabs = computed(() => [
  {
    key: "all",
    title: "All",
    count: activeClaims.value.length,
  },
  { key: 'export-blockers', title: 'Export blockers', count: exportBlockingClaimIds.value.size },
  ...allClaimSections.value.map((section) => ({
    key: section.key,
    title: section.title,
    count: section.claims.length,
  })),
]);
const exportBlockingClaimIds = computed(() => new Set(
  (accountantPreflight.value.blockers || []).filter(item => item.type === 'claim').map(item => Number(item.id)),
));
const exportClaimIssues = (claim) => (accountantPreflight.value.blockers || [])
  .find(item => item.type === 'claim' && Number(item.id) === Number(claim.id))?.issues || [];
const visibleClaimSections = computed(() =>
  allClaimSections.value.map(section => claimApprovalFilter.value === 'export-blockers'
    ? { ...section, claims: section.claims.filter(claim => exportBlockingClaimIds.value.has(Number(claim.id))) } : section).filter(
    (section) =>
      section.claims.length > 0 &&
      (['all', 'export-blockers'].includes(claimApprovalFilter.value) ||
        section.key === claimApprovalFilter.value),
  ),
);
const acceptedHoursTotal = computed(() =>
  activeClaims.value
    .filter((claim) => ["accepted", "overridden"].includes(claim.status))
    .reduce((total, claim) => total + Number(claim.accepted_hours || 0), 0)
    .toFixed(2),
);
const accountantPreflight = computed(() =>
  activeRun.value?.accountant_export_preflight || {
    exportable: false,
    blocking_count: 0,
    errors: ["Prepare and approve payroll claims before export."],
  },
);
const canSendAll = computed(
  () =>
    Boolean(activeRun.value?.payslips?.length) && unresolvedCount.value === 0,
);
const actionButtonClass =
  "btn inline-flex h-9 w-full items-center justify-center px-3 text-sm text-center";
const desktopIconButtonClass =
  "btn inline-flex h-9 w-full items-center justify-center rounded-xl px-2 text-xs text-center whitespace-nowrap";
const importActionIconClass =
  "inline-flex h-7 w-7 shrink-0 items-center justify-center rounded text-sm font-semibold leading-none transition hover:bg-white/70 disabled:cursor-not-allowed disabled:opacity-40 dark:hover:bg-slate-700";
const auditLinkClass =
  "text-xs font-medium text-sky-700 transition hover:text-sky-800 hover:underline dark:text-sky-300 dark:hover:text-sky-200";
const payrollSummaryBuckets = [
  { key: "FA", label: "FA" },
  { key: "DRIVING", label: "Driving" },
  { key: "PTS", label: "PTS" },
  { key: "EMT", label: "EMT" },
  { key: "OTHER", label: "Other" },
];

const payrollStaffChecks = computed(() =>
  new Map(
    (activeRun.value?.staff_checks || []).map((check) => [
      Number(check.staff_id),
      check,
    ]),
  ),
);

const formatMoney = (value) => value ?? "—";
const formatEstimatedMoney = (value) =>
  value === null || value === undefined || value === ""
    ? "—"
    : new Intl.NumberFormat("en-GB", {
        style: "currency",
        currency: "GBP",
      }).format(Number(value));
const deductionMethodLabel = (method) => ({
  statutory_estimate: "Tax code + statutory estimate",
  prior_payslip_rate: "Prior payslip deduction rate",
  contractor: "Contractor: no PAYE deductions",
  incomplete: "Incomplete estimate",
}[method] || "Estimate");

const formatDate = (value) => {
  if (!value) {
    return "—";
  }

  return dayjs(value).format("DD MMM YYYY");
};

const formatDateTime = (value) => {
  if (!value) {
    return "—";
  }

  return dayjs(value).format("DD MMM YYYY HH:mm");
};

const formatScheduledTime = (record) => {
  const start = String(record?.start_time || "").slice(0, 5);
  const finish = String(record?.finish_time || "").slice(0, 5);

  if (!start && !finish) {
    return "—";
  }

  return `${start || "?"}–${finish || "?"}`;
};

const periodLabel = (run) => {
  if (!run) {
    return "";
  }
  if (run.pay_period_start && run.pay_period_end) {
    return `${formatDate(run.pay_period_start)} - ${formatDate(run.pay_period_end)}`;
  }
  return "Period not set";
};

const statusTone = (status) => {
  switch (status) {
    case "sent":
      return "bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200 dark:bg-slate-800 dark:text-emerald-300 dark:ring-slate-700";
    case "partially_sent":
      return "bg-amber-100 text-amber-700 ring-1 ring-amber-200 dark:bg-slate-800 dark:text-amber-300 dark:ring-slate-700";
    case "error":
    case "failed":
      return "bg-rose-100 text-rose-700 ring-1 ring-rose-200 dark:bg-slate-800 dark:text-rose-300 dark:ring-slate-700";
    case "ready":
      return "bg-sky-100 text-sky-700 ring-1 ring-sky-200 dark:bg-slate-800 dark:text-sky-300 dark:ring-slate-700";
    case "accepted":
    case "completed":
      return "bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200 dark:bg-slate-800 dark:text-emerald-300 dark:ring-slate-700";
    case "overridden":
      return "bg-violet-100 text-violet-700 ring-1 ring-violet-200 dark:bg-slate-800 dark:text-violet-300 dark:ring-slate-700";
    case "rejected":
      return "bg-rose-100 text-rose-700 ring-1 ring-rose-200 dark:bg-slate-800 dark:text-rose-300 dark:ring-slate-700";
    case "needs_review":
    case "queued":
    case "processing":
      return "bg-amber-100 text-amber-700 ring-1 ring-amber-200 dark:bg-slate-800 dark:text-amber-300 dark:ring-slate-700";
    case "excluded":
      return "bg-slate-100 text-slate-500 ring-1 ring-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:ring-slate-700";
    case "unmatched":
      return "bg-slate-100 text-slate-700 ring-1 ring-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:ring-slate-700";
    default:
      return "bg-slate-100 text-slate-700 ring-1 ring-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:ring-slate-700";
  }
};

const auditTone = (status) => {
  switch (status) {
    case "opened":
      return "bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200 dark:bg-slate-800 dark:text-emerald-300 dark:ring-slate-700";
    case "delivered":
      return "bg-sky-100 text-sky-700 ring-1 ring-sky-200 dark:bg-slate-800 dark:text-sky-300 dark:ring-slate-700";
    case "processed":
    case "sent":
      return "bg-slate-100 text-slate-700 ring-1 ring-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:ring-slate-700";
    case "bounce":
    case "blocked":
    case "deferred":
    case "dropped":
    case "spamreport":
    case "failed":
      return "bg-rose-100 text-rose-700 ring-1 ring-rose-200 dark:bg-slate-800 dark:text-rose-300 dark:ring-slate-700";
    default:
      return "bg-slate-100 text-slate-700 ring-1 ring-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:ring-slate-700";
  }
};

const matchLabel = (payslip) => {
  if (!payslip.staff) {
    return "No staff match";
  }

  if (!payslip.staff.email) {
    return "Matched staff has no email";
  }

  if (!payslip.matched_by) {
    return "Matched staff record";
  }

  return `Matched by ${payslip.matched_by.replace("_", " ")}`;
};

const recipientLabel = (payslip) =>
  payslip.recipient_email || "No staff email on file";

const recipientHelp = (payslip) => {
  if (!payslip.staff) {
    return "Match this payslip to a staff record before sending.";
  }

  if (!payslip.staff.email) {
    return "Add an email address to the matched staff profile before sending.";
  }

  return "Uses the matched staff contact email.";
};

const claimStaffLabel = (claim) =>
  claim?.staff?.name || claim?.source_staff_name || "";
const claimJobDateLabel = (claim) => {
  const reference = claim?.reference_code || claim?.source_reference || "No reference";
  const date = claim?.job_date?.date || claim?.source_date;
  const start = claim?.job_date?.start_time;
  const finish = claim?.job_date?.finish_time;
  const timeRange =
    start && finish ? ` ${start} - ${finish}` : "";

  return date ? `${reference} · ${formatDate(date)}${timeRange}` : reference;
};
const staffSearchOutput = (staff) =>
  staff?.contact?.name || staff?.name || "Unnamed staff";
const vehicleSearchOutput = (vehicle) => {
  const registration = vehicle?.registrationNumber || "No registration";
  const description = [vehicle?.make, vehicle?.model].filter(Boolean).join(" ");
  const inactive = vehicle?.active === false ? " · inactive" : "";

  return `${registration}${description ? ` · ${description}` : ""}${inactive}`;
};
const jobDateSearchOutput = (jobDate) => {
  const neptuneReference = jobDate?.reference?.txt || "No reference";
  const externalReference = jobDate?.reference?.platform_shift_ref;
  const reference = externalReference
    ? `${neptuneReference} / ${externalReference}`
    : neptuneReference;
  const date = jobDate?.date ? formatDate(jobDate.date) : "No date";
  const start = jobDate?.start_time;
  const finish = jobDate?.finish_time;
  const timeRange = start && finish ? ` ${start} - ${finish}` : "";
  const referenceName = jobDate?.reference?.name;

  return `${reference}${referenceName ? ` · ${referenceName}` : ""} · ${date}${timeRange}`;
};
const extendedJobSearchPeriodStart = (run) => {
  if (!run?.pay_period_start) {
    return "";
  }
  const start = dayjs(run?.pay_period_start);

  return start.isValid()
    ? start.subtract(2, "month").format("YYYY-MM-DD")
    : "";
};
const jobDateSearchUrl = (run, searchAllDates = false) => {
  if (searchAllDates) {
    return "/api/job-date/search?limit=30&query=";
  }

  return `/api/job-date/search?period_start=${encodeURIComponent(
    extendedJobSearchPeriodStart(run),
  )}&period_end=${encodeURIComponent(run?.pay_period_end || "")}&limit=30&query=`;
};
const claimJobDateSearchUrl = (run, searchAllDates = false) =>
  jobDateSearchUrl(run, searchAllDates);
const importJobDateSearchUrl = (run, searchAllDates = false) =>
  jobDateSearchUrl(run, searchAllDates);
const isOutsidePayrollPeriod = (date, run) => {
  if (!date || !run?.pay_period_start || !run?.pay_period_end) {
    return false;
  }
  const value = dayjs(date);
  const start = dayjs(run?.pay_period_start);
  const end = dayjs(run?.pay_period_end);

  if (!value.isValid() || !start.isValid() || !end.isValid()) {
    return false;
  }

  return value.isBefore(start, "day") || value.isAfter(end, "day");
};

const importRecordTitle = (record) =>
  record.source_display_name ||
  record.source_file_name ||
  record.source_path ||
  `Import #${record.id}`;
const aiVerificationLabel = (record) => {
  const verification = record?.ai_verification;
  if (!verification?.attempted) return "";
  if (!verification.succeeded) return "AI verification failed";
  if (verification.verifier_warnings?.length) return "AI verification uncertain";
  return verification.change_count > 0
    ? `AI corrected ${verification.change_count}`
    : "AI verified";
};
const aiVerificationTone = (record) => {
  const verification = record?.ai_verification;
  if (!verification?.succeeded) {
    return "bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200";
  }
  if (verification.verifier_warnings?.length) {
    return "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200";
  }
  return verification.change_count > 0
    ? "bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200"
    : "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200";
};
const aiVerificationTitle = (record) => {
  const verification = record?.ai_verification;
  if (!verification) return "";
  if (verification.error) return verification.error;
  if (verification.verifier_warnings?.length) {
    return verification.verifier_warnings.join("\n");
  }
  if (verification.changes?.length) {
    return verification.changes
      .slice(0, 12)
      .map((change) => `${change.field}: ${change.before ?? "blank"} → ${change.after ?? "blank"}`)
      .join("\n");
  }
  return `${verification.model || "OpenAI"} independently agreed with the first extraction.`;
};
const importSourceDocument = (record) =>
  (activeRun.value?.timesheet_documents || []).find(
    (document) => Number(document.id) === Number(record?.source_document_id),
  );
const aiVerificationRetryPending = (record) =>
  reprocessingDocumentIds.value.has(record?.source_document_id) ||
  ["queued", "processing"].includes(importSourceDocument(record)?.status);

const blankImportEntry = (sequence = 1) => ({
  sequence,
  is_shift_lead: true,
  shift_group_key: `manual-${sequence}`,
  date: "",
  shift_reference: "",
  reference_type: "",
  patient_reference: "",
  patient_category: "",
  job_start_time: "",
  job_start_postcode: "",
  job_end_time: "",
  job_end_postcode: "",
  work_start_time: "",
  work_finish_time: "",
  agreed_rate: "",
  claimed_hours: "",
  hours_driving: "",
  payment_basis: "hourly",
  quantity: "",
  unit_rate: "",
  amount: "",
  job_date_id: "",
  job_date_date: "",
  job_date_query: "",
  search_all_job_dates: false,
  product_id: "",
  match_suggestions: [],
  co_worker: "",
  co_worker_staff_id: "",
  co_worker_query: "",
  vehicle_registration: "",
  vehicle_id: "",
  vehicle_query: "",
  notes: "",
});

const shiftReferenceType = (value) => {
  const normalized = String(value || "").trim().toUpperCase();
  if (normalized.startsWith("M1SJ")) return "M1SJ";
  if (normalized.startsWith("M1S")) return "M1S";
  return "";
};
const jobDateReferenceType = (jobDate) =>
  String(jobDate?.reference?.type?.prefix || jobDate?.reference?.reference_type || "")
    .trim()
    .toUpperCase() || shiftReferenceType(jobDate?.reference?.txt);
const importEntryIsShift = (entry) =>
  ["M1S", "M1SJ"].includes(
    String(
      entry?.reference_type ||
        shiftReferenceType(entry?.job_date_query) ||
        shiftReferenceType(entry?.shift_reference),
    ).toUpperCase(),
  );
const sharedShiftEntries = (entry, record) => {
  if (!record || !importEntryIsShift(entry)) return [entry];
  return importEntriesForRecord(record).filter(
    (candidate) => candidate.shift_group_key === entry.shift_group_key,
  );
};
const hydrateSharedShiftValues = (entries) => {
  const groups = new Map();
  entries.forEach((entry) => {
    if (!importEntryIsShift(entry)) return;
    const key = entry.shift_group_key || `entry-${entry.sequence}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(entry);
  });
  groups.forEach((group) => {
    const preferred = group.find((entry) => entry.is_shift_lead) || group[0];
    const hasValue = (entry, fields) => fields.some(
      (field) => entry?.[field] !== "" && entry?.[field] !== null && entry?.[field] !== undefined,
    );
    const sourceFor = (fields) =>
      (hasValue(preferred, fields)
        ? preferred
        : group.find((entry) => hasValue(entry, fields))) || preferred;
    const coWorkerSource = sourceFor([
      "co_worker_staff_id",
      "co_worker_query",
      "co_worker",
    ]);
    const vehicleSource = sourceFor([
      "vehicle_id",
      "vehicle_query",
      "vehicle_registration",
    ]);
    const productSource = sourceFor(["product_id"]);
    group.forEach((entry) => {
      entry.co_worker_staff_id = coWorkerSource?.co_worker_staff_id ?? "";
      entry.co_worker_query = coWorkerSource?.co_worker_query || "";
      entry.co_worker = coWorkerSource?.co_worker || "";
      entry.vehicle_id = vehicleSource?.vehicle_id ?? "";
      entry.vehicle_query = vehicleSource?.vehicle_query || "";
      entry.vehicle_registration = vehicleSource?.vehicle_registration || "";
      entry.product_id = productSource?.product_id ?? "";
    });
  });
  return entries;
};

const normalizeImportEntries = (entries, record) => {
  const sourceEntries =
    Array.isArray(entries) && entries.length
      ? entries
      : [
          {
            sequence: 1,
            source_date: record?.source_date,
            work_start_time: record?.start_of_work_time,
            work_finish_time: record?.end_of_work_day_time,
            agreed_rate: record?.rate_code,
            claimed_hours: record?.claimed_hours,
            payment_basis: record?.payment_basis,
            quantity: record?.claimed_quantity,
            unit_rate: record?.claimed_unit_rate,
            amount: record?.claimed_amount,
            product_id: record?.product?.id,
            co_worker: record?.co_worker_name,
            vehicle_registration: record?.vehicle_registration,
            notes: record?.notes,
          },
        ];
  const isSingleEntry = sourceEntries.length === 1;

  const normalizedEntries = sourceEntries.map((entry, index) => {
    const sequence = Number(entry?.sequence ?? index + 1);
    const segment =
      (record?.segments || []).find((candidate) =>
        (candidate?.entry_sequences || []).includes(sequence),
      ) ||
      (record?.segments || []).find(
        (candidate) => Number(candidate?.sequence) === sequence,
      ) ||
      (isSingleEntry ? record?.segments?.[0] : null);
    const segmentSequences = (segment?.entry_sequences || []).map(Number);
    const entryJobDateId = Number(entry?.job_date_id || 0);
    const segmentJobDateId = Number(segment?.job_date?.id || 0);
    const movedToDifferentJob =
      entryJobDateId > 0 &&
      segmentJobDateId > 0 &&
      entryJobDateId !== segmentJobDateId;
    const storedShiftLead =
      typeof entry?.is_shift_lead === "boolean"
        ? entry.is_shift_lead
        : null;
    const isComponentBreakdown =
      segment?.component_type === "mixed" || Number(segment?.claimed_driving_hours || 0) > 0;
    const separatePaidShift = String(entry?.shift_group_key || '').startsWith('separate-paid-');
    const dailyGroup = separateWorkDateGroup(sourceEntries, { ...entry, sequence }, segment);
    const isShiftLead = separatePaidShift ? true : dailyGroup ? dailyGroup.isLead : isComponentBreakdown
      ? true
      : movedToDifferentJob
      ? true
      : storedShiftLead ?? (segmentSequences.length
          ? sequence === Math.min(...segmentSequences)
          : segment
            ? Number(segment.sequence) === sequence
            : true);
    const jobDate =
      segment?.job_date || (isSingleEntry ? record?.job_date : null);
    const vehicle = entry?.vehicle || (isSingleEntry ? record?.vehicle : null);

    return {
      id: entry?.id ?? null,
      sequence,
      date:
        entry?.source_date ||
        entry?.date ||
        (isSingleEntry ? record?.source_date : "") ||
        "",
      shift_reference:
        entry?.shift_reference || record?.source_reference || "",
      reference_type:
        String(entry?.reference_type || jobDateReferenceType(jobDate)).toUpperCase() ||
        shiftReferenceType(entry?.shift_reference || record?.source_reference),
      patient_reference: entry?.patient_reference || "",
      patient_category: entry?.patient_category || "",
      job_start_time: entry?.job_start_time || "",
      job_start_postcode: entry?.job_start_postcode || "",
      job_end_time: entry?.job_end_time || "",
      job_end_postcode: entry?.job_end_postcode || "",
      work_start_time:
        isShiftLead
          ? entry?.work_start_time ||
            (!dailyGroup && segment?.source_start_time) ||
            (!dailyGroup && record?.start_of_work_time) ||
            ""
          : "",
      work_finish_time:
        isShiftLead
          ? entry?.work_finish_time ||
            (!dailyGroup && segment?.source_finish_time) ||
            (!dailyGroup && record?.end_of_work_day_time) ||
            ""
          : "",
      component_type:
        entry?.component_type ||
        (String(entry?.agreed_rate || "").toUpperCase().startsWith("DRIV")
          ? "job_driving"
          : "work"),
      is_shift_lead: isShiftLead,
      shift_group_key: separatePaidShift ? entry.shift_group_key : dailyGroup ? dailyGroup.key : movedToDifferentJob
        ? `job-${entryJobDateId}`
        : entry?.shift_group_key || (segment
        ? String(segment.id ?? segment.sequence)
        : `entry-${sequence}`),
      agreed_rate:
        entry?.agreed_rate || (isSingleEntry ? record?.rate_code : "") || "",
      claimed_hours:
        isShiftLead
          ? entry?.claimed_hours ??
            (dailyGroup ? null : segment?.source_claimed_hours) ??
            (dailyGroup ? null : record?.claimed_hours) ??
            ""
          : "",
      calculated_hours:
        isShiftLead
          ? entry?.calculated_hours ?? segment?.calculated_hours ?? ""
          : "",
      hours_driving:
        isShiftLead
          ? entry?.hours_driving ??
            (isSingleEntry ? record?.hours_driving ?? "" : "")
          : "",
      payment_basis:
        entry?.payment_basis ||
        (isSingleEntry ? record?.payment_basis : "") ||
        "hourly",
      quantity:
        entry?.quantity ??
        (isSingleEntry ? (record?.claimed_quantity ?? "") : ""),
      unit_rate:
        entry?.unit_rate ??
        (isSingleEntry ? (record?.claimed_unit_rate ?? "") : ""),
      amount:
        entry?.amount ??
        (isSingleEntry ? (record?.claimed_amount ?? "") : ""),
      job_date_id: entry?.job_date_id ?? jobDate?.id ?? "",
      match_method: entry?.match_method || null,
      job_date_date: jobDate?.date ?? "",
      job_date_query: jobDate
        ? jobDateSearchOutput(jobDate)
        : entry?.shift_reference || record?.source_reference || "",
      search_all_job_dates: false,
      product_id:
        entry?.product_id ?? record?.product?.id ?? "",
      match_suggestions: Array.isArray(segment?.match_suggestions)
        ? segment.match_suggestions
        : [],
      co_worker:
        entry?.co_worker ||
        segment?.co_worker?.name ||
        record?.co_worker?.name ||
        record?.co_worker_name ||
        "",
      co_worker_staff_id:
        entry?.co_worker_staff_id ??
        segment?.co_worker_staff_id ??
        record?.co_worker_staff_id ??
        "",
      co_worker_query:
        segment?.co_worker?.name ||
        record?.co_worker?.name ||
        entry?.co_worker ||
        record?.co_worker_name ||
        "",
      vehicle_registration:
        entry?.vehicle_registration || record?.vehicle_registration || "",
      vehicle_id:
        entry?.vehicle_id ??
        vehicle?.id ??
        (isSingleEntry ? record?.vehicle_id : "") ??
        "",
      vehicle_query: vehicle
        ? vehicleSearchOutput(vehicle)
        : entry?.vehicle_registration || record?.vehicle_registration || "",
      notes: entry?.notes || record?.notes || "",
    };
  });

  return hydrateSharedShiftValues(normalizedEntries);
};

const makeSeparatePaidShift = (row) => {
  const original = (row.record.entries || []).find(entry => Number(entry.sequence) === Number(row.entry.sequence));
  Object.assign(row.entry, {
    is_shift_lead: true,
    shift_group_key: `separate-paid-${row.entry.sequence}`,
    work_start_time: original?.work_start_time || '',
    work_finish_time: original?.work_finish_time || '',
    claimed_hours: original?.claimed_hours ?? '',
    calculated_hours: '',
  });
  retainedReviewIds.value = new Set([...retainedReviewIds.value, row.record.id]);
  showToast('This row now has its own paid start and finish. Check its work date and job reference.');
};

const importEntriesForRecord = (record) => importEdit(record).entries || [];

const addImportEntry = (record) => {
  if (activeRun.value?.status !== 'draft' || savingImportIds.value.has(record.id) || aiVerificationRetryPending(record)) return;
  const entries = importEntriesForRecord(record);
  const nextSequence = Math.max(0, ...entries.map((entry) => Number(entry.sequence) || 0)) + 1;
  const entry = blankImportEntry(nextSequence);
  if (record.status === 'imported') {
    entry.shift_group_key = `separate-paid-${nextSequence}`;
  }
  entries.push(entry);
  retainedReviewIds.value = new Set([...retainedReviewIds.value, record.id]);
  queueImportAutosave(record);
  showToast(record.status === 'imported'
    ? 'Row added. Complete its date, job and paid times, then click Save correction. Existing claims are unchanged until saved.'
    : 'Row added. Complete its date, job and paid times.');
};

const removeImportEntry = (record, index) => {
  const entries = importEntriesForRecord(record);
  const [removed] = entries.splice(index, 1);
  if (removed?.is_shift_lead) {
    const replacement = entries.find(
      (entry) => entry.shift_group_key === removed.shift_group_key,
    );
    if (replacement) {
      replacement.is_shift_lead = true;
      replacement.work_start_time = removed.work_start_time;
      replacement.work_finish_time = removed.work_finish_time;
      replacement.claimed_hours = removed.claimed_hours;
      replacement.calculated_hours = removed.calculated_hours;
    }
  }

  return removed;
};

const deleteImportEntry = async (record, index) => {
  const entries = importEntriesForRecord(record);
  const entry = entries[index];
  // Removing a new, unsaved correction row must not reopen the import or
  // delete any of its existing claims.
  if (record.status === 'imported' && entry && !(record.entries || []).some(
    saved => Number(saved.sequence) === Number(entry.sequence),
  )) {
    if (savingImportIds.value.has(record.id) || activeRun.value?.status !== 'draft') return;
    removeImportEntry(record, index);
    queueImportAutosave(record);
    return;
  }
  if (entries.length <= 1) {
    showToast("A timesheet must retain at least one row. Exclude the timesheet instead.", "error");
    return;
  }
  const imported = record.status === "imported";
  const confirmation = imported
    ? "Delete this imported row? Neptune will remove the generated claims for this timesheet, retain the source file, and return it to Needs review. Re-import it after checking the remaining rows."
    : "Delete this timesheet entry? The original source file will be retained.";
  if (!window.confirm(confirmation)) {
    return;
  }

  const viewport = captureViewport();
  const snapshot = entries.map((entry) => ({ ...entry }));
  let reopened = false;
  removeImportEntry(record, index);
  const nextIds = new Set(savingImportIds.value);
  nextIds.add(record.id);
  savingImportIds.value = nextIds;

  try {
    if (imported) {
      await apiClient.post(
        `/api/timesheet-import-records/${record.id}/reopen`,
        { base_updated_at: record.updated_at || null },
      );
      reopened = true;
    }
    await apiClient.put(
      `/api/timesheet-import-records/${record.id}`,
      importPayload(record),
    );
    await loadTimesheetImports();
    if (imported && activeRunId.value) {
      try {
        await refreshClaims({ silent: true });
      } catch {
        showToast(
          "The imported row was deleted, but the claim list could not be refreshed automatically. Use Rebuild Claims before export.",
          "error",
        );
        return;
      }
    }
    showToast(
      imported
        ? "Imported row deleted. Check the remaining rows, then re-import the timesheet."
        : "Timesheet entry deleted.",
    );
  } catch (error) {
    entries.splice(0, entries.length, ...snapshot);
    if (reopened) {
      await loadTimesheetImports();
      if (activeRunId.value) {
        try {
          await refreshClaims({ silent: true });
        } catch {
          // The import remains safely blocked in Needs review.
        }
      }
    }
    showToast(
      error?.response?.data?.errors?.timesheet?.[0] ||
        error?.response?.data?.errors?.payroll_run?.[0] ||
        error?.response?.data?.message ||
        (reopened
          ? "The timesheet was reopened, but the row could not be deleted. Review it before re-importing."
          : "Unable to delete the timesheet entry."),
      "error",
    );
  } finally {
    const after = new Set(savingImportIds.value);
    after.delete(record.id);
    savingImportIds.value = after;
    await restoreViewport(viewport);
  }
};

const reopenImportForCorrection = async (record) => {
  if (
    !record?.id ||
    record.status !== "imported" ||
    activeRun.value?.status !== "draft" ||
    savingImportIds.value.has(record.id)
  ) return;
  if (!window.confirm(
    "Reopen this imported timesheet for correction? Its generated payroll claims and approval decisions will be removed. The source file and extracted rows will be retained so you can correct or delete rows, then re-import.",
  )) return;

  const viewport = captureViewport();
  const next = new Set(savingImportIds.value);
  next.add(record.id);
  savingImportIds.value = next;

  try {
    const response = await apiClient.post(
      `/api/timesheet-import-records/${record.id}/reopen`,
      { base_updated_at: record.updated_at || null },
    );
    await loadTimesheetImports();
    if (activeRunId.value) {
      try {
        await refreshClaims({ silent: true });
      } catch {
        showToast(
          "The timesheet was reopened, but the claim list could not be refreshed automatically. Use Rebuild Claims before export.",
          "error",
        );
        return;
      }
    }
    const removed = Number(response.data?.removed_payroll_claims || 0);
    showToast(
      `Timesheet reopened for correction. ${removed} payroll claim${removed === 1 ? "" : "s"} removed; correct the rows and re-import.`,
    );
  } catch (error) {
    showToast(
      error?.response?.data?.errors?.timesheet?.[0] ||
        error?.response?.data?.errors?.payroll_run?.[0] ||
        error?.response?.data?.message ||
        "Unable to reopen the imported timesheet.",
      "error",
    );
  } finally {
    const after = new Set(savingImportIds.value);
    after.delete(record.id);
    savingImportIds.value = after;
    await restoreViewport(viewport);
  }
};

const formatHoursValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return "—";
  }

  return Number(value).toFixed(2);
};

const formatAcceptedClaimValue = (claim) =>
  claim?.payment_basis && claim.payment_basis !== "hourly"
    ? `${claim.currency === "GBP" || !claim.currency ? "£" : `${claim.currency} `}${Number(claim.accepted_amount || 0).toFixed(2)}`
    : `${formatHoursValue(claim?.accepted_hours)} h`;

const formatOtherSummary = (row) => {
  const parts = [];
  if (Number(row?.OTHER || 0) > 0) {
    parts.push(`${formatHoursValue(row.OTHER)} h`);
  }
  if (Number(row?.other_amount || 0) > 0) {
    parts.push(`£${Number(row.other_amount).toFixed(2)}`);
  }

  return parts.join(" · ") || "—";
};

const payrollSummaryRateTotals = (row) => {
  const totals = new Map();

  const addTotal = (key, label, hours = 0, amount = 0) => {
    if (!totals.has(key)) {
      totals.set(key, {
        key,
        label,
        hours: 0,
        amount: 0,
        claims_count: 0,
      });
    }

    const total = totals.get(key);
    total.hours += Number(hours || 0);
    total.amount += Number(amount || 0);
    total.claims_count += 1;
  };

  (row?.claims || []).forEach((claim) => {
    const rateCode = String(claim?.rate_code || "Unspecified").toUpperCase();

    if (claim?.payment_basis === "hourly") {
      if (Number(claim?.work_hours || 0) > 0) {
        addTotal(
          `hours-${rateCode}`,
          rateCode,
          Number(claim.work_hours || 0),
        );
      }
      if (Number(claim?.driving_hours || 0) > 0) {
        addTotal("hours-DRIVING", "Driving", Number(claim.driving_hours || 0));
      }
      return;
    }

    addTotal(
      `amount-${rateCode}`,
      rateCode === "UNSPECIFIED" ? "Other" : rateCode,
      0,
      Number(claim?.accepted_amount || 0),
    );
  });

  const preferredOrder = ["FA", "DRIVING", "PTS", "EMT"];

  return Array.from(totals.values())
    .map((total) => ({
      ...total,
      hours: Number(total.hours.toFixed(2)),
      amount: Number(total.amount.toFixed(2)),
    }))
    .sort((left, right) => {
      const leftLabel = left.label.toUpperCase();
      const rightLabel = right.label.toUpperCase();
      const leftIndex = preferredOrder.indexOf(leftLabel);
      const rightIndex = preferredOrder.indexOf(rightLabel);
      if (leftIndex !== -1 || rightIndex !== -1) {
        return (leftIndex === -1 ? preferredOrder.length : leftIndex) -
          (rightIndex === -1 ? preferredOrder.length : rightIndex);
      }
      return leftLabel.localeCompare(rightLabel);
    });
};

const formatPayrollRateTotal = (total) => {
  const parts = [];
  if (Number(total?.hours || 0) > 0) {
    parts.push(`${formatHoursValue(total.hours)} h`);
  }
  if (Number(total?.amount || 0) > 0) {
    parts.push(`£${Number(total.amount).toFixed(2)}`);
  }

  return parts.join(" · ") || "—";
};

const claimCalculationTone = (claim) => {
  switch (claim?.system_status) {
    case "accepted":
      return "border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-900/50 dark:bg-emerald-950/20 dark:text-emerald-100";
    case "rejected":
      return "border-rose-200 bg-rose-50 text-rose-900 dark:border-rose-900/50 dark:bg-rose-950/20 dark:text-rose-100";
    default:
      return "border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/20 dark:text-amber-100";
  }
};

const claimCalculationHeadline = (claim) => {
  switch (claim?.system_status) {
    case "accepted":
      return "Neptune auto-approved this claim";
    case "rejected":
      return "Neptune calculated that this claim does not match";
    default:
      return "Neptune could not auto-approve this claim";
  }
};

const claimCalculationBody = (claim) => {
  const meta = claim?.calculation_meta || {};
  if ((claim?.payment_basis || "hourly") !== "hourly") {
    const amount = claim?.claimed_amount;
    const currency = claim?.currency || "GBP";
    return (
      claim?.reason ||
      `${claim.payment_basis} claim${amount === null || amount === undefined ? "" : ` for ${currency} ${Number(amount).toFixed(2)}`} needs a reviewer-confirmed amount.`
    );
  }
  const tolerance = meta?.tolerance_hours;
  const claimed = formatHoursValue(claim?.claimed_hours);
  const expected = formatHoursValue(claim?.expected_total_hours);
  const toleranceText =
    tolerance === null || tolerance === undefined ? null : `${Number(tolerance).toFixed(2)} h`;

  if (claim?.system_status === "accepted") {
    return `Claimed ${claimed} h matched Neptune's expected ${expected} h${toleranceText ? ` within a tolerance of ${toleranceText}` : ""}.`;
  }

  if (claim?.system_status === "rejected") {
    return claim?.reason || `Claimed ${claimed} h did not match Neptune's expected ${expected} h.`;
  }

  return claim?.reason || "Neptune needs more information before it can confirm this claim.";
};

const claimTravelSummary = (claim) => {
  const driving = claim?.calculation_meta?.driving;
  if (!driving) {
    if (Number(claim?.expected_driving_hours || 0) === 0) {
      return "No travel pay applies for this reference under the current payroll rule.";
    }
    return "";
  }

  const outbound = Number(driving?.outbound?.driving_minutes || 0);
  const inbound = Number(driving?.return?.driving_minutes || 0);

  return `Travel was calculated from base to venue and return: ${outbound.toFixed(0)} min outbound, ${inbound.toFixed(0)} min return.`;
};

const claimDifferenceSummary = (claim) => {
  const difference = claim?.calculation_meta?.difference_hours;
  const tolerance = claim?.calculation_meta?.tolerance_hours;

  if (difference === null || difference === undefined) {
    return "";
  }

  const parts = [`Difference ${Number(difference).toFixed(2)} h`];
  if (tolerance !== null && tolerance !== undefined) {
    parts.push(`Tolerance ${Number(tolerance).toFixed(2)} h`);
  }

  return parts.join(" · ");
};

const claimRuleLabel = (claim) => {
  const rule = (claim?.rule_code || claim?.reference_type || "").toUpperCase();
  if (!rule) {
    return "Manual review";
  }

  switch (rule) {
    case "M1E":
    case "M1P":
    case "M1S":
    case "M1SJ":
      return `${rule}: job hours, plus outbound and return driving only for the assigned driver`;
    default:
      return `${rule}: no automatic payroll rule configured`;
  }
};

const claimPayRateCode = (claim) => {
  const explicitRate = String(claim?.rate_code || "")
    .trim()
    .toUpperCase();

  if (explicitRate) {
    return explicitRate;
  }

  const productRate = String(claim?.product?.short || "")
    .trim()
    .toUpperCase();

  return productRate === "ECA" ? "EMT" : productRate;
};

const claimRateBucket = (claim) => {
  if ((claim?.payment_basis || "hourly") !== "hourly") {
    return "OTHER";
  }

  const rate = claimPayRateCode(claim);

  if (["DRIVE", "DRIVING"].includes(rate)) {
    return "DRIVING";
  }

  return ["FA", "PTS", "EMT"].includes(rate) ? rate : "OTHER";
};

const payrollSummaryRows = computed(() => {
  const grouped = new Map();
  const estimateRows = activeRun.value?.cost_estimate?.rows || [];
  const estimatesByStaffId = new Map(
    estimateRows
      .filter((row) => row.staff_id)
      .map((row) => [Number(row.staff_id), row]),
  );
  const estimatesByName = new Map(
    estimateRows.map((row) => [String(row.staff_name || "").trim().toLowerCase(), row]),
  );

  approvedClaims.value.forEach((claim) => {
    const staffName = claim?.staff?.name || claim?.source_staff_name || "Unmatched staff";
    const bucket = claimRateBucket(claim);
    const nonHourly = (claim?.payment_basis || "hourly") !== "hourly";
    const hours = nonHourly
      ? 0
      : Number(
          claim?.accepted_hours ?? claim?.expected_total_hours ?? claim?.claimed_hours ?? 0,
        );
    const drivingHours = nonHourly
      ? 0
      : Math.min(
          hours,
          Math.max(
            0,
            Number(claim?.accepted_driving_hours ?? claim?.expected_driving_hours ?? 0),
          ),
        );
    const workHours = nonHourly
      ? 0
      : Math.max(0, Number(claim?.accepted_work_hours ?? hours - drivingHours));
    const acceptedAmount = Number(claim?.accepted_amount || 0);
    const detail = {
      id: claim.id,
      reference_id: claim?.reference?.id || null,
      reference_code:
        claim?.reference_code || claim?.source_reference || "No reference",
      date: claim?.job_date?.date || claim?.source_date || null,
      bucket,
      bucket_label:
        payrollSummaryBuckets.find((item) => item.key === bucket)?.label || bucket,
      rate_code: claimPayRateCode(claim) || "Unspecified",
      rate_inferred: !claim?.rate_code && Boolean(claimPayRateCode(claim)),
      product_label: claim?.product?.short || claim?.product?.name || "—",
      claimed_hours: Number(claim?.claimed_hours || 0),
      job_hours: Number(claim?.expected_job_hours || 0),
      work_hours: workHours,
      driving_hours: drivingHours,
      expected_total_hours: Number(claim?.expected_total_hours || 0),
      accepted_hours: Number(
        claim?.accepted_hours ?? claim?.expected_total_hours ?? claim?.claimed_hours ?? 0,
      ),
      payment_basis: claim?.payment_basis || "hourly",
      accepted_amount: acceptedAmount,
      currency: claim?.currency || "GBP",
    };

    if (!grouped.has(staffName)) {
      const staffId = claim?.staff?.id || null;
      const staffCheck = staffId
        ? payrollStaffChecks.value.get(Number(staffId))
        : null;
      grouped.set(staffName, {
        staff_name: staffName,
        staff_id: staffId,
        payroll_checked: Boolean(staffCheck),
        payroll_checked_at: staffCheck?.checked_at || null,
        payroll_checked_by: staffCheck?.checked_by?.name || null,
        claims_count: 0,
        total_hours: 0,
        FA: 0,
        DRIVING: 0,
        PTS: 0,
        EMT: 0,
        OTHER: 0,
        other_amount: 0,
        claims: [],
      });
    }

    const row = grouped.get(staffName);
    row.claims_count += 1;
    row.total_hours += hours;
    row[bucket] += workHours;
    row.DRIVING += drivingHours;
    if (nonHourly) {
      row.other_amount += acceptedAmount;
    }
    row.claims.push(detail);
  });

  return Array.from(grouped.values())
    .map((row) => ({
      ...row,
      estimate:
        (row.staff_id ? estimatesByStaffId.get(Number(row.staff_id)) : null) ||
        estimatesByName.get(String(row.staff_name || "").trim().toLowerCase()) ||
        null,
      total_hours: Number(row.total_hours.toFixed(2)),
      FA: Number(row.FA.toFixed(2)),
      DRIVING: Number(row.DRIVING.toFixed(2)),
      PTS: Number(row.PTS.toFixed(2)),
      EMT: Number(row.EMT.toFixed(2)),
      OTHER: Number(row.OTHER.toFixed(2)),
      other_amount: Number(row.other_amount.toFixed(2)),
      claims: row.claims.sort((left, right) => {
        const leftKey = `${left.date || ""}|${left.reference_code}|${left.id}`;
        const rightKey = `${right.date || ""}|${right.reference_code}|${right.id}`;
        return leftKey.localeCompare(rightKey);
      }),
    }))
    .sort((left, right) => left.staff_name.localeCompare(right.staff_name));
});

const togglePayrollSummaryRow = (staffName) => {
  const next = new Set(expandedPayrollSummaryRows.value);
  if (next.has(staffName)) {
    next.delete(staffName);
  } else {
    next.add(staffName);
  }
  expandedPayrollSummaryRows.value = next;
};

const payrollSummaryRowExpanded = (staffName) =>
  expandedPayrollSummaryRows.value.has(staffName);

const setPayrollStaffChecked = async (row) => {
  if (!row.staff_id || savingStaffCheckIds.value.has(row.staff_id)) return;

  const next = new Set(savingStaffCheckIds.value);
  next.add(row.staff_id);
  savingStaffCheckIds.value = next;

  try {
    const response = await apiClient.put(
      `/api/payroll-runs/${activeRunId.value}/staff-checks/${row.staff_id}`,
      { checked: !row.payroll_checked },
    );
    activeRun.value = normalizeRun(response.data.run);
    syncEmailOverrides(activeRun.value);
    syncClaimEdits(activeRun.value, { preserveDirty: true });
    replaceRunSummary(response.data.run);
    showToast(
      row.payroll_checked
        ? `${row.staff_name} marked as not checked.`
        : `${row.staff_name} payroll total checked.`,
    );
  } catch (error) {
    showToast(
      error?.response?.data?.message || "Unable to update the payroll check.",
      "error",
    );
  } finally {
    const after = new Set(savingStaffCheckIds.value);
    after.delete(row.staff_id);
    savingStaffCheckIds.value = after;
  }
};

const showToast = (message, tone = "success") => {
  toast.message = message;
  toast.tone = tone;
  toast.visible = true;

  if (toastTimeout) {
    clearTimeout(toastTimeout);
  }

  toastTimeout = window.setTimeout(() => {
    toast.visible = false;
  }, 3000);
};

const auditSummary = (payslip) => {
  if (!payslip.audit) {
    return "No delivery audit yet.";
  }

  if (payslip.audit.status === "opened") {
    return payslip.audit.opened_count > 1
      ? `Opened ${payslip.audit.opened_count} times`
      : "Opened";
  }

  if (payslip.audit.status === "delivered") {
    return "Delivered";
  }

  if (payslip.audit.status === "processed") {
    return "Accepted by SendGrid";
  }

  if (payslip.audit.status === "sent") {
    return "Sent";
  }

  return payslip.audit.status.replace("_", " ");
};

const sendButtonLabel = (payslip) => {
  if (!payslip.recipient_email) {
    return "No Email";
  }

  if (sendingIds.value.has(payslip.id)) {
    return "Sending...";
  }

  return "Resend";
};

const sendErrorMessage = (error, fallback) =>
  error?.response?.data?.errors?.message?.[0] ||
  error?.response?.data?.errors?.payslips_pdf?.[0] ||
  error?.response?.data?.message ||
  fallback;
const payslipUploadErrorMessage = (error, fallback) => {
  const errors = error?.response?.data?.errors || {};
  const nestedFileError = Object.entries(errors).find(([field]) =>
    field.startsWith("payslips."),
  )?.[1]?.[0];

  return (
    errors?.payslips?.[0] ||
    nestedFileError ||
    error?.response?.data?.message ||
    fallback
  );
};
const settingsErrorMessage = (error, fallback) =>
  error?.response?.data?.errors?.pay_run_start_date?.[0] ||
  error?.response?.data?.errors?.pay_run_finish_date?.[0] ||
  error?.response?.data?.errors?.claim_tolerance_minutes?.[0] ||
  error?.response?.data?.message ||
  fallback;

const setFiles = (event, field) => {
  createForm.value[field] = event.target.files?.[0] ?? null;
};
const setCompletePayslipFile = (event) => {
  completePayslipFile.value = event.target.files?.[0] ?? null;
  completePayslipError.value = "";
};
const setAdditionalPayslipFiles = (event) => {
  additionalPayslipFiles.value = Array.from(event.target.files || []);
  additionalPayslipsError.value = "";
};
const setTimesheetFiles = (event) => {
  timesheetFiles.value = Array.from(event.target.files || []);
  timesheetUploadError.value = "";
};

const normalizeRun = (run) => ({
  ...run,
  payslips: run?.payslips || [],
  claims: run?.claims || [],
  timesheet_documents: run?.timesheet_documents || [],
  staff_checks: run?.staff_checks || [],
  cost_estimate: run?.cost_estimate || { rows: [], totals: {}, warnings: [] },
});
const normalizeSettings = (settings) => ({
  pay_run_start_date: settings?.pay_run_start_date || "",
  pay_run_finish_date: settings?.pay_run_finish_date || "",
  claim_tolerance_minutes: settings?.claim_tolerance_minutes ?? "",
  accountant_export_rates: {
    FA: settings?.accountant_export_rates?.FA ?? "",
    PTS: settings?.accountant_export_rates?.PTS ?? "",
    EMT: settings?.accountant_export_rates?.EMT ?? "",
  },
  cost_estimation_settings: {
    holiday_pay_percent: settings?.cost_estimation_settings?.holiday_pay_percent ?? "",
    employee_pension_percent: settings?.cost_estimation_settings?.employee_pension_percent ?? "",
    employer_pension_percent: settings?.cost_estimation_settings?.employer_pension_percent ?? "",
    employer_ni_percent: settings?.cost_estimation_settings?.employer_ni_percent ?? "",
  },
});
const normalizeProducts = (products) =>
  (Array.isArray(products) ? products : [])
    .map((product) => ({
      id: product.id,
      name: product.name,
      short: product.short,
      label: product.short ? `${product.short} · ${product.name}` : product.name,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
const syncEmailOverrides = (run) => {
  (run?.payslips || []).forEach((payslip) => {
    emailOverrides[payslip.id] = payslip.email_override || "";
  });
};
const normalizedClaimEdit = (claim) => ({
  accepted_hours: claim.accepted_hours ?? claim.expected_total_hours ?? "",
  accepted_work_hours:
    claim.accepted_work_hours ??
    claim.claimed_work_hours ??
    claim.expected_job_hours ??
    "",
  accepted_driving_hours:
    claim.accepted_driving_hours ??
    claim.claimed_driving_hours ??
    claim.expected_driving_hours ??
    "",
  accepted_amount: claim.accepted_amount ?? claim.claimed_amount ?? "",
  accepted_hours_touched: false,
  accepted_work_hours_touched: false,
  accepted_driving_hours_touched: false,
  override_notes: claim.override_notes || "",
  staff_id: claim.staff?.id ?? "",
  job_date_id: claim.job_date?.id ?? "",
  job_date_date: claim.job_date?.date ?? "",
  product_id: claim.product?.id ?? "",
  rate_code: claim.rate_code || "",
  staff_query: claimStaffLabel(claim),
  job_date_query: claimJobDateLabel(claim),
  search_all_job_dates: false,
});
const claimEditFingerprint = (edit) =>
  JSON.stringify({
    accepted_hours: edit?.accepted_hours ?? "",
    accepted_work_hours: edit?.accepted_work_hours ?? "",
    accepted_driving_hours: edit?.accepted_driving_hours ?? "",
    accepted_amount: edit?.accepted_amount ?? "",
    accepted_hours_touched: Boolean(edit?.accepted_hours_touched),
    accepted_work_hours_touched: Boolean(edit?.accepted_work_hours_touched),
    accepted_driving_hours_touched: Boolean(
      edit?.accepted_driving_hours_touched,
    ),
    override_notes: edit?.override_notes || "",
    staff_id: edit?.staff_id ?? "",
    job_date_id: edit?.job_date_id ?? "",
    job_date_date: edit?.job_date_date || "",
    product_id: edit?.product_id ?? "",
    rate_code: edit?.rate_code || "",
    staff_query: edit?.staff_query || "",
    job_date_query: edit?.job_date_query || "",
    search_all_job_dates: Boolean(edit?.search_all_job_dates),
  });
const claimEditIsDirty = (claimId) =>
  Boolean(claimEdits[claimId]) &&
  claimEditSnapshots.has(Number(claimId)) &&
  claimEditFingerprint(claimEdits[claimId]) !==
    claimEditSnapshots.get(Number(claimId));
const syncClaimEdits = (
  run,
  { preserveDirty = true, resetClaimIds = [] } = {},
) => {
  const currentClaimIds = new Set(
    (run?.claims || []).map((claim) => Number(claim.id)),
  );
  const resetIds = new Set(resetClaimIds.map((claimId) => Number(claimId)));

  (run?.claims || []).forEach((claim) => {
    const claimId = Number(claim.id);
    const serverEdit = normalizedClaimEdit(claim);
    const preserveCurrentEdit =
      preserveDirty && !resetIds.has(claimId) && claimEditIsDirty(claimId);

    claimEditSnapshots.set(claimId, claimEditFingerprint(serverEdit));
    if (!preserveCurrentEdit) {
      claimEdits[claim.id] = serverEdit;
    }
  });

  Object.keys(claimEdits).forEach((claimId) => {
    if (!currentClaimIds.has(Number(claimId))) {
      delete claimEdits[claimId];
      claimEditSnapshots.delete(Number(claimId));
    }
  });
};
const syncImportEdits = (records, { preserveExisting = false } = {}) => {
  (records || []).forEach((record) => {
    if (
      preserveExisting &&
      importEdits[record.id] &&
      (importAutosaveTimers.has(record.id) ||
        importSaveState[record.id] === "saving" ||
        activeImportRow.value?.recordId === record.id ||
        importRecordHasUnsavedChanges(record))
    ) {
      return;
    }
    importEdits[record.id] = {
      claimant_name: record.source_staff_name || "",
      date: record.source_date || "",
      shift_reference: record.source_reference || "",
      call_sign: record.call_sign || "",
      start_of_work_time: record.start_of_work_time || "",
      end_of_work_day_time: record.end_of_work_day_time || "",
      agreed_rate: record.rate_code || "",
      total_claim_hours: record.claimed_hours ?? "",
      payment_basis: record.payment_basis || "hourly",
      claimed_quantity: record.claimed_quantity ?? "",
      claimed_unit_rate: record.claimed_unit_rate ?? "",
      claimed_amount: record.claimed_amount ?? "",
      currency: record.currency || "GBP",
      hours_driving: record.hours_driving ?? "",
      vehicle_registration: record.vehicle_registration || "",
      vehicle_id: record.vehicle_id ?? record.vehicle?.id ?? "",
      vehicle_query: record.vehicle
        ? vehicleSearchOutput(record.vehicle)
        : record.vehicle_registration || "",
      co_worker: record.co_worker_name || "",
      co_worker_staff_id: record.co_worker_staff_id ?? "",
      notes: record.notes || "",
      staff_id: record.staff?.id ?? "",
      job_date_id: record.job_date?.id ?? "",
      product_id: record.product?.id ?? "",
      staff_query: record.staff?.name || record.source_staff_name || "",
      job_date_query: record.job_date ? jobDateSearchOutput(record.job_date) : "",
      entries: normalizeImportEntries(record.entries, record),
    };
    importAutosaveSnapshots.set(
      record.id,
      JSON.stringify(importPayload(record)),
    );
    ensureImportAutosaveWatcher(record.id);
  });
};

function ensureImportAutosaveWatcher(recordId) {
  if (importEditWatchStops.has(recordId)) return;
  const stop = watch(
    () => importEdits[recordId],
    () => {
      const record = timesheetImportRecords.value.find(
        (item) => item.id === Number(recordId),
      );
      if (!record || !importEdits[recordId]) return;
      const fingerprint = JSON.stringify(importPayload(record));
      if (fingerprint !== importAutosaveSnapshots.get(Number(recordId))) {
        queueImportAutosave(recordId);
      }
    },
    { deep: true },
  );
  importEditWatchStops.set(Number(recordId), stop);
}
const claimEdit = (claim) => {
  if (!claimEdits[claim.id]) {
    claimEdits[claim.id] = normalizedClaimEdit(claim);
    claimEditSnapshots.set(
      Number(claim.id),
      claimEditFingerprint(claimEdits[claim.id]),
    );
  }

  return claimEdits[claim.id];
};
const importEdit = (record) => {
  if (!importEdits[record.id]) {
    syncImportEdits([record]);
  }

  return importEdits[record.id];
};
const approvalSourceIds = computed(() => new Set(
  activeClaims.value.map((claim) => Number(claim.source_claim_id)).filter(Boolean),
));
const allTimesheetSpreadsheetGroups = computed(() =>
  stagedImportRecords.value.map((record) => {
      const edit = importEdit(record);
      const rows = (edit.entries || []).map((entry, entryIndex) => ({
          key: `${record.id}-${entry.id || entryIndex}`,
          record,
          edit,
          entry,
          entryIndex,
        }));

      const group = {
        key: record.id,
        record,
        edit,
        rows,
      };
      group.issues = ["imported", "excluded"].includes(record.status)
        ? []
        : timesheetReviewIssues(group);
      group.missingApprovals = activeRun.value && !loadingRun.value
        ? missingImportApprovals(record, approvalSourceIds.value)
        : [];
      return group;
    }),
);
const timesheetSpreadsheetGroups = computed(() =>
  allTimesheetSpreadsheetGroups.value.filter((group) => {
      if (retainedReviewIds.value.has(group.record.id)) return true;
      if (timesheetReadinessFilter.value === "broken") {
        return group.missingApprovals.length > 0;
      }
      if (timesheetReadinessFilter.value === "duplicates") {
        return group.record.duplicate_submissions?.length > 0;
      }
      if (advancedTimesheetTable.value && focusedTimesheetImportId.value) {
        return Number(group.record.id) === Number(focusedTimesheetImportId.value);
      }
      if (timesheetReadinessFilter.value === "all") {
        return true;
      }

      const needsReview = group.issues.length > 0;
      return timesheetReadinessFilter.value === "needs_review"
        ? needsReview
        : !needsReview && !["imported", "excluded"].includes(group.record.status);
    }),
);
const timesheetFocusStorageKey = (runId) =>
  `payroll-timesheet-focus:${Number(runId || 0)}`;
const restoreTimesheetFocus = (runId) => {
  const stored = Number(window.sessionStorage.getItem(timesheetFocusStorageKey(runId)) || 0);
  focusedTimesheetImportId.value = stored || null;
};
const clearTimesheetFocus = () => {
  window.sessionStorage.removeItem(timesheetFocusStorageKey(activeRunId.value));
  focusedTimesheetImportId.value = null;
};
const toggleTimesheetFocus = (record) => {
  if (Number(focusedTimesheetImportId.value) === Number(record.id)) {
    clearTimesheetFocus();
    return;
  }

  focusedTimesheetImportId.value = Number(record.id);
  window.sessionStorage.setItem(
    timesheetFocusStorageKey(activeRunId.value),
    String(record.id),
  );
};
const calculatedEntryHours = (entry) => {
  if ((entry?.payment_basis || "hourly") !== "hourly") {
    return null;
  }

  const parseMinutes = (value) => {
    const match = String(value || "").match(/^(\d{1,2}):(\d{2})/);
    if (!match) {
      return null;
    }
    const hour = Number(match[1]);
    const minute = Number(match[2]);
    return hour <= 23 && minute <= 59 ? hour * 60 + minute : null;
  };
  const start = parseMinutes(entry?.work_start_time);
  let finish = parseMinutes(entry?.work_finish_time);
  if (start === null || finish === null) {
    return entry?.calculated_hours === "" ? null : entry?.calculated_hours;
  }
  if (finish <= start) {
    finish += 24 * 60;
  }

  return Math.round(((finish - start) / 60) * 100) / 100;
};
const entryHoursDiffer = (entry) => {
  const written = Number(entry?.claimed_hours);
  const calculated = Number(calculatedEntryHours(entry));

  return entry?.claimed_hours !== ""
    && entry?.claimed_hours !== null
    && calculatedEntryHours(entry) !== null
    && Math.abs(written - calculated) > 0.02;
};
const entryIsDrivingComponent = (entry) =>
  ["outbound_driving", "job_driving", "return_driving"].includes(
    entry?.component_type,
  );
const entryHasDrivingHours = (entry) =>
  entry?.hours_driving !== "" &&
  entry?.hours_driving !== null &&
  Number(entry?.hours_driving) > 0;
const entryRateCode = (entry, entries = []) => {
  const lead = entries.find(
    (candidate) =>
      candidate.is_shift_lead &&
      candidate.shift_group_key === entry.shift_group_key,
  );
  return String(entry?.agreed_rate || lead?.agreed_rate || "")
    .trim()
    .toUpperCase();
};
const incompletePtsJourney = (entry, entries) =>
  entryRateCode(entry, entries) === "PTS" &&
  !!String(entry?.patient_reference || "").trim() &&
  (!entry?.job_start_time || !entry?.job_end_time);
const entryReviewIssues = (row, includeStaff = true) => {
  const issues = [];
  const add = (short, message) => issues.push({ short, message });

  if (includeStaff && !row.edit.staff_id) {
    add(
      "Staff not matched",
      `Select Staff for “${row.edit.claimant_name || row.record.source_staff_name || "Unknown"}”.`,
    );
  }
  if (!row.entry.date) {
    add("Date missing", "Enter the work date.");
  }
  if (row.entry.job_date_id && row.entry.job_date_date && row.entry.date && row.entry.date !== row.entry.job_date_date) {
    add("Job date differs", `Written date ${formatDate(row.entry.date)} differs from the matched job (${formatDate(row.entry.job_date_date)}). Check the original.`);
  }
  if (row.entry.is_shift_lead && !row.entry.job_date_id) {
    add(
      row.entry.match_suggestions?.length
        ? "Suggested job available"
        : "Job not matched",
      row.entry.match_suggestions?.length
        ? "Confirm one of the suggested Job References."
        : `Match Job Reference “${row.entry.shift_reference || "blank"}”.`,
    );
  } else if (
    row.entry.is_shift_lead &&
    isOutsidePayrollPeriod(row.entry.job_date_date, activeRun.value)
  ) {
    add(
      "Late claim",
      `Confirm late claim dated ${formatDate(row.entry.job_date_date)}.`,
    );
  }
  if (row.entry.is_shift_lead && row.entry.payment_basis === "hourly") {
    const missingTimes = [
      !row.entry.work_start_time ? "start" : null,
      !row.entry.work_finish_time ? "finish" : null,
    ].filter(Boolean);
    if (
      missingTimes.length &&
      (!entryIsDrivingComponent(row.entry) || !entryHasDrivingHours(row.entry))
    ) {
      add(
        "Paid times missing",
        `Enter paid ${missingTimes.join(" and ")} time${missingTimes.length > 1 ? "s" : ""}.`,
      );
    }
  } else if (
    row.entry.is_shift_lead &&
    (row.entry.amount === "" || row.entry.amount === null)
    && (
      row.entry.quantity === ""
      || row.entry.quantity === null
      || row.entry.unit_rate === ""
      || row.entry.unit_rate === null
    )
  ) {
    add(
      "Claim value missing",
      "Enter an amount or quantity and unit rate.",
    );
  }
  if (entryHoursDiffer(row.entry)) {
    const written = Number(row.entry.claimed_hours).toFixed(2);
    const calculated = Number(calculatedEntryHours(row.entry)).toFixed(2);
    add(
      "Written hours differ",
      `Claimed ${written}h; calculated ${calculated}h.`,
    );
  }
  if (incompletePtsJourney(row.entry, row.edit.entries || [])) {
    add(
      "PTS job times missing",
      "Enter a patient/job start and finish time. These are separate from the paid shift times.",
    );
  }

  return issues;
};
const timesheetReviewIssues = (group) => {
  const issues = [];
  const seen = new Set();
  const add = (scope, message) => {
    const normalizedMessage = String(message || "").trim();
    if (!normalizedMessage) return;
    const key = `${scope}|${normalizedMessage}`.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    issues.push({ scope, message: normalizedMessage });
  };

  if (!group.edit.staff_id) {
    add(
      "Timesheet",
      `Select Staff for “${group.edit.claimant_name || group.record.source_staff_name || "Unknown"}”.`,
    );
  }
  group.rows.forEach((row) => {
    const messages = entryReviewIssues(row, false).map((issue) => issue.message);
    if (messages.length) {
      add(
        `Row ${row.entry.sequence || row.entryIndex + 1}`,
        messages.join(" "),
      );
    }
  });

  return issues;
};
const reviewFilterCounts = computed(() => {
  const groups = allTimesheetSpreadsheetGroups.value;
  const needsReview = groups.filter(
    (group) => group.issues.length > 0,
  ).length;
  const ready = groups.filter(
    (group) =>
      group.issues.length === 0 &&
      !["imported", "excluded"].includes(group.record.status),
  ).length;

  return {
    all: groups.length,
    needs_review: needsReview,
    broken: groups.filter((group) => group.missingApprovals.length > 0).length,
    duplicates: timesheetImportSummary.value.duplicates || 0,
    ready,
  };
});
const importReviewCount = computed(
  () => reviewFilterCounts.value.needs_review,
);
const groupStatusLabel = (group) => {
  if (group.record.status === "imported") return "Imported";
  if (group.record.status === "excluded") return "Excluded";
  return group.issues.length ? "Needs review" : "Ready to import";
};
const groupStatusTone = (group) =>
  groupStatusLabel(group) === "Ready to import"
    ? "bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200"
    : statusTone(group.record.status);
const entryIssueLabel = (row) => {
  const issue = entryReviewIssues(row)[0];
  if (issue) return issue.short;

  return row.record.status === "imported" ? "Imported" : "Ready to import";
};
const entryIssueTitle = (row) => {
  const issues = entryReviewIssues(row);
  return issues.length
    ? issues.map((issue) => issue.message).join("\n")
    : entryIssueLabel(row);
};
const importRetryBlocker = (record) => {
  const edit = importEdit(record);
  if ((edit.entries || []).some(entry => entry.job_date_id && entry.date && entry.job_date_date && entry.date !== entry.job_date_date)) {
    return 'The work date differs from a selected job date. Select the job for the corrected date before saving.';
  }
  if (!edit.staff_id) {
    return "Select a matched Staff name before retrying this import. The original file is available; its claimant is not matched to Neptune staff yet.";
  }

  const unmatchedJob = (edit.entries || []).find(
    (entry) => entry.is_shift_lead && !entry.job_date_id,
  );
  if (unmatchedJob) {
    return "Select a matched Job Reference for every shift before retrying this import.";
  }

  const missingPaidTime = (edit.entries || []).find(
    (entry) =>
      entry.is_shift_lead &&
      entry.payment_basis === "hourly" &&
      (!entry.work_start_time || !entry.work_finish_time) &&
      (!entryIsDrivingComponent(entry) || !entryHasDrivingHours(entry)),
  );
  if (missingPaidTime) {
    return "Enter paid start and finish times for work rows. Driving rows may use start and finish times or an explicit driving-hours value.";
  }

  const missingPtsTimes = (edit.entries || []).find((entry) =>
    incompletePtsJourney(entry, edit.entries || []),
  );
  if (missingPtsTimes) {
    return "Enter a start and finish time for every PTS patient/job line. Paid hours still use the overall shift start and finish.";
  }

  return "";
};
const setClaimStaff = (claim, staff) => {
  const edit = claimEdit(claim);
  edit.staff_id = staff?.id ?? "";
  edit.staff_query = staff ? staffSearchOutput(staff) : "";
};
const setClaimJobDate = (claim, jobDate) => {
  const edit = claimEdit(claim);
  edit.job_date_id = jobDate?.id ?? "";
  edit.job_date_date = jobDate?.date ?? "";
  edit.job_date_query = jobDate ? jobDateSearchOutput(jobDate) : "";
};
const clearClaimStaff = (claim) => {
  const edit = claimEdit(claim);
  edit.staff_id = "";
  edit.staff_query = "";
};
const clearClaimJobDate = (claim) => {
  const edit = claimEdit(claim);
  edit.job_date_id = "";
  edit.job_date_date = "";
  edit.job_date_query = "";
};
const updateClaimJobDateQuery = (claim, value) => {
  const edit = claimEdit(claim);
  if (edit.job_date_id && value === edit.job_date_query) {
    return;
  }
  edit.job_date_query = value;
  edit.job_date_id = "";
  edit.job_date_date = "";
};
const setImportStaff = (record, staff) => {
  const edit = importEdit(record);
  edit.staff_id = staff?.id ?? "";
  edit.staff_query = staff ? staffSearchOutput(staff) : "";
  if (staff?.contact?.name || staff?.name) {
    edit.claimant_name = staffSearchOutput(staff);
  }
};
const updateImportStaffQuery = (record, value) => {
  const edit = importEdit(record);
  if (edit.staff_id && value === edit.staff_query) {
    return;
  }
  edit.staff_query = value;
  // Keep the corrected source name even when the reviewer cannot select a
  // Neptune staff member yet. This is staged progress, not an assignment.
  edit.claimant_name = value;
  edit.staff_id = "";
};
const setImportEntryJobDate = (entry, jobDate, record = null) => {
  const entries = record ? importEntriesForRecord(record) : [entry];
  if (record && !entry.is_shift_lead && jobDate?.id) {
    const currentLead = entries.find(
      (candidate) =>
        candidate.is_shift_lead &&
        candidate.shift_group_key === entry.shift_group_key,
    );
    if (
      currentLead?.job_date_id &&
      Number(currentLead.job_date_id) !== Number(jobDate.id)
    ) {
      entry.is_shift_lead = true;
      entry.shift_group_key = `job-${jobDate.id}-${entry.sequence}`;
      entry.work_start_time = "";
      entry.work_finish_time = "";
      entry.claimed_hours = "";
      entry.calculated_hours = "";
    }
  }
  const targets = record && entry.is_shift_lead
    ? entries.filter(
        (candidate) => candidate.shift_group_key === entry.shift_group_key,
      )
    : [entry];
  targets.forEach((candidate) => {
    candidate.job_date_id = jobDate?.id ?? "";
    candidate.match_method = jobDate?.reason ? "suggestion" : "manual";
    candidate.job_date_date = jobDate?.date ?? "";
    candidate.job_date_query = jobDate ? jobDateSearchOutput(jobDate) : "";
    candidate.reference_type = jobDate ? jobDateReferenceType(jobDate) : "";
    if (jobDate) {
      candidate.shift_reference = String(
        jobDate?.reference?.platform_shift_ref ||
          jobDate?.reference?.txt ||
          candidate.shift_reference ||
          "",
      );
    }
  });
  hydrateSharedShiftValues(entries);
  if (record && entries.filter((candidate) => candidate.is_shift_lead).length === 1) {
    const edit = importEdit(record);
    edit.job_date_id = jobDate?.id ?? "";
    edit.job_date_query = jobDate ? jobDateSearchOutput(jobDate) : "";
  }
};
const updateImportEntryJobQuery = (entry, value, record = null) => {
  if (entry.job_date_id && value === entry.job_date_query) {
    return;
  }
  const entries = record ? importEntriesForRecord(record) : [entry];
  const targets = record && entry.is_shift_lead
    ? entries.filter(
        (candidate) => candidate.shift_group_key === entry.shift_group_key,
      )
    : [entry];
  targets.forEach((candidate) => {
    candidate.job_date_query = value;
    candidate.shift_reference = value;
    candidate.reference_type = "";
    candidate.job_date_id = "";
    candidate.match_method = null;
    candidate.job_date_date = "";
  });
};
const setImportEntryCoWorker = (entry, staff, record = null) => {
  sharedShiftEntries(entry, record).forEach((candidate) => {
    candidate.co_worker_staff_id = staff?.id ?? "";
    candidate.co_worker_query = staff ? staffSearchOutput(staff) : "";
    candidate.co_worker = staff ? staffSearchOutput(staff) : "";
  });
};
const updateImportEntryCoWorkerQuery = (entry, value, record = null) => {
  if (entry.co_worker_staff_id && value === entry.co_worker_query) {
    return;
  }
  sharedShiftEntries(entry, record).forEach((candidate) => {
    candidate.co_worker_query = value;
    candidate.co_worker = value;
    candidate.co_worker_staff_id = "";
  });
};
const setImportEntryVehicle = (entry, vehicle, record = null) => {
  sharedShiftEntries(entry, record).forEach((candidate) => {
    candidate.vehicle_id = vehicle?.id ?? "";
    candidate.vehicle_query = vehicle ? vehicleSearchOutput(vehicle) : "";
    candidate.vehicle_registration = vehicle?.registrationNumber || "";
  });
};
const updateImportEntryVehicleQuery = (entry, value, record = null) => {
  if (entry.vehicle_id && value === entry.vehicle_query) {
    return;
  }
  sharedShiftEntries(entry, record).forEach((candidate) => {
    candidate.vehicle_query = value;
    candidate.vehicle_registration = value;
    candidate.vehicle_id = "";
  });
};
const setImportEntryProduct = (entry, productId, record = null) => {
  sharedShiftEntries(entry, record).forEach((candidate) => {
    candidate.product_id = productId;
  });
};

const claimDefaultAcceptedHours = (claim, status) => {
  if (status === "overridden") {
    return claim?.claimed_hours ?? claim?.expected_total_hours ?? null;
  }

  return claim?.expected_total_hours ?? claim?.claimed_hours ?? null;
};

const claimAcceptedHoursPayload = (claim, status) => {
  const edits = claimEdits[claim.id] || {};
  const rawValue = edits.accepted_hours;

  if (
    rawValue === "" ||
    rawValue === null ||
    rawValue === undefined ||
    (edits.accepted_hours_touched === false && claim?.accepted_hours === null)
  ) {
    const fallback = claimDefaultAcceptedHours(claim, status);
    return fallback === null || fallback === undefined ? null : Number(fallback);
  }

  return Number(rawValue);
};

const claimAcceptedAmountPayload = (claim) => {
  if ((claim?.payment_basis || "hourly") === "hourly") {
    return null;
  }

  const rawValue = claimEdits[claim.id]?.accepted_amount;
  if (rawValue === "" || rawValue === null || rawValue === undefined) {
    return claim?.claimed_amount === null || claim?.claimed_amount === undefined
      ? null
      : Number(claim.claimed_amount);
  }

  return Number(rawValue);
};

const replaceRunSummary = (run) => {
  const summary = {
    id: run.id,
    name: run.name,
    tax_month: run.tax_month,
    pay_period_start: run.pay_period_start,
    pay_period_end: run.pay_period_end,
    status: run.status,
    sent_at: run.sent_at,
    created_at: run.created_at,
    payslips_source_name: run.payslips_source_name,
    supporting_document_name: run.supporting_document_name,
    supporting_document_download_url: run.supporting_document_download_url,
    accountant_export_download_url: run.accountant_export_download_url,
    payslip_count: run.payslip_count,
    sent_payslip_count: run.sent_payslip_count,
    claim_count: run.claim_count,
    accepted_claim_count: run.accepted_claim_count,
    review_claim_count: run.review_claim_count,
    timesheet_document_count: run.timesheet_document_count,
    accountant_export_preflight: run.accountant_export_preflight,
  };

  const index = runs.value.findIndex((item) => item.id === run.id);
  if (index === -1) {
    runs.value = [summary, ...runs.value];
    return;
  }

  runs.value.splice(index, 1, summary);
};

const loadRun = async (runId, { preserveLocalEdits = false, throwOnError = false } = {}) => {
  if (!runId) {
    activeRun.value = null;
    return;
  }
  if (activeRunId.value && Number(activeRunId.value) !== Number(runId)
      && timesheetImportRecords.value.some(importRecordHasUnsavedChanges)) {
    showToast('Save your timesheet changes before switching payroll runs.', 'error');
    return;
  }

  loadingRun.value = true;
  loadingError.value = "";

  try {
    const response = await apiClient.get(`/api/payroll-runs/${runId}`);
    activeRun.value = normalizeRun(response.data);
    syncEmailOverrides(activeRun.value);
    syncClaimEdits(activeRun.value, { preserveDirty: preserveLocalEdits });
    activeRunId.value = runId;
    restoreTimesheetFocus(runId);
    if (!preserveLocalEdits) {
      sendMessage.value = "";
      completePayslipFile.value = null;
      completePayslipError.value = "";
      if (completePayslipInput.value) {
        completePayslipInput.value.value = "";
      }
      additionalPayslipFiles.value = [];
      additionalPayslipsError.value = "";
      if (additionalPayslipInput.value) {
        additionalPayslipInput.value.value = "";
      }
    }
    replaceRunSummary(response.data);
    await loadTimesheetImports({ background: preserveLocalEdits });
  } catch (error) {
    loadingError.value =
      error?.response?.data?.message ||
      "Unable to load the selected payroll run.";
    if (throwOnError) throw error;
  } finally {
    loadingRun.value = false;
  }
};

const loadUnclaimedWork = async () => {
  if (!activeRunId.value) {
    unclaimedWorkReport.value = {
      summary: {
        scheduled: 0,
        claimed: 0,
        missing_employees: 0,
        missing_timesheets: 0,
        missing_timesheet_claims_received: 0,
        contractor_assignments: 0,
        contractor_payroll_claims: 0,
      },
      records: [],
    };
    return;
  }

  loadingUnclaimedWork.value = true;
  unclaimedWorkError.value = "";

  try {
    const response = await apiClient.get(
      `/api/payroll-runs/${activeRunId.value}/unclaimed-work`,
    );
    unclaimedWorkReport.value = response.data;
  } catch (error) {
    unclaimedWorkError.value =
      error?.response?.data?.message ||
      "Unable to compare scheduled jobs with submitted timesheets.";
  } finally {
    loadingUnclaimedWork.value = false;
  }
};

const missingTimesheetKey = (record) =>
  `${record.job_date_id}:${record.staff_id}`;
const categorizingMissingTimesheet = (record) =>
  categorizingMissingTimesheetKeys.value.has(missingTimesheetKey(record));

const setMissingTimesheetCategory = async (record, missingTimesheet) => {
  if (!activeRunId.value || activeRun.value?.status !== "draft") return;

  const key = missingTimesheetKey(record);
  const pending = new Set(categorizingMissingTimesheetKeys.value);
  pending.add(key);
  categorizingMissingTimesheetKeys.value = pending;

  try {
    const response = await apiClient.put(
      `/api/payroll-runs/${activeRunId.value}/missing-timesheets`,
      {
        job_date_id: record.job_date_id,
        staff_id: record.staff_id,
        missing_timesheet: missingTimesheet,
      },
    );
    unclaimedWorkReport.value = response.data;
    showToast(
      missingTimesheet
        ? `${record.staff_name}'s job will be paid in a later run after their timesheet is submitted.`
        : `${record.staff_name}'s job returned to missing claims.`,
    );
  } catch (error) {
    const validationMessage = Object.values(
      error?.response?.data?.errors || {},
    ).flat()?.[0];
    showToast(
      validationMessage ||
        sendErrorMessage(error, "Unable to update the missing timesheet category."),
      "error",
    );
  } finally {
    const next = new Set(categorizingMissingTimesheetKeys.value);
    next.delete(key);
    categorizingMissingTimesheetKeys.value = next;
  }
};

const closeClaimRepairReview = () => {
  if (applyingClaimRepairs.value) return;
  claimRepairOpen.value = false;
  claimRepairError.value = "";
  selectedClaimRepairIds.value = new Set();
};

const loadClaimMatchRepairs = async () => {
  if (!activeRunId.value) return;

  loadingClaimRepairs.value = true;
  claimRepairError.value = "";
  selectedClaimRepairIds.value = new Set();

  try {
    const response = await apiClient.get(
      `/api/payroll-runs/${activeRunId.value}/claim-match-repairs`,
    );
    claimRepairPreview.value = response.data;
  } catch (error) {
    claimRepairError.value = sendErrorMessage(
      error,
      "Unable to scan payroll claims for matching problems.",
    );
  } finally {
    loadingClaimRepairs.value = false;
  }
};

const openClaimRepairReview = async () => {
  claimRepairOpen.value = true;
  await loadClaimMatchRepairs();
};

const toggleClaimRepairSelection = (repair) => {
  if (!repair?.repairable || applyingClaimRepairs.value) return;
  const next = new Set(selectedClaimRepairIds.value);
  if (next.has(repair.payroll_run_claim_id)) {
    next.delete(repair.payroll_run_claim_id);
  } else {
    next.add(repair.payroll_run_claim_id);
  }
  selectedClaimRepairIds.value = next;
};

const toggleAllRepairableClaims = () => {
  selectedClaimRepairIds.value = allRepairableClaimsSelected.value
    ? new Set()
    : new Set(
        repairableClaimRows.value.map(
          (repair) => repair.payroll_run_claim_id,
        ),
      );
};

const applySelectedClaimRepairs = async () => {
  const repairs = repairableClaimRows.value
    .filter((repair) =>
      selectedClaimRepairIds.value.has(repair.payroll_run_claim_id),
    )
    .map((repair) => ({
      payroll_run_claim_id: repair.payroll_run_claim_id,
      target_job_date_id: repair.suggested_job_date.id,
      base_updated_at: repair.base_updated_at,
    }));

  if (!repairs.length || !activeRunId.value) return;

  applyingClaimRepairs.value = true;
  claimRepairError.value = "";

  try {
    const runId = activeRunId.value;
    const response = await apiClient.post(
      `/api/payroll-runs/${runId}/claim-match-repairs`,
      { repairs },
    );
    claimRepairPreview.value = response.data.preview;
    unclaimedWorkReport.value = response.data.unclaimed_work;
    selectedClaimRepairIds.value = new Set();
    await loadRun(runId, { preserveLocalEdits: true });
    claimRepairOpen.value = false;
    showToast(
      `${response.data.summary.repaired} claim match${response.data.summary.repaired === 1 ? "" : "es"} repaired and reopened for review.`,
    );
  } catch (error) {
    claimRepairError.value = sendErrorMessage(
      error,
      "Unable to apply the selected claim repairs.",
    );
  } finally {
    applyingClaimRepairs.value = false;
  }
};

const loadSettings = async () => {
  settingsLoading.value = true;
  settingsError.value = "";

  try {
    const response = await apiClient.get("/api/payroll-settings");
    settingsForm.value = normalizeSettings(response.data);
  } catch (error) {
    settingsError.value = settingsErrorMessage(
      error,
      "Unable to load payroll settings.",
    );
  } finally {
    settingsLoading.value = false;
  }
};

const loadProducts = async () => {
  try {
    const response = await apiClient.get("/api/product");
    availableProducts.value = normalizeProducts(response.data);
  } catch (error) {
    showToast(
      error?.response?.data?.message || "Unable to load payroll products.",
      "error",
    );
  }
};

const loadTimesheetImports = async ({ background = false } = {}) => {
  if (!background) {
    loadingImports.value = true;
  }

  try {
    const params = {
      status: focusedTimesheetImportId.value ? "all" : importStatusFilter.value,
      include_unresolved: 1,
      limit: 250,
      duplicates_only: timesheetReadinessFilter.value === 'duplicates' ? 1 : 0,
    };

    if (activeRun.value?.pay_period_start) {
      params.period_start = activeRun.value.pay_period_start;
    }
    if (activeRun.value?.pay_period_end) {
      params.period_end = activeRun.value.pay_period_end;
    }
    if (activeRunId.value) {
      params.payroll_run_id = activeRunId.value;
    }

    const response = await apiClient.get("/api/timesheet-import-records", {
      params,
    });
    timesheetImportSummary.value = response.data?.summary || {
      total: 0,
      imported: 0,
      needs_review: 0,
      excluded: 0,
      entry_rows: 0,
    };
    const incomingRecords = Array.isArray(response.data?.records)
      ? response.data.records
      : [];
    const orderRunId = Number(activeRunId.value || 0);
    if (timesheetImportOrderRunId !== orderRunId) {
      timesheetImportOrder.clear();
      timesheetImportOrderRunId = orderRunId;
      retainedReviewIds.value = new Set();
    }
    incomingRecords.forEach((record) => {
      if (!timesheetImportOrder.has(Number(record.id))) {
        timesheetImportOrder.set(Number(record.id), timesheetImportOrder.size);
      }
    });
    const existingRecords = new Map(
      timesheetImportRecords.value.map((record) => [record.id, record]),
    );
    const existingOrder = new Map(
      timesheetImportRecords.value.map((record, index) => [record.id, index]),
    );
    const nextRecords = background
      ? incomingRecords.map((record) => {
          const locallyProtected =
            importAutosaveTimers.has(record.id) ||
            importSaveState[record.id] === "saving" ||
            activeImportRow.value?.recordId === record.id ||
            importRecordHasUnsavedChanges(existingRecords.get(record.id));
          return locallyProtected ? existingRecords.get(record.id) || record : record;
        })
      : incomingRecords.map((record) => importRecordHasUnsavedChanges(existingRecords.get(record.id))
          ? existingRecords.get(record.id) || record : record);
    const incomingIds = new Set(nextRecords.map(record => record.id));
    for (const existing of existingRecords.values()) {
      if (!incomingIds.has(existing.id) && (importRecordHasUnsavedChanges(existing)
          || Number(existing.id) === Number(focusedTimesheetImportId.value))) nextRecords.push(existing);
    }
    timesheetImportRecords.value = nextRecords.sort((left, right) => {
      const leftOrder = timesheetImportOrder.get(Number(left.id)) ??
        existingOrder.get(left.id) ?? Number.MAX_SAFE_INTEGER;
      const rightOrder = timesheetImportOrder.get(Number(right.id)) ??
        existingOrder.get(right.id) ?? Number.MAX_SAFE_INTEGER;
      return leftOrder - rightOrder;
    });
    syncImportEdits(timesheetImportRecords.value, {
      preserveExisting: true,
    });
    if (focusedTimesheetImportId.value) {
      const focusedRecord = timesheetImportRecords.value.find(
        (record) => Number(record.id) === Number(focusedTimesheetImportId.value),
      );
      if (!focusedRecord || ["imported", "excluded"].includes(focusedRecord.status)) {
        clearTimesheetFocus();
      }
    }
    lastFullTimesheetSyncAt = Date.now();
    if (!background) {
      const currentIds = new Set(timesheetImportRecords.value.map((record) => record.id));
      importEditWatchStops.forEach((stop, recordId) => {
        if (!currentIds.has(recordId)) {
          stop();
          importEditWatchStops.delete(recordId);
          importAutosaveSnapshots.delete(recordId);
          delete importEdits[recordId];
        }
      });
    }
  } catch (error) {
    if (!background) {
      showToast(
        error?.response?.data?.message || "Unable to load timesheet imports.",
        "error",
      );
    }
  } finally {
    if (!background) {
      loadingImports.value = false;
    }
  }
};

const pendingTimesheetDocuments = () =>
  (activeRun.value?.timesheet_documents || []).filter((document) =>
    ["queued", "processing"].includes(document.status),
  );

const stopTimesheetProcessingPolling = () => {
  if (timesheetProcessingPollTimeout) {
    window.clearTimeout(timesheetProcessingPollTimeout);
    timesheetProcessingPollTimeout = null;
  }
};

const scheduleTimesheetProcessingPoll = (delay = 3000) => {
  stopTimesheetProcessingPolling();
  if (
    isSettingsPage.value ||
    !activeRunId.value ||
    pendingTimesheetDocuments().length === 0
  ) {
    return;
  }

  timesheetProcessingPollTimeout = window.setTimeout(
    pollTimesheetProcessing,
    delay,
  );
};

const pollTimesheetProcessing = async () => {
  if (timesheetProcessingPollInFlight || !activeRunId.value) {
    scheduleTimesheetProcessingPoll();
    return;
  }

  const runId = activeRunId.value;
  const previousDocuments = new Map(
    (activeRun.value?.timesheet_documents || []).map((document) => [
      document.id,
      document,
    ]),
  );
  timesheetProcessingPollInFlight = true;
  timesheetProcessingPollTimeout = null;

  try {
    const response = await apiClient.get(`/api/payroll-runs/${runId}`);
    if (activeRunId.value !== runId) {
      return;
    }

    const refreshedRun = normalizeRun(response.data);
    const completedDocuments = refreshedRun.timesheet_documents.filter(
      (document) =>
        ["queued", "processing"].includes(
          previousDocuments.get(document.id)?.status,
        ) && !["queued", "processing"].includes(document.status),
    );

    activeRun.value = {
      ...activeRun.value,
      timesheet_documents: refreshedRun.timesheet_documents,
      timesheet_document_count: refreshedRun.timesheet_document_count,
      accountant_export_preflight:
        refreshedRun.accountant_export_preflight,
      claim_count: refreshedRun.claim_count,
      accepted_claim_count: refreshedRun.accepted_claim_count,
      review_claim_count: refreshedRun.review_claim_count,
    };
    replaceRunSummary(refreshedRun);

    if (completedDocuments.length > 0) {
      await loadTimesheetImports({ background: true });
      const failedCount = completedDocuments.filter(
        (document) => document.status === "failed",
      ).length;
      if (failedCount > 0) {
        showToast(
          `${failedCount} timesheet${failedCount === 1 ? "" : "s"} could not be processed. Review the error shown with the source file.`,
          "error",
        );
      } else {
        showToast(
          `${completedDocuments.length} timesheet${completedDocuments.length === 1 ? "" : "s"} finished processing. The imported rows are ready.`,
        );
      }
    }
  } catch (_error) {
    // A transient background refresh should not interrupt the reviewer. The
    // next scheduled poll will retry while the document remains pending.
  } finally {
    timesheetProcessingPollInFlight = false;
    scheduleTimesheetProcessingPoll();
  }
};

const saveSettings = async () => {
  settingsSaving.value = true;
  settingsError.value = "";
  settingsNotice.value = "";

  try {
    const response = await apiClient.put("/api/payroll-settings", {
      pay_run_start_date: settingsForm.value.pay_run_start_date || null,
      pay_run_finish_date: settingsForm.value.pay_run_finish_date || null,
      claim_tolerance_minutes:
        settingsForm.value.claim_tolerance_minutes === ""
          ? null
          : Number(settingsForm.value.claim_tolerance_minutes),
      accountant_export_rates: Object.fromEntries(
        Object.entries(settingsForm.value.accountant_export_rates || {}).map(
          ([code, value]) => [code, value === "" ? null : Number(value)],
        ),
      ),
      cost_estimation_settings: Object.fromEntries(
        Object.entries(settingsForm.value.cost_estimation_settings || {}).map(
          ([key, value]) => [key, value === "" ? null : Number(value)],
        ),
      ),
    });

    settingsForm.value = normalizeSettings(response.data);
    settingsNotice.value = "Payroll settings saved.";
  } catch (error) {
    settingsError.value = settingsErrorMessage(
      error,
      "Unable to save payroll settings.",
    );
  } finally {
    settingsSaving.value = false;
  }
};

const loadRuns = async ({ preserveLocalEdits = false } = {}) => {
  loadingRuns.value = true;
  loadingError.value = "";

  try {
    const response = await apiClient.get("/api/payroll-runs");
    runs.value = Array.isArray(response.data) ? response.data : [];

    if (!activeRunId.value && runs.value.length > 0) {
      await loadRun(runs.value[0].id);
    } else if (activeRunId.value) {
      await loadRun(activeRunId.value, { preserveLocalEdits });
    } else {
      await loadTimesheetImports();
    }
  } catch (error) {
    loadingError.value =
      error?.response?.data?.message || "Unable to load payroll runs.";
  } finally {
    loadingRuns.value = false;
  }
};

const refreshRunSafely = async () => {
  const dirtyClaimCount = Object.keys(claimEdits).filter((claimId) =>
    claimEditIsDirty(claimId),
  ).length;

  await flushPendingImportAutosaves();
  await loadRuns({ preserveLocalEdits: true });
  if (loadingError.value) return;

  showToast(
    dirtyClaimCount > 0
      ? `Run refreshed. ${dirtyClaimCount} unresolved local claim edit${dirtyClaimCount === 1 ? " was" : "s were"} preserved.`
      : "Run refreshed.",
  );
};

const closePayPeriod = async () => {
  if (!activeRunId.value || activeRun.value?.status !== "draft" || closingRun.value) {
    return;
  }

  if (!window.confirm(`Close ${activeRun.value.name}? No more timesheets can be added to this pay period.`)) {
    return;
  }

  closingRun.value = true;
  loadingError.value = "";
  try {
    const response = await apiClient.post(
      `/api/payroll-runs/${activeRunId.value}/close`,
    );
    activeRun.value = normalizeRun(response.data.run);
    syncEmailOverrides(activeRun.value);
    syncClaimEdits(activeRun.value);
    replaceRunSummary(response.data.run);
    showToast("Pay period closed.");
  } catch (error) {
    showToast(
      error?.response?.data?.message || "Unable to close the pay period.",
      "error",
    );
  } finally {
    closingRun.value = false;
  }
};

const createRun = async () => {
  if (!createForm.value.name) {
    createError.value = "A run name is required.";
    return;
  }

  createError.value = "";
  creating.value = true;

  const formData = new FormData();
  formData.append("name", createForm.value.name);
  if (createForm.value.tax_month) {
    formData.append("tax_month", createForm.value.tax_month);
  }
  if (createForm.value.pay_period_start) {
    formData.append("pay_period_start", createForm.value.pay_period_start);
  }
  if (createForm.value.pay_period_end) {
    formData.append("pay_period_end", createForm.value.pay_period_end);
  }
  if (createForm.value.payslips_pdf) {
    formData.append("payslips_pdf", createForm.value.payslips_pdf);
  }
  if (createForm.value.supporting_document) {
    formData.append(
      "supporting_document",
      createForm.value.supporting_document,
    );
  }

  try {
    const response = await apiClient.post("/api/payroll-runs", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    const run = normalizeRun(response.data);
    activeRun.value = run;
    syncEmailOverrides(run);
    syncClaimEdits(run, { preserveDirty: false });
    activeRunId.value = run.id;
    sendMessage.value = "";
    replaceRunSummary(run);
    createForm.value = {
      name: "",
      tax_month: "",
      ...defaultPayrollPeriod(),
      payslips_pdf: null,
      supporting_document: null,
    };
  } catch (error) {
    createError.value =
      error?.response?.data?.errors?.payslips_pdf?.[0] ||
      error?.response?.data?.message ||
      "Unable to create payroll run.";
  } finally {
    creating.value = false;
  }
};

const uploadCompletePayslips = async () => {
  if (!activeRunId.value || activeRun.value?.status !== "draft") {
    completePayslipError.value =
      "A complete payslip PDF can only be uploaded to a draft payroll run.";
    return;
  }

  if (Number(activeRun.value?.payslip_count || 0) > 0) {
    completePayslipError.value =
      "This payroll run already has payslips. Add missing slips individually.";
    return;
  }

  if (!completePayslipFile.value) {
    completePayslipError.value = "Select the complete payslip PDF.";
    return;
  }

  completePayslipError.value = "";
  uploadingCompletePayslips.value = true;

  const formData = new FormData();
  formData.append("payslips_pdf", completePayslipFile.value);

  try {
    const response = await apiClient.post(
      `/api/payroll-runs/${activeRunId.value}/payslips/bulk`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );

    activeRun.value = normalizeRun(response.data.run);
    syncEmailOverrides(activeRun.value);
    syncClaimEdits(activeRun.value);
    replaceRunSummary(response.data.run);

    const addedCount = Number(response.data.added_count || 0);
    showToast(
      `${addedCount} payslip${addedCount === 1 ? "" : "s"} imported from the complete PDF.`,
    );

    completePayslipFile.value = null;
    if (completePayslipInput.value) {
      completePayslipInput.value.value = "";
    }
  } catch (error) {
    completePayslipError.value = sendErrorMessage(
      error,
      "Unable to import the complete payslip PDF.",
    );
  } finally {
    uploadingCompletePayslips.value = false;
  }
};

const addIndividualPayslips = async () => {
  if (!activeRunId.value || activeRun.value?.status !== "draft") {
    additionalPayslipsError.value =
      "Individual payslips can only be added to a draft payroll run.";
    return;
  }

  if (additionalPayslipFiles.value.length === 0) {
    additionalPayslipsError.value =
      "Select at least one individual payslip PDF.";
    return;
  }

  additionalPayslipsError.value = "";
  addingPayslips.value = true;

  const formData = new FormData();
  additionalPayslipFiles.value.forEach((file) => {
    formData.append("payslips[]", file);
  });

  try {
    const response = await apiClient.post(
      `/api/payroll-runs/${activeRunId.value}/payslips`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );

    activeRun.value = normalizeRun(response.data.run);
    syncEmailOverrides(activeRun.value);
    syncClaimEdits(activeRun.value);
    replaceRunSummary(response.data.run);

    const addedCount = Number(response.data.added_count || 0);
    showToast(
      `${addedCount} individual payslip${addedCount === 1 ? "" : "s"} added.`,
    );

    additionalPayslipFiles.value = [];
    if (additionalPayslipInput.value) {
      additionalPayslipInput.value.value = "";
    }
  } catch (error) {
    additionalPayslipsError.value = payslipUploadErrorMessage(
      error,
      "Unable to add the individual payslips.",
    );
  } finally {
    addingPayslips.value = false;
  }
};

const uploadTimesheets = async () => {
  if (!activeRunId.value || timesheetFiles.value.length === 0) {
    timesheetUploadError.value = "Select at least one timesheet or invoice.";
    return;
  }

  uploadingTimesheets.value = true;
  timesheetUploadError.value = "";
  const formData = new FormData();
  timesheetFiles.value.forEach((file) => formData.append("timesheets[]", file));

  try {
    const response = await apiClient.post(
      `/api/payroll-runs/${activeRunId.value}/timesheets`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } },
    );
    activeRun.value = normalizeRun(response.data.run);
    replaceRunSummary(response.data.run);
    timesheetFiles.value = [];
    if (timesheetInput.value) {
      timesheetInput.value.value = "";
    }
    await loadTimesheetImports();
    showToast("Timesheets uploaded. Extraction has been queued.");
  } catch (error) {
    const errors = error?.response?.data?.errors || {};
    const nested = Object.entries(errors).find(([field]) =>
      field.startsWith("timesheets."),
    )?.[1]?.[0];
    timesheetUploadError.value =
      errors?.timesheets?.[0] ||
      nested ||
      error?.response?.data?.message ||
      "Unable to upload the timesheets.";
  } finally {
    uploadingTimesheets.value = false;
  }
};

const importTimesheetAction = () => {
  if (uploadingTimesheets.value || activeRun.value?.status !== "draft") {
    return;
  }

  if (timesheetFiles.value.length > 0) {
    uploadTimesheets();
    return;
  }

  timesheetInput.value?.click();
};

const reprocessTimesheet = async (
  document,
  { verifyWithBestAi = false } = {},
) => {
  const viewport = captureViewport();
  const next = new Set(reprocessingDocumentIds.value);
  next.add(document.id);
  reprocessingDocumentIds.value = next;

  try {
    await apiClient.post(
      `/api/payroll-runs/${activeRunId.value}/timesheets/${document.id}/reprocess`,
      { verify_with_best_ai: verifyWithBestAi },
    );
    await loadRun(activeRunId.value);
    showToast(
      verifyWithBestAi
        ? "Best AI extraction and verification has been queued. This can take several minutes."
        : "Fast timesheet reprocessing has been queued.",
    );
  } catch (error) {
    showToast(
      error?.response?.data?.message || "Unable to reprocess the timesheet.",
      "error",
    );
  } finally {
    const after = new Set(reprocessingDocumentIds.value);
    after.delete(document.id);
    reprocessingDocumentIds.value = after;
    await restoreViewport(viewport);
  }
};

const deleteTimesheetDocument = async (document) => {
  const documentId = Number(document?.source_document_id || document?.id);
  if (!documentId || !activeRunId.value) {
    showToast("This timesheet does not have a deletable source document.", "error");
    return;
  }
  const name = document?.source_file_name || document?.name || "this timesheet";
  if (!window.confirm(
    `Delete ${name}, all of its staged rows, and every draft payroll claim it generated? Approval decisions for those claims will also be removed. You can then upload the file again.`,
  )) {
    return;
  }

  const viewport = captureViewport();
  const next = new Set(deletingTimesheetDocumentIds.value);
  next.add(documentId);
  deletingTimesheetDocumentIds.value = next;

  try {
    const response = await apiClient.delete(
      `/api/payroll-runs/${activeRunId.value}/timesheets/${documentId}`,
    );
    activeRun.value = normalizeRun(response.data.run);
    replaceRunSummary(response.data.run);
    if (Number(timesheetImagePreview.value?.recordId) === Number(document?.id)) {
      closeTimesheetImagePreview();
    }
    await loadTimesheetImports();
    showToast("Timesheet deleted. The same file can now be uploaded again.");
  } catch (error) {
    showToast(
      error?.response?.data?.errors?.timesheet?.[0] ||
        error?.response?.data?.message ||
        "Unable to delete the timesheet.",
      "error",
    );
  } finally {
    const after = new Set(deletingTimesheetDocumentIds.value);
    after.delete(documentId);
    deletingTimesheetDocumentIds.value = after;
    await restoreViewport(viewport);
  }
};

const sendAll = async () => {
  if (!activeRunId.value) {
    return;
  }

  sendingAll.value = true;

  try {
    const response = await apiClient.post(
      `/api/payroll-runs/${activeRunId.value}/send`,
      {
        message: sendMessage.value.trim() || null,
      },
    );
    activeRun.value = normalizeRun(response.data.run);
    syncEmailOverrides(activeRun.value);
    syncClaimEdits(activeRun.value);
    replaceRunSummary(response.data.run);
  } catch (error) {
    loadingError.value = sendErrorMessage(error, "Unable to send payslips.");
  } finally {
    sendingAll.value = false;
  }
};

const sendSingle = async (payslip) => {
  const recipient = payslip.recipient_email;
  const nextIds = new Set(sendingIds.value);
  nextIds.add(payslip.id);
  sendingIds.value = nextIds;

  try {
    const response = await apiClient.post(
      `/api/payroll-runs/${activeRunId.value}/send`,
      {
        payslip_ids: [payslip.id],
        message: sendMessage.value.trim() || null,
      },
    );
    activeRun.value = normalizeRun(response.data.run);
    syncEmailOverrides(activeRun.value);
    syncClaimEdits(activeRun.value);
    replaceRunSummary(response.data.run);
    if (recipient) {
      showToast(`Payslip emailed to ${recipient}.`);
    }
  } catch (error) {
    payslip.last_error = sendErrorMessage(error, "Unable to send payslip.");
  } finally {
    const after = new Set(sendingIds.value);
    after.delete(payslip.id);
    sendingIds.value = after;
  }
};

const refreshClaims = async ({ silent = false, message = "Timesheet claims refreshed." } = {}) => {
  if (!activeRunId.value) {
    return;
  }

  refreshingClaims.value = true;
  loadingError.value = "";

  try {
    const response = await apiClient.post(
      `/api/payroll-runs/${activeRunId.value}/claims/refresh`,
    );
    activeRun.value = normalizeRun(response.data.run);
    syncEmailOverrides(activeRun.value);
    syncClaimEdits(activeRun.value, { preserveDirty: true });
    replaceRunSummary(response.data.run);
    if (!silent) {
      showToast(message);
    }
  } catch (error) {
    if (!silent) {
      showToast(
        error?.response?.data?.message || "Unable to refresh timesheet claims.",
        "error",
      );
    } else {
      throw error;
    }
  } finally {
    refreshingClaims.value = false;
  }
};
const prepareAccountantSummary = async () => {
  await refreshClaims({
    message: "Accountant summary refreshed from the imported timesheets.",
  });
};

const downloadAccountantWorkbook = async (draft = false) => {
  const downloadUrl = activeRun.value?.accountant_export_download_url;
  if (!downloadUrl || downloadingAccountantWorkbook.value) {
    return;
  }

  if (
    draft &&
    !window.confirm(
      "This payroll run still has export blockers. The workbook will contain approved values and list every blocker, including approved claims with missing data. It will be marked DRAFT and must not be sent as the final payroll file. Export the draft now?",
    )
  ) {
    return;
  }

  downloadingAccountantWorkbook.value = true;
  try {
    const requestUrl = draft
      ? `${downloadUrl}${downloadUrl.includes("?") ? "&" : "?"}draft=1`
      : downloadUrl;
    const response = await apiClient.get(requestUrl, { responseType: "blob" });
    const blobUrl = window.URL.createObjectURL(response.data);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = `${draft ? "DRAFT - " : ""}Payroll - ${activeRun.value?.name || "Accountant"}.xlsx`;
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
    if (draft) {
      showToast("Draft workbook downloaded. Resolve every listed issue before creating the final accountant file.");
    }
  } catch (error) {
    showToast(
      error?.response?.data?.message || "Unable to export the accountant workbook.",
      "error",
    );
  } finally {
    downloadingAccountantWorkbook.value = false;
  }
};

const updateClaim = async (
  claim,
  status,
  { successMessage = "Timesheet claim updated." } = {},
) => {
  const nextIds = new Set(savingClaimIds.value);
  nextIds.add(claim.id);
  savingClaimIds.value = nextIds;

  const edits = claimEdits[claim.id] || {};

  try {
    const response = await apiClient.put(
      `/api/payroll-runs/${activeRunId.value}/claims/${claim.id}`,
      {
        status,
        accepted_hours: claimAcceptedHoursPayload(claim, status),
        accepted_work_hours:
          (claim.payment_basis || "hourly") === "hourly"
            ? Number(edits.accepted_work_hours || 0)
            : null,
        accepted_driving_hours:
          (claim.payment_basis || "hourly") === "hourly"
            ? Number(edits.accepted_driving_hours || 0)
            : null,
        accepted_amount: claimAcceptedAmountPayload(claim),
        override_notes: edits.override_notes || null,
        staff_id:
          edits.staff_id === "" || edits.staff_id === null
            ? null
            : Number(edits.staff_id),
        job_date_id:
          edits.job_date_id === "" || edits.job_date_id === null
            ? null
            : Number(edits.job_date_id),
        product_id:
          edits.product_id === "" || edits.product_id === null
            ? null
            : Number(edits.product_id),
        rate_code: String(edits.rate_code || "").trim() || null,
      },
    );
    activeRun.value = normalizeRun(response.data.run);
    syncEmailOverrides(activeRun.value);
    syncClaimEdits(activeRun.value, {
      preserveDirty: true,
      resetClaimIds: [claim.id],
    });
    replaceRunSummary(response.data.run);
    showToast(successMessage);
  } catch (error) {
    showToast(
      error?.response?.data?.message || "Unable to update timesheet claim.",
      "error",
    );
  } finally {
    const after = new Set(savingClaimIds.value);
    after.delete(claim.id);
    savingClaimIds.value = after;
  }
};

const acceptLowerClaim = async (claim) => {
  const edit = claimEdit(claim);
  const hasComponentSplit =
    claim.claimed_work_hours !== null || claim.claimed_driving_hours !== null;
  let suggestedWorkHours;
  let suggestedDrivingHours;

  if (hasComponentSplit) {
    suggestedWorkHours = Number(claim.claimed_work_hours || 0);
    suggestedDrivingHours = Number(claim.claimed_driving_hours || 0);
  } else {
    const claimedTotal = Number(claim.claimed_hours || 0);
    const expectedWork =
      claim.expected_job_hours === null || claim.expected_job_hours === undefined
        ? claimedTotal
        : Number(claim.expected_job_hours);
    const acceptedWork = Math.min(claimedTotal, expectedWork);
    suggestedWorkHours = acceptedWork;
    suggestedDrivingHours = Math.max(0, claimedTotal - acceptedWork);
  }

  if (!edit.accepted_work_hours_touched) {
    edit.accepted_work_hours = suggestedWorkHours;
  }
  if (!edit.accepted_driving_hours_touched) {
    edit.accepted_driving_hours = suggestedDrivingHours;
  }

  if (!String(edit.override_notes || "").trim()) {
    edit.override_notes = "OK";
  }

  await updateClaim(claim, "accepted", {
    successMessage: "Lower submitted hours accepted and locked.",
  });
};

const resetClaimDecision = async (claim) => {
  if (
    !window.confirm(
      "Reset this individual payroll decision? Its accepted values will be cleared and the claim will be recalculated the next time claims are rebuilt.",
    )
  ) {
    return;
  }

  claimEdit(claim).override_notes = "";
  await updateClaim(claim, "needs_review", {
    successMessage: "Claim decision reset. Rebuild Claims when you want Neptune to recalculate it.",
  });
};

const importPayload = (record) => {
  const edit = importEdit(record);
  const paidShiftLeads = (edit.entries || []).filter((entry) => entry.is_shift_lead);
  const paidShiftLead = paidShiftLeads[0];
  const matchedCoWorkerIds = [
    ...new Set(
      (edit.entries || [])
        .map((entry) => Number(entry.co_worker_staff_id))
        .filter((staffId) => Number.isFinite(staffId) && staffId > 0),
    ),
  ];
  const sharedCoWorkerId =
    matchedCoWorkerIds.length === 1
      ? matchedCoWorkerIds[0]
      : edit.co_worker_staff_id || null;
  const sharedCoWorkerName =
    (edit.entries || []).find(
      (entry) => Number(entry.co_worker_staff_id) === Number(sharedCoWorkerId),
    )?.co_worker || edit.co_worker || null;
  const matchedVehicleIds = [
    ...new Set(
      (edit.entries || [])
        .map((entry) => Number(entry.vehicle_id))
        .filter((vehicleId) => Number.isFinite(vehicleId) && vehicleId > 0),
    ),
  ];
  const sharedVehicleId =
    matchedVehicleIds.length === 1
      ? matchedVehicleIds[0]
      : edit.vehicle_id || null;
  const sharedVehicleRegistration =
    (edit.entries || []).find(
      (entry) => Number(entry.vehicle_id) === Number(sharedVehicleId),
    )?.vehicle_registration || edit.vehicle_registration || null;

  return {
    claimant_name: edit.claimant_name || null,
    date: edit.date || null,
    shift_reference: edit.shift_reference || null,
    call_sign: edit.call_sign || null,
    start_of_work_time:
      paidShiftLead?.work_start_time || edit.start_of_work_time || null,
    end_of_work_day_time:
      paidShiftLead?.work_finish_time || edit.end_of_work_day_time || null,
    agreed_rate: edit.agreed_rate || null,
    total_claim_hours:
      edit.total_claim_hours === "" || edit.total_claim_hours === null
        ? null
        : Number(edit.total_claim_hours),
    payment_basis: edit.payment_basis || "hourly",
    claimed_quantity:
      edit.claimed_quantity === "" || edit.claimed_quantity === null
        ? null
        : Number(edit.claimed_quantity),
    claimed_unit_rate:
      edit.claimed_unit_rate === "" || edit.claimed_unit_rate === null
        ? null
        : Number(edit.claimed_unit_rate),
    claimed_amount:
      edit.claimed_amount === "" || edit.claimed_amount === null
        ? null
        : Number(edit.claimed_amount),
    currency: edit.currency || null,
    hours_driving:
      paidShiftLeads.length !== 1
        ? null
        : paidShiftLead.hours_driving === "" || paidShiftLead.hours_driving === null
          ? null
          : Number(paidShiftLead.hours_driving),
    vehicle_registration: sharedVehicleRegistration,
    vehicle_id: sharedVehicleId ? Number(sharedVehicleId) : null,
    co_worker: sharedCoWorkerName,
    co_worker_staff_id: sharedCoWorkerId ? Number(sharedCoWorkerId) : null,
    notes: edit.notes || null,
    staff_id:
      edit.staff_id === "" || edit.staff_id === null ? null : Number(edit.staff_id),
    job_date_id:
      edit.job_date_id === "" || edit.job_date_id === null
        ? null
        : Number(edit.job_date_id),
    product_id:
      edit.product_id === "" || edit.product_id === null
        ? null
        : Number(edit.product_id),
    entries: (edit.entries || []).map((entry, index) => ({
      sequence: entry.sequence || index + 1,
      match_method: entry.match_method || null,
      is_shift_lead: Boolean(entry.is_shift_lead),
      shift_group_key: entry.shift_group_key || null,
      date: entry.date || null,
      shift_reference: entry.shift_reference || null,
      reference_type: entry.reference_type || null,
      patient_reference: entry.patient_reference || null,
      patient_category: entry.patient_category || null,
      job_start_time: entry.job_start_time || null,
      job_start_postcode: entry.job_start_postcode || null,
      job_end_time: entry.job_end_time || null,
      job_end_postcode: entry.job_end_postcode || null,
      work_start_time: entry.work_start_time || null,
      work_finish_time: entry.work_finish_time || null,
      component_type: entry.component_type || "work",
      agreed_rate: entry.agreed_rate || null,
      claimed_hours:
        entry.claimed_hours === "" || entry.claimed_hours === null
          ? null
          : Number(entry.claimed_hours),
      hours_driving:
        entry.hours_driving === "" || entry.hours_driving === null
          ? null
          : Number(entry.hours_driving),
      payment_basis: entry.payment_basis || "hourly",
      quantity:
        entry.quantity === "" || entry.quantity === null
          ? null
          : Number(entry.quantity),
      unit_rate:
        entry.unit_rate === "" || entry.unit_rate === null
          ? null
          : Number(entry.unit_rate),
      amount:
        entry.amount === "" || entry.amount === null
          ? null
          : Number(entry.amount),
      job_date_id:
        entry.job_date_id === "" || entry.job_date_id === null
          ? null
          : Number(entry.job_date_id),
      product_id:
        entry.product_id === "" || entry.product_id === null
          ? null
          : Number(entry.product_id),
      co_worker_staff_id:
        entry.co_worker_staff_id === "" || entry.co_worker_staff_id === null
          ? null
          : Number(entry.co_worker_staff_id),
      co_worker: entry.co_worker || null,
      vehicle_registration: entry.vehicle_registration || null,
      vehicle_id:
        entry.vehicle_id === "" || entry.vehicle_id === null
          ? null
          : Number(entry.vehicle_id),
      notes: entry.notes || null,
    })),
  };
};

const saveImportRecord = async (record) => {
  const viewport = captureViewport();
  const nextIds = new Set(savingImportIds.value);
  nextIds.add(record.id);
  savingImportIds.value = nextIds;

  try {
    await apiClient.put(
      `/api/timesheet-import-records/${record.id}`,
      importPayload(record),
    );
    await loadTimesheetImports();
    showToast("Timesheet import corrections saved.");
  } catch (error) {
    showToast(
      error?.response?.data?.message || "Unable to save timesheet import.",
      "error",
    );
  } finally {
    const after = new Set(savingImportIds.value);
    after.delete(record.id);
    savingImportIds.value = after;
    await restoreViewport(viewport);
  }
};

const updateLocalImportRecord = (updatedRecord) => {
  if (!updatedRecord?.id) return;
  const index = timesheetImportRecords.value.findIndex(
    (record) => record.id === updatedRecord.id,
  );
  if (index >= 0) {
    timesheetImportRecords.value.splice(index, 1, updatedRecord);
  }
};

const discardImportCorrection = async (record) => {
  if (!window.confirm('Discard unsaved corrections to this timesheet and reload its saved values?')) return;
  if (activeImportRow.value?.recordId === record.id) activeImportRow.value = null;
  syncImportEdits([record]);
  importSaveState[record.id] = '';
  delete importCorrectionResults[record.id];
  await loadTimesheetImports();
};

const autosaveImportRecord = (recordId) => {
  if (importAutosaveRequests.has(recordId)) return importAutosaveRequests.get(recordId);
  const request = persistImportRecord(recordId).finally(() => importAutosaveRequests.delete(recordId));
  importAutosaveRequests.set(recordId, request);
  return request;
};
const saveImportProgress = async (record) => {
  if (!record?.id) return false;
  if (record.status === 'imported') return retryImportRecord(record);
  if (importAutosaveTimers.has(record.id)) {
    window.clearTimeout(importAutosaveTimers.get(record.id));
    importAutosaveTimers.delete(record.id);
  }
  await importAutosaveRequests.get(record.id);
  // A change made during an in-flight save queues another timer. Replace it
  // with an immediate save so this includes every value currently shown.
  if (importAutosaveTimers.has(record.id)) {
    window.clearTimeout(importAutosaveTimers.get(record.id));
    importAutosaveTimers.delete(record.id);
  }
  await autosaveImportRecord(record.id);
  if (importSaveState[record.id] === "error") {
    showToast("Unable to save this timesheet's progress.", "error");
    return false;
  }
  showToast("Timesheet progress saved. You can return to finish matching it later.");
  return true;
};
const persistImportRecord = async (recordId) => {
  const record = timesheetImportRecords.value.find((item) => item.id === recordId);
  const edit = importEdits[recordId];
  if (!record || !edit) return;
  if (record.status === "imported") {
    importSaveState[recordId] = "correction";
    return;
  }

  const payload = importPayload(record);
  const serialized = JSON.stringify(payload);
  if (serialized === importAutosaveSnapshots.get(recordId)) return;
  if (importSaveState[recordId] === "saving") {
    queueImportAutosave(recordId, 300);
    return;
  }

  importSaveState[recordId] = "saving";
  const nextIds = new Set(savingImportIds.value);
  nextIds.add(recordId);
  savingImportIds.value = nextIds;

  try {
    const response = await apiClient.put(
      `/api/timesheet-import-records/${recordId}`,
      { ...payload, base_updated_at: record.updated_at || null },
    );
    importAutosaveSnapshots.set(recordId, serialized);
    updateLocalImportRecord(response.data?.record);
    importSaveState[recordId] = "saved";
    window.setTimeout(() => {
      if (importSaveState[recordId] === "saved") importSaveState[recordId] = "";
    }, 1800);

    if (JSON.stringify(importPayload(record)) !== serialized) {
      queueImportAutosave(recordId, 250);
    }
  } catch (error) {
    importSaveState[recordId] = "error";
    if (error?.response?.status === 409 && error.response.data?.record) {
      updateLocalImportRecord(error.response.data.record);
    }
    showToast(
      error?.response?.data?.message || "Unable to auto-save timesheet changes.",
      "error",
    );
  } finally {
    const after = new Set(savingImportIds.value);
    after.delete(recordId);
    savingImportIds.value = after;
  }
};

function queueImportAutosave(recordOrId, delay = 750) {
  const recordId = Number(recordOrId?.id || recordOrId);
  if (!recordId) return;
  const record = timesheetImportRecords.value.find(item => item.id === recordId);
  if (record?.status === 'imported') {
    importSaveState[recordId] = 'correction';
    delete importCorrectionResults[recordId];
    return;
  }
  if (importAutosaveTimers.has(recordId)) {
    window.clearTimeout(importAutosaveTimers.get(recordId));
  }
  importSaveState[recordId] = "pending";
  importAutosaveTimers.set(
    recordId,
    window.setTimeout(() => {
      importAutosaveTimers.delete(recordId);
      void autosaveImportRecord(recordId);
    }, delay),
  );
}

async function flushPendingImportAutosaves() {
  const pendingRecordIds = [...importAutosaveTimers.keys()];
  pendingRecordIds.forEach((recordId) => {
    window.clearTimeout(importAutosaveTimers.get(recordId));
    importAutosaveTimers.delete(recordId);
  });
  await Promise.all(
    pendingRecordIds.map((recordId) => autosaveImportRecord(recordId)),
  );
}

function importRecordHasUnsavedChanges(record) {
  if (!record?.id || !importEdits[record.id]) return false;
  return (
    JSON.stringify(importPayload(record)) !==
    importAutosaveSnapshots.get(Number(record.id))
  );
}

const presenceByRow = computed(() => {
  const grouped = new Map();
  timesheetPresence.value.forEach((presence) => {
    const key = `${Number(presence.record_id)}:${Number(presence.entry_sequence)}`;
    if (!grouped.has(key)) grouped.set(key, []);
    grouped.get(key).push(presence);
  });
  return grouped;
});
const presenceForRow = (recordId, sequence) =>
  presenceByRow.value.get(`${Number(recordId)}:${Number(sequence)}`) || [];
const presenceInitials = (name) =>
  String(name || "?")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");
const presenceColour = (userId) => {
  const colours = ["bg-sky-600", "bg-violet-600", "bg-emerald-600", "bg-rose-600", "bg-amber-600"];
  return colours[Math.abs(Number(userId) || 0) % colours.length];
};

const touchActiveImportPresence = async () => {
  const active = activeImportRow.value;
  if (!active) return;
  await apiClient.put(`/api/timesheet-import-records/${active.recordId}/presence`, {
    tab_id: presenceTabId,
    entry_sequence: active.sequence,
  });
};
const focusImportRow = (row) => {
  retainedReviewIds.value = new Set([...retainedReviewIds.value, row.record.id]);
  const previous = activeImportRow.value;
  if (previous && previous.recordId !== row.record.id) {
    void apiClient
      .delete(`/api/timesheet-import-records/${previous.recordId}/presence`, {
        data: { tab_id: presenceTabId },
      })
      .catch(() => {
        // Presence expires automatically; switching sheets must remain instant.
      });
  }
  activeImportRow.value = {
    recordId: row.record.id,
    sequence: row.entry.sequence || row.entryIndex + 1,
  };
  void touchActiveImportPresence();
};
const blurImportRow = (event, row) => {
  if (event.currentTarget?.contains(event.relatedTarget)) return;
  window.setTimeout(async () => {
    if (
      activeImportRow.value?.recordId !== row.record.id ||
      activeImportRow.value?.sequence !== (row.entry.sequence || row.entryIndex + 1)
    ) return;
    activeImportRow.value = null;
    try {
      await apiClient.delete(`/api/timesheet-import-records/${row.record.id}/presence`, {
        data: { tab_id: presenceTabId },
      });
    } catch {
      // Presence expires automatically; editing must never be blocked by it.
    }
  }, 100);
};

const loadTimesheetPresence = async () => {
  if (!activeRunId.value) return false;
  const response = await apiClient.get("/api/timesheet-import-presence", {
    params: { payroll_run_id: activeRunId.value },
  });
  timesheetPresence.value = response.data?.presence || [];
  const localRecords = new Map(
    timesheetImportRecords.value.map((record) => [Number(record.id), record]),
  );
  return (response.data?.versions || []).some((version) => {
    const local = localRecords.get(Number(version.id));
    if (local) {
      return local.updated_at !== version.updated_at || local.status !== version.status;
    }
    return importStatusFilter.value === "all" || importStatusFilter.value === version.status;
  });
};

const stopTimesheetCollaborationPolling = () => {
  if (timesheetCollaborationTimeout) {
    window.clearTimeout(timesheetCollaborationTimeout);
    timesheetCollaborationTimeout = null;
  }
};
const pollTimesheetCollaboration = async () => {
  if (timesheetCollaborationInFlight || workspace.value !== "timesheets" || !activeRunId.value) return;
  timesheetCollaborationInFlight = true;
  try {
    const [importsChanged] = await Promise.all([
      loadTimesheetPresence(),
      touchActiveImportPresence(),
    ]);
    const fullRefreshDue = Date.now() - lastFullTimesheetSyncAt >= 30000;
    const editingLocally =
      activeImportRow.value ||
      importAutosaveTimers.size > 0 ||
      [...Object.values(importSaveState)].includes("saving");
    if (!editingLocally && (importsChanged || fullRefreshDue)) {
      await loadTimesheetImports({ background: true });
    }
  } catch {
    // Background collaboration sync is best effort; foreground actions report errors.
  } finally {
    timesheetCollaborationInFlight = false;
    stopTimesheetCollaborationPolling();
    if (workspace.value === "timesheets" && activeRunId.value) {
      timesheetCollaborationTimeout = window.setTimeout(pollTimesheetCollaboration, 3000);
    }
  }
};
const startTimesheetCollaborationPolling = () => {
  stopTimesheetCollaborationPolling();
  if (workspace.value === "timesheets" && activeRunId.value) {
    void pollTimesheetCollaboration();
  }
};

const stopFinalPayrollChecksPolling = () => {
  if (finalPayrollChecksPollTimeout) {
    window.clearTimeout(finalPayrollChecksPollTimeout);
    finalPayrollChecksPollTimeout = null;
  }
};
const pollFinalPayrollChecks = async () => {
  if (
    finalPayrollChecksPollInFlight ||
    workspace.value !== "final" ||
    !activeRunId.value
  ) {
    return;
  }

  const runId = Number(activeRunId.value);
  finalPayrollChecksPollInFlight = true;
  try {
    const response = await apiClient.get(
      `/api/payroll-runs/${runId}/staff-checks`,
    );
    if (Number(activeRunId.value) === runId && activeRun.value) {
      activeRun.value = {
        ...activeRun.value,
        staff_checks: response.data?.staff_checks || [],
      };
    }
  } catch {
    // Background reviewer sync is best effort; the next poll will retry.
  } finally {
    finalPayrollChecksPollInFlight = false;
    stopFinalPayrollChecksPolling();
    if (workspace.value === "final" && activeRunId.value) {
      finalPayrollChecksPollTimeout = window.setTimeout(
        pollFinalPayrollChecks,
        3000,
      );
    }
  }
};
const startFinalPayrollChecksPolling = () => {
  stopFinalPayrollChecksPolling();
  if (workspace.value === "final" && activeRunId.value) {
    void pollFinalPayrollChecks();
  }
};

const retryImportRecord = async (record, reviewSeconds = 0) => {
  const blocker = importRetryBlocker(record);
  if (blocker) {
    showTimesheetSaveError(record, [blocker]);
    return;
  }

  if (importAutosaveTimers.has(record.id)) {
    window.clearTimeout(importAutosaveTimers.get(record.id));
    importAutosaveTimers.delete(record.id);
  }
  await importAutosaveRequests.get(record.id);
  if (importSaveState[record.id] === "error") {
    showToast("The last save failed. Refresh and review this timesheet before confirming.", "error");
    return false;
  }
  // An in-flight save can have queued a second save; confirmation includes those edits.
  if (importAutosaveTimers.has(record.id)) {
    window.clearTimeout(importAutosaveTimers.get(record.id));
    importAutosaveTimers.delete(record.id);
  }
  record = timesheetImportRecords.value.find(item => item.id === record.id) || record;

  const viewport = captureViewport();
  const nextIds = new Set(savingImportIds.value);
  nextIds.add(record.id);
  savingImportIds.value = nextIds;

  try {
    const submittedPayload = importPayload(record);
    const correcting = record.status === "imported";
    const response = await apiClient.post(
      `/api/timesheet-import-records/${record.id}/retry`,
      { ...submittedPayload, base_updated_at: record.updated_at || null, review_seconds: Math.min(reviewSeconds, 604800) },
    );
    if (response.data?.result?.status === "imported") {
      importAutosaveSnapshots.set(record.id, JSON.stringify(submittedPayload));
      updateLocalImportRecord(response.data.record);
      importSaveState[record.id] = "saved";
      try {
        await loadTimesheetImports({ background: true });
        if (activeRunId.value) {
          if (correcting) await loadRun(activeRunId.value, { preserveLocalEdits: true, throwOnError: true });
          else await refreshClaims({ silent: true });
          await loadUnclaimedWork();
        }
        const missing = missingImportApprovals(response.data.record, approvalSourceIds.value);
        const edit = importEdit(response.data.record);
        const relevantMissing = unclaimedWorkReport.value.records.filter(item => !item.has_claim
          && Number(item.staff_id) === Number(edit.staff_id)
          && (edit.entries || []).some(entry => Number(entry.job_date_id) === Number(item.job_date_id)));
        const regroupingMessage = response.data.result.regrouped
          ? `Replaced ${response.data.result.previous_claim_count} combined claim(s) with ${response.data.result.segments.length} shift claim(s), all awaiting hours review. `
          : '';
        const message = regroupingMessage + (unclaimedWorkError.value ? 'Saved; Missing Claims could not be checked. Refresh Check to verify.'
          : missing.length ? `Saved, but ${missing.length} shift(s) are still absent from Claim Approval.`
          : relevantMissing.length ? 'Saved to Claim Approval, but a linked assignment is still missing. Check the work date and job link.'
          : 'Saved to Claim Approval. No linked assignments remain in Missing Claims; verify payable hours in Claim Approval.');
        importCorrectionResults[record.id] = message;
        showToast(message, missing.length || relevantMissing.length || unclaimedWorkError.value ? 'error' : 'success');
      } catch {
        showToast(
          "Timesheet imported, but the page could not refresh claim approvals. Use Refresh before continuing payroll review.",
          "error",
        );
      }
      return true;
    } else {
      showTimesheetSaveError(record, [response.data?.result?.message || "Timesheet still needs review."]);
    }
  } catch (error) {
    const validationMessages = Object.values(error?.response?.data?.errors || {}).flat();
    const message = validationMessages.length ? validationMessages.join('\n') : error?.response?.data?.message || 'Unable to save timesheet. The correction has not been applied.';
    importCorrectionResults[record.id] = message;
    showTimesheetSaveError(record, validationMessages.length ? validationMessages : [message], error?.response?.data);
  } finally {
    const after = new Set(savingImportIds.value);
    after.delete(record.id);
    savingImportIds.value = after;
    await restoreViewport(viewport);
  }
};

const excludeImportRecord = async (record) => {
  const viewport = captureViewport();
  const next = new Set(savingImportIds.value);
  next.add(record.id);
  savingImportIds.value = next;

  try {
    await apiClient.put(`/api/timesheet-import-records/${record.id}`, {
      ...importPayload(record),
      status: "excluded",
    });
    await loadTimesheetImports();
    if (activeRunId.value) {
      await loadRun(activeRunId.value);
    }
    showToast("Timesheet evidence excluded from this payroll run.");
  } catch (error) {
    showToast(
      error?.response?.data?.message || "Unable to exclude timesheet evidence.",
      "error",
    );
  } finally {
    const after = new Set(savingImportIds.value);
    after.delete(record.id);
    savingImportIds.value = after;
    await restoreViewport(viewport);
  }
};

const staffDisplayName = (staff) => staff?.contact?.name || staff?.name || "Unnamed staff";
const staffDisplayEmail = (staff) => staff?.contact?.email || staff?.email || "";
const savingPayslipIds = ref(new Set());

const beginStaffMatch = (payslip) => {
  staffSearch.payslipId = payslip.id;
  staffSearch.query = payslip.employee_name || "";
  staffSearch.results = [];
  staffSearch.error = "";
};

const clearStaffSearch = () => {
  staffSearch.payslipId = null;
  staffSearch.query = "";
  staffSearch.results = [];
  staffSearch.error = "";
};

const searchStaff = async () => {
  const query = staffSearch.query.trim();
  if (query.length < 2) {
    staffSearch.error = "Enter at least 2 characters.";
    staffSearch.results = [];
    return;
  }

  staffSearch.loading = true;
  staffSearch.error = "";
  try {
    const response = await apiClient.get("/api/staff/search", {
      params: { query, limit: 8 },
    });
    staffSearch.results = Array.isArray(response.data) ? response.data : [];
    if (staffSearch.results.length === 0) {
      staffSearch.error = "No staff matched that search.";
    }
  } catch (error) {
    staffSearch.error =
      error?.response?.data?.message || "Unable to search staff.";
  } finally {
    staffSearch.loading = false;
  }
};

const updatePayslip = async (payslip, payload, message) => {
  const nextIds = new Set(savingPayslipIds.value);
  nextIds.add(payslip.id);
  savingPayslipIds.value = nextIds;

  try {
    const response = await apiClient.put(
      `/api/payroll-runs/${activeRunId.value}/payslips/${payslip.id}`,
      payload,
    );
    activeRun.value = normalizeRun(response.data.run);
    syncEmailOverrides(activeRun.value);
    syncClaimEdits(activeRun.value);
    replaceRunSummary(response.data.run);
    clearStaffSearch();
    showToast(message);
  } catch (error) {
    showToast(
      error?.response?.data?.message || "Unable to update payslip.",
      "error",
    );
  } finally {
    const after = new Set(savingPayslipIds.value);
    after.delete(payslip.id);
    savingPayslipIds.value = after;
  }
};

const setPayslipStaff = (payslip, staff) =>
  updatePayslip(
    payslip,
    { staff_id: staff.id },
    `Matched ${payslip.employee_name} to ${staffDisplayName(staff)}.`,
  );

const clearPayslipStaff = (payslip) =>
  updatePayslip(payslip, { staff_id: null }, "Staff match cleared.");

const saveEmailOverride = (payslip) =>
  updatePayslip(
    payslip,
    { email_override: emailOverrides[payslip.id] || null },
    "Delivery email updated.",
  );

const openPayslip = async (payslip) => {
  const nextIds = new Set(viewingIds.value);
  nextIds.add(payslip.id);
  viewingIds.value = nextIds;

  const previewWindow = window.open("", "_blank");

  try {
    if (!previewWindow) {
      throw new Error("Preview window was blocked by the browser.");
    }

    previewWindow.document.title = "Opening payslip...";
    previewWindow.document.body.innerHTML =
      '<div style="font-family: sans-serif; padding: 24px; color: #0f172a;">Opening payslip...</div>';

    const response = await apiClient.get(payslip.view_url, {
      responseType: "blob",
    });
    const sourceBlob =
      response.data instanceof Blob ? response.data : new Blob([response.data]);
    const pdfBlob =
      sourceBlob.type === "application/pdf"
        ? sourceBlob
        : new Blob([sourceBlob], { type: "application/pdf" });
    const objectUrl = window.URL.createObjectURL(pdfBlob);

    previewWindow.location.replace(objectUrl);

    window.setTimeout(() => {
      window.URL.revokeObjectURL(objectUrl);
    }, 300_000);
  } catch (error) {
    if (previewWindow) {
      previewWindow.close();
    }
    loadingError.value = sendErrorMessage(error, "Unable to open payslip.");
  } finally {
    const after = new Set(viewingIds.value);
    after.delete(payslip.id);
    viewingIds.value = after;
  }
};

const closeAudit = () => {
  auditOpen.value = false;
  auditData.value = null;
};

const openAudit = async (payslip) => {
  auditOpen.value = true;
  auditLoading.value = true;
  auditData.value = {
    payslip: {
      employee_name: payslip.employee_name,
      recipient_email: payslip.recipient_email,
      deliveries: [],
    },
  };

  try {
    const response = await apiClient.get(payslip.audit_url);
    auditData.value = response.data;
  } catch (error) {
    showToast(sendErrorMessage(error, "Unable to load audit trail."), "error");
    closeAudit();
  } finally {
    auditLoading.value = false;
  }
};

watch(
  isSettingsPage,
  async (settingsPage) => {
    closeAudit();

    if (settingsPage) {
      await loadSettings();
      return;
    }

    if (availableProducts.value.length === 0) {
      await loadProducts();
    }
    await loadRuns();
  },
  { immediate: true },
);

watch(importStatusFilter, async () => {
  if (!isSettingsPage.value) {
    await loadTimesheetImports();
  }
});
watch(timesheetReadinessFilter, (value, previous) => {
  retainedReviewIds.value = new Set();
  if (value === 'duplicates' || previous === 'duplicates') void loadTimesheetImports();
});

watch(
  () => route.query.page,
  (page) => {
    if (isSettingsPage.value) return;
    const routeWorkspace = payrollWorkspaceFromPage(page);
    if (workspace.value !== routeWorkspace) {
      workspace.value = routeWorkspace;
    }
  },
);

watch([workspace, activeRunId], ([selectedWorkspace, runId]) => {
  if (!isSettingsPage.value) {
    const page = payrollPageFromWorkspace(selectedWorkspace);
    const currentPage = Array.isArray(route.query.page)
      ? route.query.page[0]
      : route.query.page;
    if (currentPage !== page) {
      void router.replace({
        query: { ...route.query, page },
      });
    }
  }
  if (selectedWorkspace === "unclaimed" && runId) {
    loadUnclaimedWork();
  }
  startTimesheetCollaborationPolling();
  startFinalPayrollChecksPolling();
});

watch(
  () =>
    `${activeRunId.value || ""}|${pendingTimesheetDocuments()
      .map((document) => `${document.id}:${document.status}`)
      .join(",")}`,
  () => {
    if (pendingTimesheetDocuments().length > 0) {
      scheduleTimesheetProcessingPoll(1500);
    } else {
      stopTimesheetProcessingPolling();
    }
  },
);

const warnUnsavedImports = (event) => {
  if (!timesheetImportRecords.value.some(importRecordHasUnsavedChanges)) return;
  event.preventDefault();
  event.returnValue = '';
};
onBeforeRouteLeave(() => {
  if (!timesheetImportRecords.value.some(importRecordHasUnsavedChanges)) return true;
  return window.confirm('There are unsaved timesheet changes. Leave without saving them?');
});
onMounted(() => {
  window.addEventListener('beforeunload', warnUnsavedImports);
  window.addEventListener("keydown", handleTimesheetImagePreviewKeydown);
});

const findImportMatches = async (record) => {
  refreshingMatches.value = true;
  try {
    const payload = importPayload(record);
    const response = await apiClient.post(`/api/timesheet-import-records/${record.id}/matches`, payload);
    if (JSON.stringify(payload) !== JSON.stringify(importPayload(record))) {
      showToast("The timesheet changed while matching. Select Find matches again for the updated values.", "error");
      return;
    }
    const matches = response.data;
    const edit = importEdit(record);
    if (!edit.staff_id && matches.staff) setImportStaff(record, matches.staff);
    (matches.entries || []).forEach((match) => {
      const entry = edit.entries.find((item) => Number(item.sequence) === Number(match.sequence));
      if (!entry) return;
      entry.match_suggestions = match.suggestions || [];
      if (!entry.job_date_id && match.job) setImportEntryJobDate(entry, match.job, record);
      if (!entry.vehicle_id && match.vehicle) setImportEntryVehicle(entry, match.vehicle, record);
    });
    showToast("Matches refreshed. Check the suggestions before confirming.");
  } catch (error) {
    showToast(error?.response?.data?.message || "Unable to find matches.", "error");
  } finally { refreshingMatches.value = false; }
};

const updateImportEntryDate = async (entry, record) => {
  const entries = importEntriesForRecord(record);
  entries
    .filter(
      (candidate) =>
        candidate === entry ||
        (entry.shift_group_key &&
          candidate.shift_group_key === entry.shift_group_key),
    )
    .forEach((candidate) => {
      candidate.date = entry.date;
    });
  const lead = entries.find(
    (candidate) =>
      candidate.is_shift_lead &&
      candidate.shift_group_key === entry.shift_group_key,
  );
  const linked = lead || entry;
  if ((entry.job_date_id || lead?.job_date_id) && linked.job_date_date === entry.date) return;
  if (entry.job_date_id || lead?.job_date_id) setImportEntryJobDate(linked, null, record);
  entry.match_suggestions = [];
  await findImportMatches(record);
};

const reviewActions = {
  formatDate, title: importRecordTitle, status: groupStatusLabel,
  staffOutput: staffSearchOutput, staffQuery: updateImportStaffQuery, staff: setImportStaff,
  jobOutput: jobDateSearchOutput, jobUrl: importJobDateSearchUrl,
  jobQuery: updateImportEntryJobQuery, job: setImportEntryJobDate, date: updateImportEntryDate,
  vehicleOutput: vehicleSearchOutput, vehicleQuery: updateImportEntryVehicleQuery, vehicle: setImportEntryVehicle,
  coWorkerQuery: updateImportEntryCoWorkerQuery, coWorker: setImportEntryCoWorker,
  product: setImportEntryProduct, hoursDiffer: entryHoursDiffer,
  hours: formatHoursValue, calculatedHours: calculatedEntryHours,
  focus: focusImportRow, blur: blurImportRow, presence: presenceForRow,
  confirm: retryImportRecord, save: saveImportProgress, blocker: importRetryBlocker, rematch: findImportMatches,
  add: addImportEntry, reopen: reopenImportForCorrection, exclude: excludeImportRecord,
  deleteEntry: deleteImportEntry, deleteDocument: deleteTimesheetDocument, reprocess: reprocessTimesheet,
};

onBeforeUnmount(() => {
  window.removeEventListener('beforeunload', warnUnsavedImports);
  if (toastTimeout) {
    clearTimeout(toastTimeout);
  }
  stopTimesheetProcessingPolling();
  stopTimesheetCollaborationPolling();
  stopFinalPayrollChecksPolling();
  importAutosaveTimers.forEach((timer) => window.clearTimeout(timer));
  importAutosaveTimers.clear();
  importEditWatchStops.forEach((stop) => stop());
  importEditWatchStops.clear();
  window.removeEventListener("keydown", handleTimesheetImagePreviewKeydown);
  closeTimesheetImagePreview();
});
</script>

<template>
  <PayrollExportBlockersDialog :open="exportBlockersOpen" :preflight="accountantPreflight" @close="exportBlockersOpen = false" @resolve="resolveExportBlocker" />
  <TimesheetSaveErrorDialog :error="timesheetSaveError" @close="timesheetSaveError = null" @open-import="openConflictingImport" @duplicates="timesheetSaveError = null; clearTimesheetFocus(); timesheetReadinessFilter = 'duplicates'" />
  <div class="space-y-6">
    <header v-if="!isSettingsPage && workspace === 'timesheets'" class="flex flex-wrap items-center justify-between gap-3">
      <div>
        <h1 class="text-xl font-semibold text-slate-900 dark:text-white">Payroll <span v-if="activeRun" class="font-normal text-slate-500">· {{ activeRun.name }}</span></h1>
        <p v-if="activeRun" class="text-sm text-slate-500 dark:text-slate-400">{{ periodLabel(activeRun) }}</p>
      </div>
      <RouterLink v-if="isSuper" :to="{ name: 'payroll-settings' }" class="text-sm text-sky-700 dark:text-sky-300">Payroll settings</RouterLink>
    </header>
    <section
      v-else
      class="mb-6 w-full overflow-hidden rounded-xl border border-slate-200 bg-gradient-to-br from-slate-50 via-white to-slate-100 p-4 lg:p-6 dark:border-slate-800 dark:from-slate-950 dark:via-slate-950 dark:to-slate-900"
    >
      <div
        class="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between"
      >
        <div class="max-w-3xl space-y-4">
          <div class="inline-flex rounded-full border border-slate-200 bg-white/80 p-1 shadow-sm dark:border-slate-700 dark:bg-slate-900/75">
            <RouterLink
              :to="{ name: 'payroll-runs' }"
              class="rounded-full px-4 py-2 text-sm font-medium transition"
              :class="
                !isSettingsPage
                  ? 'bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900'
                  : 'text-slate-600 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white'
              "
            >
              Runs
            </RouterLink>
            <RouterLink
              v-if="isSuper"
              :to="{ name: 'payroll-settings' }"
              class="rounded-full px-4 py-2 text-sm font-medium transition"
              :class="
                isSettingsPage
                  ? 'bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900'
                  : 'text-slate-600 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white'
              "
            >
              Settings
            </RouterLink>
          </div>
          <div>
            <h1
              class="text-2xl font-semibold tracking-tight text-slate-900 dark:text-white"
            >
              {{ pageTitle }}
            </h1>
            <p class="mt-2 text-sm text-slate-600 dark:text-slate-400">
              {{ pageDescription }}
            </p>
          </div>
        </div>
        <div
          v-if="!isSettingsPage"
          class="grid grid-cols-2 gap-3 sm:grid-cols-4"
        >
          <div
            class="rounded-2xl bg-white/80 px-4 py-3 ring-1 ring-slate-200 dark:bg-slate-900/75 dark:ring-slate-700"
          >
            <div
              class="text-[11px] font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400"
            >
              Runs
            </div>
            <div
              class="mt-1 text-2xl font-semibold text-slate-900 dark:text-white"
            >
              {{ sortedRuns.length }}
            </div>
          </div>
          <div
            class="rounded-2xl bg-white/80 px-4 py-3 ring-1 ring-slate-200 dark:bg-slate-900/75 dark:ring-slate-700"
          >
            <div
              class="text-[11px] font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400"
            >
              Active
            </div>
            <div
              class="mt-1 text-lg font-semibold text-slate-900 dark:text-white"
            >
              {{ activeRun?.name || "None" }}
            </div>
          </div>
          <div
            class="rounded-2xl bg-white/80 px-4 py-3 ring-1 ring-slate-200 dark:bg-slate-900/75 dark:ring-slate-700"
          >
            <div
              class="text-[11px] font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400"
            >
              Sent
            </div>
            <div
              class="mt-1 text-2xl font-semibold text-slate-900 dark:text-white"
            >
              {{ sentCount }}
            </div>
          </div>
          <div
            class="rounded-2xl bg-white/80 px-4 py-3 ring-1 ring-slate-200 dark:bg-slate-900/75 dark:ring-slate-700"
          >
            <div
              class="text-[11px] font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400"
            >
              Needs Review
            </div>
            <div
              class="mt-1 text-2xl font-semibold text-slate-900 dark:text-white"
            >
              {{ unresolvedCount + errorCount + claimReviewCount + importReviewCount }}
            </div>
          </div>
        </div>
        <div
          v-else
          class="rounded-2xl bg-white/80 px-4 py-4 ring-1 ring-slate-200 dark:bg-slate-900/75 dark:ring-slate-700"
        >
          <div
            class="text-[11px] font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400"
          >
            Access
          </div>
          <div class="mt-1 text-lg font-semibold text-slate-900 dark:text-white">
            Super Users Only
          </div>
          <div class="mt-2 text-sm text-slate-500 dark:text-slate-400">
            Configure payroll defaults and controls for future runs.
          </div>
        </div>
      </div>
    </section>

    <section
      v-if="isSettingsPage"
      class="box border border-slate-200 dark:border-slate-700 dark:bg-slate-900/70"
    >
      <div
        class="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between"
      >
        <div>
          <h2 class="text-xl font-semibold text-slate-900 dark:text-white">
            Payroll Settings
          </h2>
          <p class="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Central payroll defaults that only super users can update.
          </p>
        </div>
        <button
          class="btn"
          :disabled="settingsLoading || settingsSaving"
          @click.prevent="loadSettings"
        >
          Refresh Settings
        </button>
      </div>

      <div v-if="settingsLoading" class="mt-6 text-sm text-slate-500 dark:text-slate-400">
        Loading payroll settings...
      </div>

      <div v-else class="mt-6 space-y-6">
        <div class="grid gap-4 lg:grid-cols-2 xl:grid-cols-3">
          <label class="block">
            <span class="text-sm font-medium text-slate-700 dark:text-slate-200">
              Pay Run Start Date
            </span>
            <input
              v-model="settingsForm.pay_run_start_date"
              type="date"
              class="mt-2 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm transition focus:border-sky-400 focus:outline-none focus:ring-4 focus:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:[color-scheme:dark] dark:focus:border-sky-500 dark:focus:ring-sky-500/20"
            />
          </label>

          <label class="block">
            <span class="text-sm font-medium text-slate-700 dark:text-slate-200">
              Pay Run Finish Date
            </span>
            <input
              v-model="settingsForm.pay_run_finish_date"
              type="date"
              class="mt-2 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm transition focus:border-sky-400 focus:outline-none focus:ring-4 focus:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:[color-scheme:dark] dark:focus:border-sky-500 dark:focus:ring-sky-500/20"
            />
          </label>

          <label class="block">
            <span class="text-sm font-medium text-slate-700 dark:text-slate-200">
              Claim Tolerance Minutes
            </span>
            <input
              v-model="settingsForm.claim_tolerance_minutes"
              type="number"
              min="0"
              step="1"
              class="mt-2 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm transition placeholder:text-slate-400 focus:border-sky-400 focus:outline-none focus:ring-4 focus:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:placeholder:text-slate-500 dark:focus:border-sky-500 dark:focus:ring-sky-500/20"
              placeholder="15"
            />
          </label>
        </div>

        <div class="rounded-2xl border border-slate-200 p-4 dark:border-slate-700">
          <div class="text-sm font-semibold text-slate-900 dark:text-white">
            Accountant workbook hourly rates
          </div>
          <p class="mt-1 text-xs text-slate-500 dark:text-slate-400">
            These values appear above the FA, PTS and EMT hour columns in the final workbook.
          </p>
          <div class="mt-4 grid gap-4 sm:grid-cols-3">
            <label v-for="code in ['FA', 'PTS', 'EMT']" :key="code" class="block">
              <span class="text-sm font-medium text-slate-700 dark:text-slate-200">
                {{ code }} hourly rate
              </span>
              <input
                v-model="settingsForm.accountant_export_rates[code]"
                type="number"
                min="0"
                step="0.01"
                class="mt-2 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm focus:border-sky-400 focus:outline-none focus:ring-4 focus:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:border-sky-500 dark:focus:ring-sky-500/20"
              />
            </label>
          </div>
        </div>

        <div class="rounded-2xl border border-slate-200 p-4 dark:border-slate-700">
          <div class="text-sm font-semibold text-slate-900 dark:text-white">
            Payroll cost estimate assumptions
          </div>
          <p class="mt-1 text-xs text-slate-500 dark:text-slate-400">
            Used only for the planning estimate on Final Payroll. The accountant’s payroll calculation remains authoritative.
          </p>
          <div class="mt-4 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <label
              v-for="field in [
                { key: 'holiday_pay_percent', label: 'Holiday pay' },
                { key: 'employee_pension_percent', label: 'Employee pension' },
                { key: 'employer_pension_percent', label: 'Employer pension' },
                { key: 'employer_ni_percent', label: 'Employer NI' },
              ]"
              :key="field.key"
              class="block"
            >
              <span class="text-sm font-medium text-slate-700 dark:text-slate-200">
                {{ field.label }} %
              </span>
              <input
                v-model="settingsForm.cost_estimation_settings[field.key]"
                type="number"
                min="0"
                max="100"
                step="0.01"
                class="mt-2 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm focus:border-sky-400 focus:outline-none focus:ring-4 focus:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:border-sky-500 dark:focus:ring-sky-500/20"
              />
            </label>
          </div>
        </div>

        <div
          v-if="settingsError"
          class="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-300"
        >
          {{ settingsError }}
        </div>
        <div
          v-else-if="settingsNotice"
          class="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-300"
        >
          {{ settingsNotice }}
        </div>
        <div v-else class="text-sm text-slate-500 dark:text-slate-400">
          Estimates use 2026/27 monthly PAYE, NI and qualifying-earnings thresholds.
        </div>

        <div class="flex justify-end">
          <button
            class="btn accent w-full sm:w-auto"
            :disabled="settingsLoading || settingsSaving"
            @click.prevent="saveSettings"
          >
            {{ settingsSaving ? "Saving..." : "Save Settings" }}
          </button>
        </div>
      </div>
    </section>

    <template v-else>
    <div class="flex flex-wrap gap-2 border-b border-slate-200 pb-2 dark:border-slate-700">
      <button
        type="button"
        class="rounded-xl px-4 py-2 text-sm font-medium transition"
        :class="
          workspace === 'delivery'
            ? 'bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900'
            : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-white'
        "
        @click="workspace = 'delivery'"
      >
        Runs & Files
      </button>
      <button
        type="button"
        class="rounded-xl px-4 py-2 text-sm font-medium transition"
        :class="
          workspace === 'timesheets'
            ? 'bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900'
            : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-white'
        "
        @click="workspace = 'timesheets'"
      >
        Timesheet Imports
        <span
          class="ml-2 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-700"
        >
          {{ importReviewCount }} need review
        </span>
      </button>
      <button
        type="button"
        class="rounded-xl px-4 py-2 text-sm font-medium transition"
        :class="
          workspace === 'claims'
            ? 'bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900'
            : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-white'
        "
        @click="workspace = 'claims'"
      >
        Claim Approval
        <span
          class="ml-2 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-700"
        >
          {{ claimReviewCount }} review / {{ acceptedClaimsCount }} approved
        </span>
      </button>
      <button
        type="button"
        class="rounded-xl px-4 py-2 text-sm font-medium transition"
        :class="
          workspace === 'final'
            ? 'bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900'
            : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-white'
        "
        @click="workspace = 'final'"
      >
        Final Payroll
        <span
          class="ml-2 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-700"
        >
          {{ acceptedClaimsCount }} approved
        </span>
      </button>
      <button
        type="button"
        class="rounded-xl px-4 py-2 text-sm font-medium transition"
        :class="
          workspace === 'unclaimed'
            ? 'bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900'
            : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-white'
        "
        @click="workspace = 'unclaimed'"
      >
        Missing Claims
        <span
          v-if="unclaimedWorkReport.summary.missing_employees"
          class="ml-2 rounded-full bg-rose-100 px-2 py-0.5 text-[11px] text-rose-700"
        >
          {{ unclaimedWorkReport.summary.missing_employees }} missing
        </span>
      </button>
    </div>

    <template v-if="workspace === 'delivery'">
    <section
      class="box border border-slate-200 dark:border-slate-700 dark:bg-slate-900/70"
    >
      <div
        class="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between"
      >
        <div>
          <h2 class="text-xl font-semibold text-slate-900 dark:text-white">
            Create Payroll Run
          </h2>
          <p class="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Create the pay period first, then upload returned accountant files
            when they are ready.
          </p>
        </div>
        <button
          class="btn"
          :disabled="loadingRuns || loadingRun"
          title="Reload server data without discarding unresolved local edits"
          @click.prevent="refreshRunSafely"
        >
          Refresh Runs
        </button>
      </div>

      <div class="mt-6 grid gap-4 lg:grid-cols-2 xl:grid-cols-6">
        <label class="block xl:col-span-2">
          <span class="text-sm font-medium text-slate-700 dark:text-slate-200"
            >Run Name</span
          >
          <input
            v-model="createForm.name"
            type="text"
            class="mt-2 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm transition placeholder:text-slate-400 focus:border-sky-400 focus:outline-none focus:ring-4 focus:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:placeholder:text-slate-500 dark:focus:border-sky-500 dark:focus:ring-sky-500/20"
            placeholder="Month 11 2026"
          />
        </label>

        <label class="block">
          <span class="text-sm font-medium text-slate-700 dark:text-slate-200"
            >Tax Month</span
          >
          <input
            v-model="createForm.tax_month"
            type="number"
            min="1"
            max="12"
            class="mt-2 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm transition placeholder:text-slate-400 focus:border-sky-400 focus:outline-none focus:ring-4 focus:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:placeholder:text-slate-500 dark:focus:border-sky-500 dark:focus:ring-sky-500/20"
          />
        </label>

        <label class="block">
          <span class="text-sm font-medium text-slate-700 dark:text-slate-200"
            >Period Start</span
          >
          <input
            v-model="createForm.pay_period_start"
            type="date"
            class="mt-2 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm transition focus:border-sky-400 focus:outline-none focus:ring-4 focus:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:[color-scheme:dark] dark:focus:border-sky-500 dark:focus:ring-sky-500/20"
          />
        </label>

        <label class="block">
          <span class="text-sm font-medium text-slate-700 dark:text-slate-200"
            >Period End</span
          >
          <input
            v-model="createForm.pay_period_end"
            type="date"
            class="mt-2 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm transition focus:border-sky-400 focus:outline-none focus:ring-4 focus:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:[color-scheme:dark] dark:focus:border-sky-500 dark:focus:ring-sky-500/20"
          />
        </label>

        <div class="hidden xl:block"></div>
      </div>

      <div class="mt-4 grid gap-4 lg:grid-cols-2">
        <label class="block">
          <span class="text-sm font-medium text-slate-700 dark:text-slate-200"
            >Payslip PDF (optional)</span
          >
          <input
            type="file"
            accept="application/pdf"
            class="mt-2 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm transition file:mr-3 file:rounded-lg file:border-0 file:bg-slate-100 file:px-3 file:py-2 file:text-sm file:font-medium file:text-slate-700 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:file:bg-slate-800 dark:file:text-slate-100"
            @change="(event) => setFiles(event, 'payslips_pdf')"
          />
        </label>

        <label class="block">
          <span class="text-sm font-medium text-slate-700 dark:text-slate-200"
            >Supporting PDF</span
          >
          <input
            type="file"
            accept="application/pdf"
            class="mt-2 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm transition file:mr-3 file:rounded-lg file:border-0 file:bg-slate-100 file:px-3 file:py-2 file:text-sm file:font-medium file:text-slate-700 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:file:bg-slate-800 dark:file:text-slate-100"
            @change="(event) => setFiles(event, 'supporting_document')"
          />
        </label>
      </div>

      <div
        class="mt-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between"
      >
        <div
          v-if="createError"
          class="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-300"
        >
          {{ createError }}
        </div>
        <div v-else class="text-sm text-slate-500 dark:text-slate-400">
          Neptune will create a draft run and process the PDF. Payslips are not
          emailed until you send them.
        </div>

        <button
          class="btn accent w-full sm:w-auto"
          :disabled="creating"
          @click.prevent="createRun"
        >
          {{ creating ? "Creating..." : "Create Draft Run" }}
        </button>
      </div>
    </section>

    <section class="space-y-4">
      <div
        class="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between"
      >
        <div>
          <h2 class="text-xl font-semibold text-slate-900 dark:text-white">
            Runs
          </h2>
          <p class="text-sm text-slate-500 dark:text-slate-400">
            Tap a run to review or send payslips.
          </p>
        </div>
        <div
          v-if="loadingRuns"
          class="text-sm text-slate-500 dark:text-slate-400"
        >
          Loading runs...
        </div>
      </div>

      <div
        v-if="!loadingRuns && sortedRuns.length === 0"
        class="box text-sm text-slate-500 dark:bg-slate-900/70 dark:text-slate-400"
      >
        No payroll runs yet.
      </div>

      <div v-else class="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        <button
          v-for="run in sortedRuns"
          :key="run.id"
          type="button"
          class="group rounded-3xl border bg-white p-5 text-left shadow-sm transition dark:bg-slate-900/80"
          :class="
            run.id === activeRunId
              ? 'border-slate-900 ring-2 ring-slate-900/10 dark:border-slate-200 dark:ring-slate-200/10'
              : 'border-slate-200 hover:border-slate-300 hover:shadow-md dark:border-slate-700 dark:hover:border-slate-500'
          "
          @click="loadRun(run.id)"
        >
          <div class="flex items-start justify-between gap-3">
            <div class="min-w-0">
              <div
                class="truncate text-lg font-semibold text-slate-900 dark:text-white"
              >
                {{ run.name }}
              </div>
              <div class="mt-1 text-sm text-slate-500 dark:text-slate-400">
                {{ periodLabel(run) }}
              </div>
            </div>
            <span
              class="rounded-full px-2.5 py-1 text-[11px] font-semibold capitalize"
              :class="statusTone(run.status)"
            >
              {{ run.status.replace("_", " ") }}
            </span>
          </div>

          <div class="mt-5 grid grid-cols-2 gap-3">
            <div class="rounded-2xl bg-slate-50 px-3 py-3 dark:bg-slate-800/80">
              <div
                class="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400"
              >
                Payslips
              </div>
              <div
                class="mt-1 text-xl font-semibold text-slate-900 dark:text-white"
              >
                {{ run.payslip_count }}
              </div>
            </div>
            <div class="rounded-2xl bg-slate-50 px-3 py-3 dark:bg-slate-800/80">
              <div
                class="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400"
              >
                Sent
              </div>
              <div
                class="mt-1 text-xl font-semibold text-slate-900 dark:text-white"
              >
                {{ run.sent_payslip_count }}
              </div>
            </div>
            <div class="rounded-2xl bg-slate-50 px-3 py-3 dark:bg-slate-800/80">
              <div
                class="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400"
              >
                Claims
              </div>
              <div
                class="mt-1 text-xl font-semibold text-slate-900 dark:text-white"
              >
                {{ run.claim_count || 0 }}
              </div>
            </div>
            <div class="rounded-2xl bg-slate-50 px-3 py-3 dark:bg-slate-800/80">
              <div
                class="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400"
              >
                Review
              </div>
              <div
                class="mt-1 text-xl font-semibold text-slate-900 dark:text-white"
              >
                {{ run.review_claim_count || 0 }}
              </div>
            </div>
          </div>
        </button>
      </div>
    </section>

    <section
      class="box border border-slate-200 dark:border-slate-700 dark:bg-slate-900/70"
    >
      <div v-if="loadingRun && !activeRun" class="text-sm text-slate-500 dark:text-slate-400">
        Loading payroll run...
      </div>
      <div
        v-else-if="loadingError"
        class="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-300"
      >
        {{ loadingError }}
      </div>
      <div
        v-else-if="!activeRun"
        class="text-sm text-slate-500 dark:text-slate-400"
      >
        Select a payroll run to review extracted payslips.
      </div>
      <div v-else class="space-y-6">
        <div
          class="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between"
        >
          <div class="space-y-2">
            <div class="flex flex-wrap items-center gap-2">
              <h2 class="text-2xl font-semibold text-slate-900 dark:text-white">
                {{ activeRun.name }}
              </h2>
              <span
                class="rounded-full px-2.5 py-1 text-[11px] font-semibold capitalize"
                :class="statusTone(activeRun.status)"
              >
                {{ activeRun.status.replace("_", " ") }}
              </span>
            </div>
            <div
              class="flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-500 dark:text-slate-400"
            >
              <span>{{ periodLabel(activeRun) }}</span>
              <span v-if="activeRun.tax_month"
                >HMRC month {{ activeRun.tax_month }}</span
              >
              <span>{{ activeRun.payslips_source_name }}</span>
              <span v-if="activeRun.sent_at"
                >Completed {{ formatDate(activeRun.sent_at) }}</span
              >
            </div>
          </div>

          <div class="w-full max-w-xl space-y-3 xl:w-[24rem]">
            <details
              class="rounded-2xl border border-slate-200 bg-slate-50/80 p-4 dark:border-slate-700 dark:bg-slate-950/40"
            >
              <summary
                class="flex cursor-pointer list-none items-center justify-between gap-3 [&::-webkit-details-marker]:hidden"
              >
                <div>
                  <div
                    class="text-sm font-semibold text-slate-900 dark:text-white"
                  >
                    Optional note
                  </div>
                  <div class="text-xs text-slate-500 dark:text-slate-400">
                    Add a short message to the payslip email when needed.
                  </div>
                </div>
                <div
                  class="text-xs font-medium text-slate-500 dark:text-slate-400"
                >
                  {{ sendMessage ? "Included in next send" : "Expand" }}
                </div>
              </summary>

              <div class="mt-4 space-y-3">
                <textarea
                  v-model="sendMessage"
                  rows="4"
                  maxlength="2000"
                  class="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm transition placeholder:text-slate-400 focus:border-sky-400 focus:outline-none focus:ring-4 focus:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:placeholder:text-slate-500 dark:focus:border-sky-500 dark:focus:ring-sky-500/20"
                  placeholder="Optional note for this payroll email"
                ></textarea>
                <div
                  class="flex items-center justify-between gap-3 text-xs text-slate-500 dark:text-slate-400"
                >
                  <span
                    >Shown in the email body above the finance contact
                    note.</span
                  >
                  <span>{{ sendMessage.length }}/2000</span>
                </div>
              </div>
            </details>

            <div class="flex flex-col gap-2 sm:flex-row">
              <button
                v-if="activeRun.status === 'draft'"
                class="btn border-rose-200 text-rose-700 hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-rose-900 dark:text-rose-300 dark:hover:bg-rose-950/30"
                :disabled="closingRun"
                @click.prevent="closePayPeriod"
              >
                {{ closingRun ? "Closing…" : "Close Pay Period" }}
              </button>
              <a
                v-if="activeRun.supporting_document_download_url"
                class="btn"
                :href="activeRun.supporting_document_download_url"
              >
                Supporting PDF
              </a>
              <button
                class="btn accent"
                :disabled="sendingAll || !canSendAll"
                @click.prevent="sendAll"
              >
                {{ sendingAll ? "Sending..." : "Send All Payslips" }}
              </button>
            </div>

            <p
              v-if="!canSendAll"
              class="text-xs text-amber-700 dark:text-amber-300"
            >
              Add missing staff matches or staff email addresses before sending
              the full run.
            </p>
          </div>
        </div>

        <div
          v-if="activeRun.status === 'draft'"
          class="space-y-5 rounded-3xl border border-sky-200 bg-sky-50/70 p-5 dark:border-sky-900/50 dark:bg-sky-950/20"
        >
          <div
            v-if="Number(activeRun.payslip_count || 0) === 0"
            class="rounded-2xl border border-sky-200 bg-white/80 p-4 dark:border-sky-900/60 dark:bg-slate-950/50"
          >
            <div
              class="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between"
            >
              <div class="max-w-2xl">
                <h3
                  class="text-base font-semibold text-slate-900 dark:text-white"
                >
                  Upload complete payslip PDF
                </h3>
                <p class="mt-1 text-sm text-slate-600 dark:text-slate-400">
                  Upload the accountant's combined PDF containing all payslips
                  for this run. Neptune will split, process and match every
                  payslip in the file.
                </p>
              </div>

              <button
                class="btn accent w-full lg:w-auto"
                :disabled="uploadingCompletePayslips || !completePayslipFile"
                @click.prevent="uploadCompletePayslips"
              >
                {{
                  uploadingCompletePayslips
                    ? "Importing..."
                    : "Import Complete PDF"
                }}
              </button>
            </div>

            <input
              ref="completePayslipInput"
              type="file"
              accept="application/pdf"
              class="mt-4 w-full rounded-xl border border-sky-200 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm transition file:mr-3 file:rounded-lg file:border-0 file:bg-sky-100 file:px-3 file:py-2 file:text-sm file:font-medium file:text-sky-800 dark:border-sky-900/60 dark:bg-slate-950 dark:text-slate-100 dark:file:bg-sky-900/50 dark:file:text-sky-200"
              @change="setCompletePayslipFile"
            />

            <div
              v-if="completePayslipError"
              class="mt-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-300"
            >
              {{ completePayslipError }}
            </div>
            <p v-else class="mt-3 text-xs text-slate-500 dark:text-slate-400">
              This option is available before individual payslips have been
              added, preventing accidental replacement of existing slips.
            </p>
          </div>

          <div
            class="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between"
          >
            <div class="max-w-2xl">
              <h3 class="text-base font-semibold text-slate-900 dark:text-white">
                Add individual payslips
              </h3>
              <p class="mt-1 text-sm text-slate-600 dark:text-slate-400">
                Upload one or more single-payslip PDFs issued after the main
                payroll file. They will be processed and matched without
                replacing the payslips already in this run.
              </p>
            </div>

            <button
              class="btn accent w-full lg:w-auto"
              :disabled="addingPayslips || additionalPayslipFiles.length === 0"
              @click.prevent="addIndividualPayslips"
            >
              {{
                addingPayslips
                  ? "Adding..."
                  : additionalPayslipFiles.length > 0
                    ? `Add ${additionalPayslipFiles.length} Payslip${
                        additionalPayslipFiles.length === 1 ? "" : "s"
                      }`
                    : "Add Payslips"
              }}
            </button>
          </div>

          <input
            ref="additionalPayslipInput"
            type="file"
            accept="application/pdf"
            multiple
            class="mt-4 w-full rounded-xl border border-sky-200 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm transition file:mr-3 file:rounded-lg file:border-0 file:bg-sky-100 file:px-3 file:py-2 file:text-sm file:font-medium file:text-sky-800 dark:border-sky-900/60 dark:bg-slate-950 dark:text-slate-100 dark:file:bg-sky-900/50 dark:file:text-sky-200"
            @change="setAdditionalPayslipFiles"
          />

          <div
            v-if="additionalPayslipsError"
            class="mt-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-300"
          >
            {{ additionalPayslipsError }}
          </div>
          <p v-else class="mt-3 text-xs text-slate-500 dark:text-slate-400">
            Each selected PDF must contain exactly one payslip. Nothing is sent
            until you use the delivery actions below.
          </p>
        </div>

        <div class="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <div
            class="rounded-3xl border border-slate-200 bg-slate-50 px-5 py-4 dark:border-slate-800 dark:bg-slate-950/80"
          >
            <div
              class="text-[11px] font-medium uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400"
            >
              Prepared
            </div>
            <div
              class="mt-2 text-3xl font-semibold text-slate-900 dark:text-white"
            >
              {{ activeRun.payslip_count }}
            </div>
          </div>
          <div
            class="rounded-3xl border border-slate-200 bg-slate-50 px-5 py-4 dark:border-slate-800 dark:bg-slate-950/80"
          >
            <div
              class="text-[11px] font-medium uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400"
            >
              Sent
            </div>
            <div
              class="mt-2 text-3xl font-semibold text-slate-900 dark:text-white"
            >
              {{ sentCount }}
            </div>
          </div>
          <div
            class="rounded-3xl border border-slate-200 bg-slate-50 px-5 py-4 dark:border-slate-800 dark:bg-slate-950/80"
          >
            <div
              class="text-[11px] font-medium uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400"
            >
              Ready
            </div>
            <div
              class="mt-2 text-3xl font-semibold text-slate-900 dark:text-white"
            >
              {{ readyCount }}
            </div>
          </div>
          <div
            class="rounded-3xl border border-slate-200 bg-slate-50 px-5 py-4 dark:border-slate-800 dark:bg-slate-950/80"
          >
            <div
              class="text-[11px] font-medium uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400"
            >
              Needs Review
            </div>
            <div
              class="mt-2 text-3xl font-semibold text-slate-900 dark:text-white"
            >
              {{ unresolvedCount + errorCount }}
            </div>
          </div>
        </div>

        <div
          v-if="activeRun && !accountantPreflight.exportable"
          class="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/20 dark:text-amber-100"
        >
          <span>
            <strong>Accountant XLSX:</strong>
            {{ accountantPreflight.errors[0] || "Resolve the remaining payroll checks before export." }}
          </span>
          <button type="button" class="font-semibold underline" @click.prevent="exportBlockersOpen = true">
            Review blockers
          </button>
        </div>

        <section
          class="rounded-3xl border border-amber-200 bg-amber-50/60 p-4 dark:border-amber-500/30 dark:bg-amber-500/10"
        >
          <div
            class="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between"
          >
            <div>
              <h3 class="text-lg font-semibold text-slate-900 dark:text-white">
                Review Queue
              </h3>
              <p class="mt-1 text-sm text-slate-600 dark:text-slate-300">
                Resolve missing staff matches, missing delivery emails, and
                send errors before distributing the full run.
              </p>
            </div>
            <div
              class="rounded-2xl bg-white px-4 py-3 text-sm font-semibold text-slate-900 ring-1 ring-amber-200 dark:bg-slate-900 dark:text-white dark:ring-amber-500/30"
            >
              {{ reviewPayslips.length }} to review
            </div>
          </div>

          <div
            v-if="reviewPayslips.length === 0"
            class="mt-4 rounded-2xl bg-white px-4 py-3 text-sm text-emerald-700 ring-1 ring-emerald-200 dark:bg-slate-900 dark:text-emerald-300 dark:ring-emerald-500/30"
          >
            Every payslip has a recipient and is ready for the delivery step.
          </div>

          <div v-else class="mt-4 grid gap-3 xl:grid-cols-2">
            <article
              v-for="payslip in reviewPayslips"
              :key="`review-${payslip.id}`"
              class="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900"
            >
              <div class="flex items-start justify-between gap-3">
                <div class="min-w-0">
                  <div class="font-semibold text-slate-900 dark:text-white">
                    {{ payslip.employee_name }}
                  </div>
                  <div class="mt-1 text-xs text-slate-500 dark:text-slate-400">
                    {{ payslip.pay_period_label || "Payslip" }}
                    <span v-if="payslip.works_number">
                      · Works #{{ payslip.works_number }}</span
                    >
                  </div>
                </div>
                <button
                  type="button"
                  class="btn inline-flex h-9 items-center px-3 text-xs"
                  :disabled="viewingIds.has(payslip.id)"
                  @click.prevent="openPayslip(payslip)"
                >
                  {{ viewingIds.has(payslip.id) ? "Opening..." : "View" }}
                </button>
              </div>

              <div
                class="mt-3 rounded-xl bg-slate-50 px-3 py-2 text-sm dark:bg-slate-950/70"
              >
                <div class="font-medium text-slate-900 dark:text-white">
                  {{ payslip.staff ? payslip.staff.name : "No staff match" }}
                </div>
                <div class="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  {{ matchLabel(payslip) }}
                </div>
              </div>

              <div class="mt-3 grid gap-2 sm:grid-cols-[1fr_auto]">
                <input
                  v-if="staffSearch.payslipId === payslip.id"
                  v-model="staffSearch.query"
                  type="search"
                  class="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-sky-400 focus:outline-none focus:ring-4 focus:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:border-sky-500 dark:focus:ring-sky-500/20"
                  placeholder="Search staff"
                  @keyup.enter="searchStaff"
                />
                <button
                  v-if="staffSearch.payslipId === payslip.id"
                  type="button"
                  class="btn"
                  :disabled="staffSearch.loading"
                  @click.prevent="searchStaff"
                >
                  {{ staffSearch.loading ? "Searching..." : "Search" }}
                </button>
                <button
                  v-else
                  type="button"
                  class="btn sm:col-span-2"
                  @click.prevent="beginStaffMatch(payslip)"
                >
                  Match Staff
                </button>
              </div>

              <div
                v-if="staffSearch.payslipId === payslip.id"
                class="mt-2 space-y-2"
              >
                <div
                  v-if="staffSearch.error"
                  class="text-xs text-amber-700 dark:text-amber-300"
                >
                  {{ staffSearch.error }}
                </div>
                <button
                  v-for="staff in staffSearch.results"
                  :key="staff.id"
                  type="button"
                  class="flex w-full items-center justify-between gap-3 rounded-xl border border-slate-200 px-3 py-2 text-left text-sm transition hover:border-slate-400 dark:border-slate-700 dark:hover:border-slate-500"
                  :disabled="savingPayslipIds.has(payslip.id)"
                  @click.prevent="setPayslipStaff(payslip, staff)"
                >
                  <span>
                    <span class="block font-medium text-slate-900 dark:text-white">
                      {{ staffDisplayName(staff) }}
                    </span>
                    <span class="block text-xs text-slate-500 dark:text-slate-400">
                      {{ staffDisplayEmail(staff) || "No email" }}
                    </span>
                  </span>
                  <span class="text-xs text-slate-500 dark:text-slate-400">
                    Select
                  </span>
                </button>
                <button
                  v-if="payslip.staff"
                  type="button"
                  class="text-xs font-medium text-rose-600 hover:underline dark:text-rose-300"
                  :disabled="savingPayslipIds.has(payslip.id)"
                  @click.prevent="clearPayslipStaff(payslip)"
                >
                  Clear current staff match
                </button>
              </div>

              <div class="mt-3 grid gap-2 sm:grid-cols-[1fr_auto]">
                <input
                  v-model="emailOverrides[payslip.id]"
                  type="email"
                  class="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-sky-400 focus:outline-none focus:ring-4 focus:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:border-sky-500 dark:focus:ring-sky-500/20"
                  :placeholder="payslip.staff?.email || 'Delivery email override'"
                />
                <button
                  type="button"
                  class="btn"
                  :disabled="savingPayslipIds.has(payslip.id)"
                  @click.prevent="saveEmailOverride(payslip)"
                >
                  Save Email
                </button>
              </div>

              <div
                v-if="payslip.last_error"
                class="mt-3 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700 dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-300"
              >
                {{ payslip.last_error }}
              </div>
            </article>
          </div>
        </section>

        <div class="space-y-4 lg:hidden">
          <article
            v-for="payslip in activeRun.payslips"
            :key="payslip.id"
            class="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-900/80"
          >
            <div class="flex items-start justify-between gap-3">
              <div class="min-w-0">
                <div
                  class="text-base font-semibold text-slate-900 dark:text-white"
                >
                  {{ payslip.employee_name }}
                </div>
                <div class="mt-1 text-sm text-slate-500 dark:text-slate-400">
                  {{ payslip.pay_period_label || "Payslip" }}
                  <span v-if="payslip.works_number">
                    · Works #{{ payslip.works_number }}</span
                  >
                </div>
              </div>
              <span
                class="rounded-full px-2.5 py-1 text-[11px] font-semibold capitalize"
                :class="statusTone(payslip.status)"
              >
                {{ payslip.status.replace("_", " ") }}
              </span>
            </div>

            <div class="mt-4 grid grid-cols-2 gap-3">
              <div
                class="rounded-2xl bg-slate-50 px-3 py-3 dark:bg-slate-800/80"
              >
                <div
                  class="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400"
                >
                  Gross
                </div>
                <div class="mt-1 font-semibold text-slate-900 dark:text-white">
                  {{ formatMoney(payslip.gross_amount_formatted) }}
                </div>
              </div>
              <div
                class="rounded-2xl bg-slate-50 px-3 py-3 dark:bg-slate-800/80"
              >
                <div
                  class="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400"
                >
                  Net
                </div>
                <div class="mt-1 font-semibold text-slate-900 dark:text-white">
                  {{ formatMoney(payslip.net_amount_formatted) }}
                </div>
              </div>
            </div>

            <div
              class="mt-4 rounded-2xl border border-slate-200 px-4 py-3 dark:border-slate-700 dark:bg-slate-950/40"
            >
              <div
                class="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400"
              >
                Matched Staff
              </div>
              <router-link
                v-if="payslip.staff"
                :to="{ name: 'staff-view', params: { id: payslip.staff.id } }"
                class="mt-1 inline-flex text-left font-medium text-slate-900 transition hover:text-slate-600 hover:underline dark:text-white dark:hover:text-slate-200"
              >
                {{ payslip.staff.name }}
              </router-link>
              <div
                v-else
                class="mt-1 font-medium text-slate-900 dark:text-white"
              >
                No staff match
              </div>
              <div class="mt-1 text-sm text-slate-500 dark:text-slate-400">
                {{ matchLabel(payslip) }}
              </div>
            </div>

            <div
              class="mt-4 rounded-2xl border border-slate-200 px-4 py-3 dark:border-slate-700 dark:bg-slate-950/40"
            >
              <div
                class="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400"
              >
                Delivery Email
              </div>
              <div class="mt-1 font-medium text-slate-900 dark:text-white">
                {{ recipientLabel(payslip) }}
              </div>
              <div class="mt-1 text-sm text-slate-500 dark:text-slate-400">
                {{ recipientHelp(payslip) }}
              </div>
              <div
                class="mt-3 flex items-center justify-between gap-3 text-xs text-slate-500 dark:text-slate-400"
              >
                <span>{{ auditSummary(payslip) }}</span>
                <button
                  type="button"
                  :class="auditLinkClass"
                  @click.prevent="openAudit(payslip)"
                >
                  Audit
                </button>
              </div>
            </div>

            <div
              v-if="payslip.last_error"
              class="mt-3 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700 dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-300"
            >
              {{ payslip.last_error }}
            </div>

            <div
              v-if="payslip.sent_at"
              class="mt-3 text-xs text-slate-500 dark:text-slate-400"
            >
              Sent {{ formatDate(payslip.sent_at) }}
            </div>

            <div class="mt-4 grid grid-cols-3 gap-2">
              <button
                type="button"
                :class="actionButtonClass"
                :disabled="viewingIds.has(payslip.id)"
                @click.prevent="openPayslip(payslip)"
              >
                {{ viewingIds.has(payslip.id) ? "Opening..." : "View" }}
              </button>
              <a :class="actionButtonClass" :href="payslip.download_url">
                Download
              </a>
              <button
                :class="`${actionButtonClass} accent`"
                :disabled="
                  sendingIds.has(payslip.id) || !payslip.recipient_email
                "
                @click.prevent="sendSingle(payslip)"
              >
                {{ sendButtonLabel(payslip) }}
              </button>
            </div>
          </article>
        </div>

        <div
          class="hidden overflow-hidden rounded-3xl border border-slate-200 dark:border-slate-700 lg:block"
        >
          <table class="w-full table-fixed text-sm">
            <thead class="bg-slate-50 dark:bg-slate-900/80">
              <tr
                class="text-left text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400"
              >
                <th class="w-[34%] px-4 py-3">Employee</th>
                <th class="w-[30%] px-4 py-3">Delivery</th>
                <th class="w-[16%] px-4 py-3">Pay</th>
                <th class="w-[20%] px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody class="bg-white dark:bg-slate-900/60">
              <tr
                v-for="payslip in activeRun.payslips"
                :key="payslip.id"
                class="border-t border-slate-100 align-top odd:bg-white even:bg-slate-50/70 hover:bg-slate-100/80 dark:border-slate-800 dark:odd:bg-slate-900/60 dark:even:bg-slate-950/80 dark:hover:bg-slate-800/80"
              >
                <td class="px-4 py-3">
                  <div class="font-semibold text-slate-900 dark:text-white">
                    {{ payslip.employee_name }}
                  </div>
                  <div class="mt-1 text-xs text-slate-500 dark:text-slate-400">
                    {{ payslip.pay_period_label || "Payslip" }}
                    <span v-if="payslip.works_number">
                      · Works #{{ payslip.works_number }}</span
                    >
                  </div>
                  <div class="mt-1 text-xs text-slate-500 dark:text-slate-400">
                    Staff
                    <router-link
                      v-if="payslip.staff"
                      :to="{
                        name: 'staff-view',
                        params: { id: payslip.staff.id },
                      }"
                      class="ml-1 font-medium text-slate-700 transition hover:text-slate-900 hover:underline dark:text-slate-200 dark:hover:text-white"
                    >
                      {{ payslip.staff.name }}
                    </router-link>
                    <span v-else class="ml-1 font-medium text-slate-700 dark:text-slate-200">
                      No match
                    </span>
                  </div>
                  <div class="mt-1 text-xs text-slate-500 dark:text-slate-400">
                    {{ matchLabel(payslip) }}
                  </div>
                  <div class="mt-2 flex flex-wrap items-center gap-2">
                    <span
                      class="rounded-full px-2.5 py-1 text-[11px] font-semibold capitalize"
                      :class="statusTone(payslip.status)"
                    >
                      {{ payslip.status.replace("_", " ") }}
                    </span>
                    <span
                      v-if="payslip.sent_at"
                      class="text-xs text-slate-500 dark:text-slate-400"
                    >
                      Sent {{ formatDate(payslip.sent_at) }}
                    </span>
                  </div>
                </td>
                <td class="px-4 py-3">
                  <div
                    class="break-all font-medium text-slate-900 dark:text-white"
                  >
                    {{ recipientLabel(payslip) }}
                  </div>
                  <div
                    class="mt-1 line-clamp-2 text-xs text-slate-500 dark:text-slate-400"
                  >
                    {{ recipientHelp(payslip) }}
                  </div>
                  <div
                    class="mt-3 flex items-center justify-between gap-3 text-xs text-slate-500 dark:text-slate-400"
                  >
                    <span>{{ auditSummary(payslip) }}</span>
                    <button
                      type="button"
                      :class="auditLinkClass"
                      @click.prevent="openAudit(payslip)"
                    >
                      Audit
                    </button>
                  </div>
                  <div
                    v-if="payslip.last_error"
                    class="mt-2 text-xs text-rose-600 dark:text-rose-300"
                  >
                    {{ payslip.last_error }}
                  </div>
                </td>
                <td class="px-4 py-3">
                  <div class="text-xs text-slate-500 dark:text-slate-400">
                    Gross
                  </div>
                  <div class="font-medium text-slate-900 dark:text-white">
                    {{ formatMoney(payslip.gross_amount_formatted) }}
                  </div>
                  <div class="mt-2 text-xs text-slate-500 dark:text-slate-400">
                    Net
                  </div>
                  <div class="font-medium text-slate-900 dark:text-white">
                    {{ formatMoney(payslip.net_amount_formatted) }}
                  </div>
                </td>
                <td class="px-4 py-3">
                  <div
                    class="ml-auto grid w-full max-w-[15.5rem] grid-cols-[4.5rem_5.5rem_4.5rem] justify-end gap-2"
                  >
                    <button
                      type="button"
                      :class="desktopIconButtonClass"
                      :disabled="viewingIds.has(payslip.id)"
                      title="View payslip"
                      @click.prevent="openPayslip(payslip)"
                    >
                      {{ viewingIds.has(payslip.id) ? "..." : "View" }}
                    </button>
                    <a
                      :class="desktopIconButtonClass"
                      :href="payslip.download_url"
                      title="Download payslip"
                    >
                      Download
                    </a>
                    <button
                      :class="desktopIconButtonClass"
                      :disabled="
                        sendingIds.has(payslip.id) || !payslip.recipient_email
                      "
                      :title="
                        payslip.recipient_email
                          ? 'Resend payslip'
                          : 'No staff email'
                      "
                      @click.prevent="sendSingle(payslip)"
                    >
                      {{ sendingIds.has(payslip.id) ? "..." : "Resend" }}
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </section>
    </template>

    <template v-else-if="workspace === 'timesheets'">
      <section
        class="box border border-slate-200 dark:border-slate-700 dark:bg-slate-900/70"
      >
        <div
          class="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between"
        >
          <div>
            <h2 class="text-2xl font-semibold text-slate-900 dark:text-white">
              Timesheet Imports
            </h2>
          </div>
          <div class="flex flex-wrap items-center justify-end gap-2">
            <button
              v-if="activeRun"
              type="button"
              class="btn accent"
              :disabled="uploadingTimesheets || activeRun.status !== 'draft'"
              :title="activeRun.status === 'draft' ? 'Select and import payroll timesheets' : 'Timesheets can only be imported into a draft payroll run'"
              @click.prevent="importTimesheetAction"
            >
              {{
                uploadingTimesheets
                  ? "Importing..."
                  : timesheetFiles.length
                    ? `Import ${timesheetFiles.length} Selected`
                    : "＋ Import Timesheets"
              }}
            </button>
            <button
              v-if="activeRun"
              type="button"
              class="btn"
              :disabled="refreshingClaims"
              title="Rebuild the summary totals from imported timesheets"
              @click.prevent="prepareAccountantSummary"
            >
              {{ refreshingClaims ? "↻ Updating..." : "↻ Update Summary" }}
            </button>
            <button
              v-if="activeRun?.accountant_export_download_url && accountantPreflight.exportable"
              type="button"
              class="btn accent"
              :disabled="downloadingAccountantWorkbook"
              title="Download the accountant Totals workbook"
              @click.prevent="downloadAccountantWorkbook()"
            >
              {{ downloadingAccountantWorkbook ? "Preparing XLSX..." : "⇩ Accountant XLSX" }}
            </button>
            <template v-else-if="activeRun">
              <button
                type="button"
                class="btn"
                :title="accountantPreflight.errors.join('\n')"
                @click.prevent="exportBlockersOpen = true"
              >
                ⚠ XLSX blocked ({{ accountantPreflight.blocking_count }})
              </button>
              <button
                type="button"
                class="btn border-amber-400 text-amber-800 dark:border-amber-600 dark:text-amber-200"
                :disabled="downloadingAccountantWorkbook"
                title="Export approved values with a prominent draft warning and an issues sheet"
                @click.prevent="downloadAccountantWorkbook(true)"
              >
                {{ downloadingAccountantWorkbook ? "Preparing..." : "⚠ Export Draft XLSX" }}
              </button>
            </template>
            <select
              v-model="importStatusFilter"
              class="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-sky-400 focus:outline-none focus:ring-4 focus:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:border-sky-500 dark:focus:ring-sky-500/20"
            >
              <option value="all">All imports</option>
              <option value="needs_review">Needs review</option>
              <option value="imported">Imported</option>
              <option value="excluded">Excluded</option>
            </select>
            <button
              class="btn"
              :disabled="loadingImports"
              title="Refresh timesheet data without changing the current row order"
              @click.prevent="loadTimesheetImports"
            >
              {{ loadingImports ? "Refreshing..." : "↻ Refresh" }}
            </button>
          </div>
        </div>

        <div
          v-if="activeRun && !accountantPreflight.exportable"
          class="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/20 dark:text-amber-100"
        >
          <span>
            <strong>Accountant XLSX:</strong>
            {{ accountantPreflight.errors[0] || "Resolve the remaining payroll checks before export." }}
          </span>
          <button type="button" class="font-semibold underline" @click.prevent="exportBlockersOpen = true">
            Review blockers
          </button>
        </div>

        <section
          v-if="activeRun"
          class="mt-2 border-b border-slate-200 pb-1 dark:border-slate-700"
        >
          <div class="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div class="max-w-3xl">
              <div class="flex flex-wrap items-center gap-2">
                <h3 class="text-sm font-semibold text-slate-900 dark:text-white">Timesheet files</h3>
                <span class="rounded-full bg-sky-100 px-2 py-0.5 text-[11px] font-semibold text-sky-800 dark:bg-sky-900/50 dark:text-sky-200">
                  {{ activeRun.timesheet_documents?.length || 0 }} uploaded
                </span>
                <span v-if="pendingTimesheetDocuments().length" class="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800 dark:bg-amber-900/50 dark:text-amber-200">
                  {{ pendingTimesheetDocuments().length }} processing in background · safe to leave this page
                </span>
              </div>
            </div>
            <div class="flex flex-wrap items-center gap-2">
              <button v-if="activeRun.timesheet_documents?.length" type="button" class="btn !py-1 !text-xs" :aria-expanded="evidenceExpanded" @click.prevent="evidenceExpanded = !evidenceExpanded">
                {{ evidenceExpanded ? "▴ Hide files" : `▾ Show ${activeRun.timesheet_documents.length} files` }}
              </button>
              <button v-if="timesheetFiles.length" type="button" class="btn accent" :disabled="uploadingTimesheets" @click.prevent="uploadTimesheets">
                {{ uploadingTimesheets ? "Importing..." : timesheetFiles.length ? `Import ${timesheetFiles.length}` : "Import Timesheets" }}
              </button>
            </div>
          </div>
          <input
            ref="timesheetInput"
            type="file"
            accept=".pdf,.docx,.xlsx,.xlsm,.ods,.jpg,.jpeg,.png,.tif,.tiff"
            multiple
            class="sr-only"
            @change="setTimesheetFiles"
          />
          <div
            v-if="timesheetUploadError"
            class="mt-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-300"
          >
            {{ timesheetUploadError }}
          </div>

          <div
            v-if="evidenceExpanded && activeRun.timesheet_documents?.length"
            class="mt-3 grid gap-2 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4"
          >
            <article
              v-for="document in activeRun.timesheet_documents"
              :key="document.id"
              :id="`payroll-document-${document.id}`"
              tabindex="-1"
              class="rounded-xl border border-sky-200 bg-white p-2.5 dark:border-sky-900/50 dark:bg-slate-950/80"
            >
              <div class="flex items-start justify-between gap-3">
                <div class="min-w-0">
                  <div class="truncate text-sm font-semibold text-slate-900 dark:text-white">
                    {{ document.name }}
                  </div>
                  <div class="mt-1 text-xs text-slate-500 dark:text-slate-400">
                    {{ document.extractor_model || "Deterministic parser" }}
                    <span v-if="document.processed_at"> · {{ formatDateTime(document.processed_at) }}</span>
                  </div>
                  <div v-if="document.last_error" class="mt-2 text-xs text-rose-700 dark:text-rose-300">
                    {{ document.last_error }}
                  </div>
                </div>
                <span
                  class="rounded-full px-2.5 py-1 text-[11px] font-semibold capitalize"
                  :class="statusTone(document.status)"
                >
                  {{ document.status.replace("_", " ") }}
                </span>
              </div>
              <div class="mt-2 flex flex-wrap gap-1.5">
                <a class="btn !px-2 !py-1 !text-xs" :href="document.view_url" target="_blank" rel="noopener" title="View source">
                  ↗ View
                </a>
                <button
                  v-if="['failed', 'needs_review'].includes(document.status)"
                  type="button"
                  class="btn !px-2 !py-1 !text-xs"
                  :disabled="reprocessingDocumentIds.has(document.id) || ['queued', 'processing'].includes(document.status)"
                  @click.prevent="reprocessTimesheet(document)"
                >
                  {{ reprocessingDocumentIds.has(document.id) ? "Queuing..." : "↻ Reprocess" }}
                </button>
                <button
                  v-if="!['queued', 'processing'].includes(document.status)"
                  type="button"
                  class="btn !px-2 !py-1 !text-xs"
                  :disabled="reprocessingDocumentIds.has(document.id) || activeRun?.status !== 'draft'"
                  title="Extract and re-read every field from this document with the configured best AI using maximum reasoning and Pro mode. This can take several minutes."
                  @click.prevent="reprocessTimesheet(document, { verifyWithBestAi: true })"
                >
                  {{ reprocessingDocumentIds.has(document.id) ? "Queuing..." : "Extract with best AI" }}
                </button>
                <button
                  v-if="document.status !== 'processing'"
                  type="button"
                  class="btn !px-2 !py-1 !text-xs border-rose-300 text-rose-700 hover:bg-rose-50 dark:border-rose-800 dark:text-rose-300 dark:hover:bg-rose-950/40"
                  :disabled="deletingTimesheetDocumentIds.has(document.id) || activeRun?.status !== 'draft'"
                  title="Delete this source timesheet and all staged entries so it can be uploaded again"
                  @click.prevent="deleteTimesheetDocument(document)"
                >
                  {{ deletingTimesheetDocumentIds.has(document.id) ? "Deleting..." : "⌫ Delete" }}
                </button>
              </div>
            </article>
          </div>
          <div
            v-if="pendingTimesheetDocuments().length"
            class="mt-3 flex items-center gap-2 text-xs font-medium text-sky-800 dark:text-sky-200"
          >
            <span class="inline-block h-2 w-2 animate-pulse rounded-full bg-sky-500"></span>
            Processing {{ pendingTimesheetDocuments().length }} timesheet{{ pendingTimesheetDocuments().length === 1 ? "" : "s" }} — this page updates automatically.
          </div>
        </section>

        <div class="mt-1 flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 py-1 dark:border-slate-800">
          <div class="flex flex-wrap items-center gap-1" role="group" aria-label="Filter timesheets by review readiness">
            <button
              v-for="filter in [{ key: 'all', label: 'All' }, { key: 'needs_review', label: 'Needs review' }, { key: 'ready', label: 'Ready to import' }, { key: 'broken', label: 'Broken' }, { key: 'duplicates', label: 'Duplicate submissions' }]"
              :key="filter.key"
              type="button"
              class="rounded-lg px-3 py-1.5 text-xs font-semibold transition"
              :class="timesheetReadinessFilter === filter.key ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-900' : 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800'"
              @click.prevent="clearTimesheetFocus(); timesheetReadinessFilter = filter.key; if (filter.key === 'broken') importStatusFilter = 'all'"
            >
              {{ filter.label }} <span class="ml-1 opacity-70">{{ reviewFilterCounts[filter.key] }}</span>
            </button>
          </div>
          <div class="flex items-center gap-3 text-[11px] text-slate-500 dark:text-slate-400">
            <span>{{ importEntryRowsCount }} rows</span>
            <span>{{ timesheetImportSummary.imported }} imported</span>
            <span class="inline-flex items-center gap-1"><span class="h-2 w-2 rounded-full bg-emerald-500"></span> Claim created</span>
            <span class="inline-flex items-center gap-1"><span class="h-2 w-2 rounded-full bg-sky-500"></span> Ready to import</span>
            <span class="inline-flex items-center gap-1" title="Unimported rows autosave. Imported rows require Save correction."><span class="h-2 w-2 animate-pulse rounded-full bg-emerald-500"></span> Drafts autosave · corrections require Save</span>
          </div>
        </div>

        <div v-if="timesheetReadinessFilter === 'broken'" class="mt-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200">
          <p>These completed imports have one or more shifts absent from Claim Approval for this payroll run. Check staff, work dates and job references, then use Save correction. If the data is already correct, Rebuild Claims may bring an existing claim into this run.</p>
          <button type="button" class="mt-2 font-semibold underline" @click.prevent="workspace = 'unclaimed'">Compare with Missing Claims</button>
          <p v-if="!loadingRun && !loadingImports && reviewFilterCounts.broken === 0" class="mt-2 font-semibold">No broken imports in the loaded imports.</p>
        </div>
        <div v-if="timesheetReadinessFilter === 'duplicates'" class="mt-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200">
          Possible duplicate submissions in this payroll run. Compare the original files before taking action: matching shifts can be legitimate. Excluded submissions are omitted. Nothing is automatically deleted or merged.
          <p v-if="!loadingImports && !reviewFilterCounts.duplicates" class="mt-2 font-semibold">No duplicate submissions detected.</p>
          <p v-if="reviewFilterCounts.duplicates > 250" class="mt-2">Showing the first 250 flagged submissions of {{ reviewFilterCounts.duplicates }}.</p>
        </div>
        <div
          v-if="loadingImports && stagedImportRecords.length === 0"
          class="mt-6 rounded-2xl border border-dashed border-slate-300 px-4 py-8 text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400"
        >
          Loading timesheet imports...
        </div>

        <div
          v-else-if="!loadingImports && stagedImportRecords.length === 0"
          class="mt-6 rounded-2xl border border-dashed border-slate-300 px-4 py-8 text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400"
        >
          No timesheet imports matched this view.
        </div>

        <TimesheetReviewWorkspace
          v-else-if="!advancedTimesheetTable"
          :groups="allTimesheetSpreadsheetGroups"
          :readiness-filter="timesheetReadinessFilter"
          :run="activeRun"
          :products="availableProducts"
          :actions="reviewActions"
          :save-states="importSaveState"
          :saving-ids="savingImportIds"
          :initial-id="focusedTimesheetImportId"
          :refreshing-matches="refreshingMatches"
          :metrics="timesheetImportSummary.matching_metrics"
        />
        <div
          v-else
          class="mt-3 overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-950/80"
        >
          <table class="w-full min-w-[2048px] table-fixed border-collapse text-xs">
            <thead class="sticky top-0 z-20 bg-slate-100 text-left text-[10px] font-semibold uppercase tracking-wide text-slate-600 dark:bg-slate-900 dark:text-slate-300">
              <tr>
                <th class="w-12 border-b border-r border-slate-200 px-1 py-2 text-center dark:border-slate-700">State</th>
                <th class="w-40 border-b border-r border-slate-200 px-2 py-2 dark:border-slate-700">Staff</th>
                <th class="w-28 border-b border-r border-slate-200 px-2 py-2 dark:border-slate-700">Date</th>
                <th class="w-32 border-b border-r border-slate-200 px-2 py-2 dark:border-slate-700">Patient / event</th>
                <th class="w-64 border-b border-r border-slate-200 px-2 py-2 dark:border-slate-700">Job Reference:</th>
                <th class="w-56 border-b border-r border-slate-200 px-2 py-2 dark:border-slate-700">Paid time</th>
                <th class="w-28 border-b border-r border-slate-200 px-2 py-2 dark:border-slate-700">Hours</th>
                <th class="w-20 border-b border-r border-slate-200 px-2 py-2 dark:border-slate-700">Driving</th>
                <th class="w-48 border-b border-r border-slate-200 px-2 py-2 dark:border-slate-700">Pay</th>
                <th class="w-40 border-b border-r border-slate-200 px-2 py-2 dark:border-slate-700">Claim value</th>
                <th class="w-40 border-b border-r border-slate-200 px-2 py-2 dark:border-slate-700">Co-worker</th>
                <th class="w-40 border-b border-r border-slate-200 px-2 py-2 dark:border-slate-700">Journey</th>
                <th class="w-40 border-b border-r border-slate-200 px-2 py-2 dark:border-slate-700">Vehicle</th>
                <th class="w-40 border-b border-slate-200 px-2 py-2 dark:border-slate-700">Notes</th>
              </tr>
            </thead>
            <tbody>
              <template
                v-for="(group, groupIndex) in timesheetSpreadsheetGroups"
                :key="group.key"
              >
                <tr class="border-t-4 border-slate-300 bg-slate-200/80 dark:border-slate-600 dark:bg-slate-800">
                  <td colspan="14" class="px-2 py-1.5">
                    <div class="flex items-center justify-between gap-3">
                      <div class="flex min-w-0 items-center gap-2">
                        <span
                          class="rounded-full px-2 py-0.5 text-[10px] font-semibold capitalize"
                          :class="groupStatusTone(group)"
                        >
                          {{ groupStatusLabel(group) }}
                        </span>
                        <span v-if="group.missingApprovals.length" class="rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-semibold text-rose-800 dark:bg-rose-950 dark:text-rose-200" :title="group.missingApprovals.map(unit => `Shift ${unit.sequence || 1}: ${unit.claim_id ? 'claim exists but is absent from this run’s Claim Approval' : 'no claim created'}${unit.review_message ? ' — ' + unit.review_message : ''}`).join('\n')">
                          {{ group.missingApprovals.length }} missing from Claim Approval
                        </span>
                        <button
                          v-if="groupStatusLabel(group) === 'Ready to import'"
                          type="button"
                          class="whitespace-nowrap rounded-full bg-sky-600 px-2.5 py-1 text-[10px] font-semibold text-white transition hover:bg-sky-500 disabled:cursor-not-allowed disabled:opacity-50"
                          :disabled="savingImportIds.has(group.record.id) || activeRun?.status !== 'draft' || !!importRetryBlocker(group.record)"
                          :title="importRetryBlocker(group.record) || 'Create the payroll claim from these checked rows'"
                          @click.prevent="retryImportRecord(group.record)"
                        >
                          {{ savingImportIds.has(group.record.id) ? "Importing…" : "Import ready" }}
                        </button>
                        <span class="truncate font-semibold text-slate-800 dark:text-slate-100" :title="importRecordTitle(group.record)">
                          {{ importRecordTitle(group.record) }}
                        </span>
                        <span class="whitespace-nowrap text-[10px] text-slate-500 dark:text-slate-400">
                          {{ group.rows.length }} row{{ group.rows.length === 1 ? "" : "s" }}
                        </span>
                        <span
                          v-if="Number(focusedTimesheetImportId) === Number(group.record.id)"
                          class="whitespace-nowrap rounded-full bg-sky-100 px-2 py-0.5 text-[10px] font-semibold text-sky-800 dark:bg-sky-900/50 dark:text-sky-200"
                        >
                          Focused
                        </span>
                        <span
                          v-if="aiVerificationLabel(group.record)"
                          class="whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-semibold"
                          :class="aiVerificationTone(group.record)"
                          :title="aiVerificationTitle(group.record)"
                        >
                          {{ aiVerificationLabel(group.record) }}
                        </span>
                        <button
                          v-if="group.record.source_document_id"
                          type="button"
                          class="whitespace-nowrap rounded-full border border-sky-300 bg-white px-2 py-0.5 text-[10px] font-semibold text-sky-700 transition hover:bg-sky-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-sky-700 dark:bg-slate-900 dark:text-sky-200 dark:hover:bg-sky-950/40"
                          :disabled="aiVerificationRetryPending(group.record) || activeRun?.status !== 'draft'"
                          :title="group.record.ai_verification?.error || 'Extract and re-read every field from the original document with the configured best AI using maximum reasoning and Pro mode'"
                          @click.prevent="reprocessTimesheet({ id: group.record.source_document_id }, { verifyWithBestAi: true })"
                        >
                          {{
                            aiVerificationRetryPending(group.record)
                              ? "Processing..."
                              : group.record.ai_verification?.succeeded
                                ? "Re-verify AI"
                                : group.record.ai_verification?.attempted
                                  ? "Retry best AI extraction"
                                  : "Extract with best AI"
                          }}
                        </button>
                      </div>
                      <div class="flex shrink-0 items-center gap-1">
                        <span
                          v-if="importSaveState[group.record.id]"
                          class="mr-1 text-[10px] font-medium"
                          :class="importSaveState[group.record.id] === 'error' ? 'text-rose-600' : 'text-slate-500 dark:text-slate-400'"
                        >
                          {{ importSaveState[group.record.id] === "saving" ? "Saving…" : importSaveState[group.record.id] === "correction" ? "Unsaved correction — click Save correction" : importSaveState[group.record.id] === "pending" ? "Unsaved…" : importSaveState[group.record.id] === "error" ? "Save failed" : "Saved" }}
                        </span>
                        <button
                          v-if="!['imported', 'excluded'].includes(group.record.status)"
                          type="button"
                          :class="[
                            importActionIconClass,
                            Number(focusedTimesheetImportId) === Number(group.record.id)
                              ? 'bg-sky-100 text-sky-800 dark:bg-sky-900/50 dark:text-sky-200'
                              : '',
                          ]"
                          :title="Number(focusedTimesheetImportId) === Number(group.record.id) ? 'Show all timesheets' : 'Focus on only this timesheet until it is imported'"
                          :aria-label="Number(focusedTimesheetImportId) === Number(group.record.id) ? 'Show all timesheets' : 'Focus this timesheet'"
                          :aria-pressed="Number(focusedTimesheetImportId) === Number(group.record.id)"
                          @click.prevent="toggleTimesheetFocus(group.record)"
                        >
                          <svg viewBox="0 0 24 24" class="h-3.5 w-3.5" aria-hidden="true">
                            <path fill="currentColor" d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Zm0 3a7 7 0 1 1 0 14 7 7 0 0 1 0-14Zm0 3a4 4 0 1 0 0 8 4 4 0 0 0 0-8Zm0 2a2 2 0 1 1 0 4 2 2 0 0 1 0-4Z" />
                          </svg>
                        </button>
                        <a
                          v-if="group.record.source_view_url"
                          :href="group.record.source_view_url"
                          target="_blank"
                          rel="noopener"
                          :class="importActionIconClass"
                          title="Open original timesheet"
                          aria-label="Open original timesheet"
                        >↗</a>
                        <button
                          v-if="group.record.source_view_url && timesheetRecordIsImage(group.record)"
                          type="button"
                          :class="[
                            importActionIconClass,
                            Number(timesheetImagePreview?.recordId) === Number(group.record.id)
                              ? 'bg-sky-100 text-sky-800 dark:bg-sky-900/50 dark:text-sky-200'
                              : '',
                          ]"
                          title="Open original image in the review pane"
                          aria-label="Preview original timesheet image"
                          @click.prevent="openTimesheetImagePreview(group.record)"
                        >
                          <svg viewBox="0 0 24 24" class="h-3.5 w-3.5" aria-hidden="true">
                            <path fill="currentColor" d="M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Zm0 2v12h16V6H4Zm2 10 3.5-4.5 2.5 3 2-2.5 4 5H6Zm9-8.5a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3Z" />
                          </svg>
                        </button>
                        <button
                          v-if="group.record.status !== 'imported'"
                          type="button"
                          :class="importActionIconClass"
                          :disabled="savingImportIds.has(group.record.id)"
                          :title="importRetryBlocker(group.record) || 'Import the corrected rows (this does not re-read the original file)'"
                          aria-label="Retry import"
                          @click.prevent="retryImportRecord(group.record)"
                        >↻</button>
                        <button
                          v-if="group.record.status === 'imported'"
                          type="button"
                          class="rounded bg-sky-600 px-3 py-1 text-xs font-semibold text-white disabled:opacity-50"
                          :disabled="savingImportIds.has(group.record.id) || activeRun?.status !== 'draft'"
                          title="Save changes and update the existing claims together"
                          @click.prevent="retryImportRecord(group.record)"
                        >{{ savingImportIds.has(group.record.id) ? 'Saving…' : 'Save correction' }}</button>
                        <button
                          v-if="group.record.status === 'imported' && importRecordHasUnsavedChanges(group.record)"
                          type="button"
                          class="px-2 py-1 text-xs font-semibold underline"
                          :disabled="savingImportIds.has(group.record.id)"
                          @click.prevent="discardImportCorrection(group.record)"
                        >Discard correction</button>
                        <button
                          type="button"
                          class="rounded border border-slate-300 px-2 py-1 text-xs font-semibold text-slate-700 disabled:opacity-50 dark:border-slate-600 dark:text-slate-200"
                          :disabled="savingImportIds.has(group.record.id) || activeRun?.status !== 'draft' || aiVerificationRetryPending(group.record)"
                          title="Add a row to this timesheet"
                          aria-label="Add a row to this timesheet"
                          @click.prevent="addImportEntry(group.record)"
                        >＋ Add row</button>
                        <button
                          v-if="group.record.source_document_id"
                          type="button"
                          :class="[importActionIconClass, 'text-rose-700 hover:!bg-rose-100 dark:text-rose-300 dark:hover:!bg-rose-950/40']"
                          :disabled="deletingTimesheetDocumentIds.has(group.record.source_document_id) || activeRun?.status !== 'draft'"
                          title="Delete the source timesheet, its rows, and all draft claims it generated"
                          aria-label="Delete entire timesheet"
                          @click.prevent="deleteTimesheetDocument(group.record)"
                        >
                          <svg viewBox="0 0 24 24" class="h-3.5 w-3.5" aria-hidden="true">
                            <path fill="currentColor" d="M9 3h6l1 2h4v2H4V5h4l1-2Zm-2 6h10l-1 11H8L7 9Zm3 2v7h2v-7h-2Zm4 0v7h2v-7h-2Z" />
                          </svg>
                        </button>
                        <button
                          v-if="group.record.status !== 'imported'"
                          type="button"
                          :class="[importActionIconClass, 'text-rose-700 hover:!bg-rose-100 dark:text-rose-300 dark:hover:!bg-rose-950/40']"
                          :disabled="savingImportIds.has(group.record.id)"
                          title="Exclude this timesheet"
                          aria-label="Exclude this timesheet"
                          @click.prevent="excludeImportRecord(group.record)"
                        >⊘</button>
                      </div>
                    </div>
                    <div
                      v-if="group.issues.length || importCorrectionResults[group.record.id]"
                      class="mt-1.5 flex items-start gap-2 rounded border border-amber-300 bg-amber-50 px-2 py-1.5 text-[10px] text-amber-950 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-100"
                    >
                      <span class="shrink-0 font-bold uppercase tracking-wide">{{ group.issues.length ? 'Fix' : 'Save result' }}</span>
                      <span v-if="importCorrectionResults[group.record.id]" class="whitespace-pre-line text-xs">{{ importCorrectionResults[group.record.id] }}</span>
                      <ul class="flex min-w-0 flex-wrap gap-x-4 gap-y-1">
                        <li
                          v-for="(issue, issueIndex) in group.issues"
                          :key="`${issue.scope}-${issueIndex}`"
                        >
                          <strong>{{ issue.scope }}:</strong> {{ issue.message }}
                        </li>
                      </ul>
                    </div>
                    <div v-if="group.record.duplicate_submissions?.length" class="mt-2 rounded border border-amber-300 bg-amber-50 px-2 py-1.5 text-xs text-amber-950 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-100">
                      <strong>Duplicate submission check</strong>
                      <p v-for="peer in group.record.duplicate_submissions" :key="peer.id">
                        {{ peer.file_name }} (import #{{ peer.id }}): {{ peer.reasons.join('; ') }}
                        <a v-if="peer.source_view_url" :href="peer.source_view_url" target="_blank" rel="noopener" class="ml-2 underline">View other original</a>
                      </p>
                    </div>
                  </td>
                </tr>
                <tr
                  v-for="row in group.rows"
                  :key="row.key"
                  :inert="(row.record.status === 'imported' && savingImportIds.has(row.record.id)) || activeRun?.status !== 'draft'"
                  class="align-top"
                  @focusin="focusImportRow(row)"
                  @focusout="blurImportRow($event, row)"
                  :class="
                    entryHoursDiffer(row.entry)
                      ? 'bg-rose-50/80 dark:bg-rose-950/20'
                      : group.issues.length
                        ? 'bg-amber-50/50 dark:bg-amber-950/10'
                        : groupIndex % 2
                          ? 'bg-slate-50 dark:bg-slate-900/50'
                          : 'bg-white dark:bg-slate-950'
                  "
                >
                  <td class="border-b border-r border-slate-200 px-1 py-2 text-center dark:border-slate-800">
                    <span
                      class="inline-flex h-5 w-5 items-center justify-center rounded-full font-bold"
                      :class="
                        entryIssueLabel(row) === 'Imported'
                          ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
                          : entryIssueLabel(row) === 'Ready to import'
                            ? 'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-200'
                            : 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200'
                      "
                      :title="entryIssueTitle(row)"
                    >
                      {{ entryIssueLabel(row) === "Imported" ? "✓" : entryIssueLabel(row) === "Ready to import" ? "→" : "!" }}
                    </span>
                    <div v-if="presenceForRow(row.record.id, row.entry.sequence || row.entryIndex + 1).length" class="mt-1 flex justify-center -space-x-1">
                      <span
                        v-for="person in presenceForRow(row.record.id, row.entry.sequence || row.entryIndex + 1)"
                        :key="`${person.user_id}-${person.last_seen_at}`"
                        class="inline-flex h-4 w-4 items-center justify-center rounded-full border border-white text-[7px] font-bold text-white shadow-sm dark:border-slate-950"
                        :class="presenceColour(person.user_id)"
                        :title="`${person.name}${person.is_current_user ? ' (you)' : ''} is editing this row`"
                      >
                        <img v-if="person.avatar_url" :src="person.avatar_url" :alt="person.name" class="h-full w-full rounded-full object-cover" />
                        <span v-else>{{ presenceInitials(person.name) }}</span>
                      </span>
                    </div>
                    <button
                      v-if="row.edit.entries.length > 1"
                      type="button"
                      class="mx-auto mt-1 flex h-6 w-6 items-center justify-center rounded border border-rose-200 text-rose-600 transition hover:bg-rose-100 hover:text-rose-800 disabled:cursor-not-allowed disabled:opacity-50 dark:border-rose-900 dark:text-rose-300 dark:hover:bg-rose-950/50"
                      :disabled="savingImportIds.has(row.record.id) || activeRun?.status !== 'draft'"
                      :title="row.record.status === 'imported' ? 'Reopen this timesheet, remove the generated claims, and delete this row before re-importing' : 'Delete this timesheet entry and save immediately'"
                      aria-label="Delete this timesheet entry"
                      @click.prevent="deleteImportEntry(row.record, row.entryIndex)"
                    >
                      <svg viewBox="0 0 24 24" class="h-3.5 w-3.5" aria-hidden="true">
                        <path fill="currentColor" d="M9 3h6l1 2h4v2H4V5h4l1-2Zm-2 6h10l-1 11H8L7 9Zm3 2v7h2v-7h-2Zm4 0v7h2v-7h-2Z" />
                      </svg>
                    </button>
                  </td>
                  <td class="border-b border-r border-slate-200 p-1 dark:border-slate-800">
                    <div
                      v-if="row.entry.is_shift_lead"
                      class="rounded border p-0.5"
                      :class="
                        row.edit.staff_id
                          ? 'border-emerald-400 bg-emerald-50 dark:border-emerald-700 dark:bg-emerald-950/20'
                          : row.edit.staff_query
                            ? 'border-amber-400 bg-amber-50 dark:border-amber-700 dark:bg-amber-950/20'
                            : 'border-slate-300 dark:border-slate-700'
                      "
                    >
                      <div class="px-1 text-[9px] font-semibold" :class="row.edit.staff_id ? 'text-emerald-700 dark:text-emerald-300' : 'text-amber-700 dark:text-amber-300'">
                        {{ row.edit.staff_id ? "✓ matched" : "• select staff" }}
                      </div>
                      <SearchableInput
                        :model-value="row.edit.staff_query"
                        :output="staffSearchOutput"
                        url="/api/staff/search?query="
                        placeholder="Search staff"
                        input-class="!border-0 !bg-transparent !px-1.5 !py-1 !text-xs !shadow-none"
                        results-class="!static !mt-1 !max-h-48"
                        :clear-on-select="false"
                        @update:modelValue="updateImportStaffQuery(row.record, $event)"
                        @selected="setImportStaff(row.record, $event)"
                      />
                    </div>
                  </td>
                  <td class="border-b border-r border-slate-200 p-1 dark:border-slate-800">
                    <input
                      v-model="row.entry.date"
                      type="date"
                      class="w-full rounded border border-slate-300 bg-white px-1 py-1.5 text-[11px] dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:[color-scheme:dark]"
                      @change="updateImportEntryDate(row.entry, row.record)"
                    />
                  </td>
                  <td class="border-b border-r border-slate-200 p-1 dark:border-slate-800">
                    <input v-model="row.entry.patient_reference" type="text" placeholder="Patient / event" class="w-full rounded border border-slate-300 bg-white px-1.5 py-1 text-xs dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100" />
                    <input v-model="row.entry.patient_category" type="text" placeholder="Category" class="mt-1 w-full rounded border border-slate-300 bg-white px-1.5 py-1 text-[10px] dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100" />
                  </td>
                  <td class="border-b border-r border-slate-200 p-1 dark:border-slate-800">
                    <div
                      class="rounded border p-0.5"
                      :class="
                        row.entry.job_date_id && isOutsidePayrollPeriod(row.entry.job_date_date, activeRun)
                          ? 'border-amber-400 bg-amber-50 dark:border-amber-700 dark:bg-amber-950/20'
                          : row.entry.job_date_id
                          ? 'border-emerald-400 bg-emerald-50 dark:border-emerald-700 dark:bg-emerald-950/20'
                          : row.entry.job_date_query
                            ? 'border-amber-400 bg-amber-50 dark:border-amber-700 dark:bg-amber-950/20'
                            : 'border-slate-300 dark:border-slate-700'
                      "
                    >
                      <div class="flex items-center justify-between gap-1 px-1 text-[9px] font-semibold">
                        <span
                          :class="
                            row.entry.job_date_id && !isOutsidePayrollPeriod(row.entry.job_date_date, activeRun)
                              ? 'text-emerald-700 dark:text-emerald-300'
                              : 'text-amber-700 dark:text-amber-300'
                          "
                        >
                          {{
                            row.entry.job_date_id && isOutsidePayrollPeriod(row.entry.job_date_date, activeRun)
                              ? "↩ late claim matched"
                              : row.entry.job_date_id
                                ? "✓ Neptune job matched"
                                : "• search name or number"
                          }}
                        </span>
                        <label
                          class="flex shrink-0 cursor-pointer items-center gap-1 font-medium text-slate-500 dark:text-slate-400"
                          title="Search outside the current and two preceding payroll periods"
                        >
                          <input v-model="row.entry.search_all_job_dates" type="checkbox" class="h-3 w-3 rounded border-slate-300" />
                          all dates
                        </label>
                      </div>
                      <SearchableInput
                        :model-value="row.entry.job_date_query"
                        :output="jobDateSearchOutput"
                        :url="importJobDateSearchUrl(activeRun, row.entry.search_all_job_dates)"
                        placeholder="Reference name or number"
                        input-class="!border-0 !bg-transparent !px-1.5 !py-1 !text-xs !shadow-none"
                        results-class="!static !mt-1 !max-h-48"
                        :clear-on-select="false"
                        :search-on-focus="true"
                        @update:modelValue="updateImportEntryJobQuery(row.entry, $event, row.record)"
                        @selected="setImportEntryJobDate(row.entry, $event, row.record)"
                      />
                    </div>
                    <div v-if="row.entry.is_shift_lead && !row.entry.job_date_id && row.entry.match_suggestions?.length" class="mt-1 flex flex-wrap gap-1">
                      <button
                        v-for="suggestion in row.entry.match_suggestions.slice(0, 2)"
                        :key="suggestion.id"
                        type="button"
                        class="max-w-full truncate rounded bg-amber-100 px-1.5 py-0.5 text-[9px] font-semibold text-amber-900 hover:bg-amber-200 dark:bg-amber-900/40 dark:text-amber-100"
                        :title="`${jobDateSearchOutput(suggestion)} · ${suggestion.reason || 'possible match'}`"
                        @click.prevent="setImportEntryJobDate(row.entry, suggestion, row.record)"
                      >
                        {{ suggestion.reference?.txt || "Use job" }} · {{ formatDate(suggestion.date) }}
                        <span v-if="!suggestion.date_exact"> ⚠</span>
                      </button>
                    </div>
                    <span v-if="!row.entry.is_shift_lead" class="whitespace-nowrap text-[9px] font-semibold text-slate-400 dark:text-slate-500">same shift</span>
                  </td>
                  <td class="border-b border-r border-slate-200 p-1 dark:border-slate-800">
                    <div v-if="row.entry.is_shift_lead" class="grid min-w-[13.5rem] grid-cols-2 gap-1">
                      <input v-model="row.entry.work_start_time" type="time" title="Paid start" class="min-w-[6.5rem] w-full rounded border border-slate-300 bg-white px-1 py-1 text-[10px] dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:[color-scheme:dark]" />
                      <input v-model="row.entry.work_finish_time" type="time" title="Paid finish" class="min-w-[6.5rem] w-full rounded border border-slate-300 bg-white px-1 py-1 text-[10px] dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:[color-scheme:dark]" />
                    </div>
                    <div v-else class="space-y-1">
                      <span class="block text-[9px] text-slate-500 dark:text-slate-400">Supporting line · uses shift times</span>
                      <button type="button" class="text-[10px] font-semibold text-sky-700 underline dark:text-sky-300" @click.prevent="makeSeparatePaidShift(row)">Separate paid shift · enter times</button>
                    </div>
                  </td>
                  <td class="border-b border-r border-slate-200 p-1 dark:border-slate-800">
                    <input v-if="row.entry.is_shift_lead" v-model="row.entry.claimed_hours" type="number" min="0" step="0.01" title="Written hours" class="w-full rounded border border-slate-300 bg-white px-1.5 py-1 text-right text-xs dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100" />
                    <div
                      v-if="row.entry.is_shift_lead"
                      class="mt-1 text-right text-[9px] font-semibold tabular-nums"
                      :class="entryHoursDiffer(row.entry) ? 'text-rose-700 dark:text-rose-300' : 'text-slate-500 dark:text-slate-400'"
                    >
                      calc {{ formatHoursValue(calculatedEntryHours(row.entry)) }}
                      <template v-if="row.entry.claimed_hours !== '' && calculatedEntryHours(row.entry) !== null">
                        · Δ {{ (Number(row.entry.claimed_hours) - Number(calculatedEntryHours(row.entry))).toFixed(2) }}
                      </template>
                    </div>
                  </td>
                  <td class="border-b border-r border-slate-200 p-1 dark:border-slate-800">
                    <div v-if="row.entry.is_shift_lead" class="grid min-w-[7.5rem] gap-1">
                      <select v-model="row.entry.component_type" title="Pay component" class="w-full rounded border border-slate-300 bg-white px-1 py-1 text-[10px] dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100">
                        <option value="work">Work</option>
                        <option value="outbound_driving">Drive out</option>
                        <option value="job_driving">Job driving</option>
                        <option value="return_driving">Drive back</option>
                      </select>
                      <input v-model="row.entry.hours_driving" type="number" min="0" step="0.01" placeholder="Drive hrs" title="Claimed driving hours" class="w-full rounded border border-slate-300 bg-white px-1 py-1 text-right text-xs dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100" />
                    </div>
                    <span v-else class="whitespace-nowrap text-[9px] font-semibold text-slate-400 dark:text-slate-500">same shift</span>
                  </td>
                  <td class="border-b border-r border-slate-200 p-1 dark:border-slate-800">
                    <div class="grid grid-cols-[1fr_3.5rem] gap-1">
                      <select v-model="row.entry.payment_basis" class="w-full rounded border border-slate-300 bg-white px-1 py-1 text-[10px] dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100">
                        <option value="hourly">Hourly</option>
                        <option value="daily">Daily</option>
                        <option value="item">Item</option>
                        <option value="flat">Flat</option>
                      </select>
                      <input v-model="row.entry.agreed_rate" type="text" placeholder="Rate" class="w-full rounded border border-slate-300 bg-white px-1 py-1 text-[10px] uppercase dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100" />
                    </div>
                    <select :value="row.entry.product_id" class="mt-1 w-full rounded border border-slate-300 bg-white px-1 py-1 text-[10px] dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100" @change="setImportEntryProduct(row.entry, $event.target.value, row.record)">
                      <option value="">No product / role</option>
                      <option v-for="product in availableProducts" :key="product.id" :value="product.id">{{ product.label }}</option>
                    </select>
                  </td>
                  <td class="border-b border-r border-slate-200 p-1 dark:border-slate-800">
                    <div class="grid grid-cols-3 gap-1">
                      <input v-model="row.entry.quantity" type="number" min="0" step="0.01" placeholder="Qty" title="Quantity" class="w-full rounded border border-slate-300 bg-white px-1 py-1 text-[9px] dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100" />
                      <input v-model="row.entry.unit_rate" type="number" min="0" step="0.01" placeholder="Rate" title="Unit rate" class="w-full rounded border border-slate-300 bg-white px-1 py-1 text-[9px] dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100" />
                      <input v-model="row.entry.amount" type="number" min="0" step="0.01" placeholder="Total" title="Amount" class="w-full rounded border border-slate-300 bg-white px-1 py-1 text-[9px] dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100" />
                    </div>
                  </td>
                  <td class="border-b border-r border-slate-200 p-1 dark:border-slate-800">
                    <div
                      class="rounded border p-0.5"
                      :class="
                        row.entry.co_worker_staff_id
                          ? 'border-emerald-400 bg-emerald-50 dark:border-emerald-700 dark:bg-emerald-950/20'
                          : 'border-slate-300 dark:border-slate-700'
                      "
                    >
                      <div class="px-1 text-[9px] font-semibold" :class="row.entry.co_worker_staff_id ? 'text-emerald-700 dark:text-emerald-300' : 'text-slate-500 dark:text-slate-400'">
                        {{ row.entry.co_worker_staff_id ? "✓ matched" : "optional" }}
                      </div>
                      <SearchableInput
                        :model-value="row.entry.co_worker_query"
                        :output="staffSearchOutput"
                        url="/api/staff/search?query="
                        placeholder="Search co-worker"
                        input-class="!border-0 !bg-transparent !px-1.5 !py-1 !text-xs !shadow-none"
                        results-class="!static !mt-1 !max-h-48"
                        :clear-on-select="false"
                        @update:modelValue="updateImportEntryCoWorkerQuery(row.entry, $event, row.record)"
                        @selected="setImportEntryCoWorker(row.entry, $event, row.record)"
                      />
                    </div>
                  </td>
                  <td class="border-b border-r border-slate-200 p-1 dark:border-slate-800">
                    <div class="grid grid-cols-2 gap-1 ">
                      <input v-model="row.entry.job_start_time" type="text" placeholder="Start" title="Journey start time" class="min-w-16 w-full rounded border border-slate-300 bg-white px-1 py-1 text-[9px] dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100" />
                      <input v-model="row.entry.job_end_time" type="text" placeholder="End" title="Journey end time" class="min-w-16 w-full rounded border border-slate-300 bg-white px-1 py-1 text-[9px] dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100" />
                      <input v-model="row.entry.job_start_postcode" type="text" placeholder="From" title="Journey start postcode" class="w-full rounded border border-slate-300 bg-white px-1 py-1 text-[9px] dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100" />
                      <input v-model="row.entry.job_end_postcode" type="text" placeholder="To" title="Journey end postcode" class="w-full rounded border border-slate-300 bg-white px-1 py-1 text-[9px] dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100" />
                    </div>
                  </td>
                  <td class="border-b border-r border-slate-200 p-1 dark:border-slate-800">
                    <div
                      class="rounded border p-0.5"
                      :class="
                        row.entry.vehicle_id
                          ? 'border-emerald-400 bg-emerald-50 dark:border-emerald-700 dark:bg-emerald-950/20'
                          : 'border-slate-300 dark:border-slate-700'
                      "
                    >
                      <div class="px-1 text-[9px] font-semibold" :class="row.entry.vehicle_id ? 'text-emerald-700 dark:text-emerald-300' : 'text-slate-500 dark:text-slate-400'">
                        {{ row.entry.vehicle_id ? "✓ matched" : "optional" }}
                      </div>
                      <SearchableInput
                        :model-value="row.entry.vehicle_query"
                        :output="vehicleSearchOutput"
                        url="/api/vehicle/search?query="
                        placeholder="Registration"
                        input-class="!border-0 !bg-transparent !px-1.5 !py-1 !text-xs !uppercase !shadow-none"
                        results-class="!static !mt-1 !max-h-48"
                        :clear-on-select="false"
                        :search-on-focus="true"
                        @update:modelValue="updateImportEntryVehicleQuery(row.entry, $event, row.record)"
                        @selected="setImportEntryVehicle(row.entry, $event, row.record)"
                      />
                    </div>
                  </td>
                  <td class="border-b border-slate-200 p-1 dark:border-slate-800">
                    <textarea v-model="row.entry.notes" rows="2" class="w-full resize-none rounded border border-slate-300 bg-white px-1.5 py-1 text-[10px] dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"></textarea>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>

      </section>
    </template>

    <template v-else-if="workspace === 'claims'">
      <section
        class="box border border-slate-200 dark:border-slate-700 dark:bg-slate-900/70"
      >
        <div
          class="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between"
        >
          <div>
            <h2 class="text-2xl font-semibold text-slate-900 dark:text-white">
              Active Payroll Preparation
            </h2>
            <p class="mt-1 max-w-3xl text-sm text-slate-500 dark:text-slate-400">
              Review imported timesheet claims, correct hours, and approve the
              rows that will feed the accountant payroll sheet. Rebuild Claims
              creates or refreshes one claim row per imported timesheet,
              recalculates expected hours from the payroll rules, and
              auto-approves anything that matches. Explicitly accepted,
              overridden, or excluded decisions remain locked until you reset
              that individual claim. This workspace shows both the claims that
              still need review and the claims already approved into the run.
            </p>
          </div>
          <div class="flex flex-col gap-2 sm:flex-row">
            <button
              v-if="activeRun?.accountant_export_download_url && accountantPreflight.exportable"
              type="button"
              class="btn"
              :disabled="downloadingAccountantWorkbook"
              @click.prevent="downloadAccountantWorkbook()"
            >
              {{ downloadingAccountantWorkbook ? "Preparing XLSX..." : "Export Accountant Workbook" }}
            </button>
            <button
              v-else-if="activeRun?.accountant_export_download_url"
              class="btn border-amber-400 text-amber-800 dark:border-amber-600 dark:text-amber-200"
              type="button"
              :disabled="downloadingAccountantWorkbook"
              :title="accountantPreflight.errors.join('\n')"
              @click.prevent="downloadAccountantWorkbook(true)"
            >
              {{ downloadingAccountantWorkbook ? "Preparing..." : "⚠ Export Draft Workbook" }}
            </button>
            <button
              class="btn"
              :disabled="loadingRuns || loadingRun"
              title="Reload server data without discarding unresolved local edits"
              @click.prevent="refreshRunSafely"
            >
              Refresh Run
            </button>
            <button
              class="btn accent"
              :disabled="!activeRun || refreshingClaims"
              @click.prevent="refreshClaims"
            >
              {{ refreshingClaims ? "Refreshing..." : "Rebuild Claims" }}
            </button>
          </div>
        </div>

        <div
          v-if="!activeRun"
          class="mt-6 rounded-2xl border border-dashed border-slate-300 px-4 py-8 text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400"
        >
          Select or create a payroll run before preparing timesheet claims.
        </div>

        <div v-else class="mt-6 space-y-6">
          <div class="rounded-3xl border border-slate-200 bg-slate-50 p-5 dark:border-slate-800 dark:bg-slate-950/70">
            <div class="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <div class="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">
                  Active Run
                </div>
                <h3 class="mt-2 text-xl font-semibold text-slate-900 dark:text-white">
                  {{ activeRun.name }}
                </h3>
                <div class="mt-1 text-sm text-slate-500 dark:text-slate-400">
                  {{ periodLabel(activeRun) }}
                  <span v-if="activeRun.tax_month"> · HMRC month {{ activeRun.tax_month }}</span>
                </div>
              </div>
              <span
                class="rounded-full px-2.5 py-1 text-[11px] font-semibold capitalize"
                :class="statusTone(activeRun.status)"
              >
                {{ activeRun.status.replace("_", " ") }}
              </span>
            </div>
          </div>

          <div class="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <div class="rounded-3xl border border-slate-200 bg-white px-5 py-4 dark:border-slate-800 dark:bg-slate-950/80">
              <div class="text-[11px] font-medium uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">
                Claims
              </div>
              <div class="mt-2 text-3xl font-semibold text-slate-900 dark:text-white">
                {{ activeClaims.length }}
              </div>
            </div>
            <div class="rounded-3xl border border-slate-200 bg-white px-5 py-4 dark:border-slate-800 dark:bg-slate-950/80">
              <div class="text-[11px] font-medium uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">
                Accepted
              </div>
              <div class="mt-2 text-3xl font-semibold text-slate-900 dark:text-white">
                {{ acceptedClaimsCount }}
              </div>
            </div>
            <div class="rounded-3xl border border-slate-200 bg-white px-5 py-4 dark:border-slate-800 dark:bg-slate-950/80">
              <div class="text-[11px] font-medium uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">
                Needs Review
              </div>
              <div class="mt-2 text-3xl font-semibold text-slate-900 dark:text-white">
                {{ claimReviewCount }}
              </div>
            </div>
            <div class="rounded-3xl border border-slate-200 bg-white px-5 py-4 dark:border-slate-800 dark:bg-slate-950/80">
              <div class="text-[11px] font-medium uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">
                Excluded
              </div>
              <div class="mt-2 text-3xl font-semibold text-slate-900 dark:text-white">
                {{ excludedClaimsCount }}
              </div>
            </div>
          </div>

          <div class="rounded-3xl border border-slate-200 bg-white px-5 py-4 dark:border-slate-800 dark:bg-slate-950/80">
            <div class="text-[11px] font-medium uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">
              Accepted Hours
            </div>
            <div class="mt-2 text-3xl font-semibold text-slate-900 dark:text-white">
              {{ acceptedHoursTotal }}
            </div>
          </div>

          <div
            v-if="activeClaims.length === 0"
            class="rounded-2xl border border-dashed border-slate-300 px-4 py-8 text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400"
          >
            No timesheet claims have been prepared for this payroll run yet.
          </div>

          <div v-else class="space-y-6">
            <div class="overflow-x-auto border-b border-slate-200 dark:border-slate-700">
              <div
                class="flex min-w-max items-end gap-1"
                role="tablist"
                aria-label="Filter claim approvals"
              >
                <button
                  v-for="tab in claimApprovalTabs"
                  :key="tab.key"
                  type="button"
                  role="tab"
                  class="inline-flex items-center gap-2 rounded-t-xl border border-b-0 px-3 py-2 text-sm font-semibold transition"
                  :class="
                    claimApprovalFilter === tab.key
                      ? 'border-slate-300 bg-white text-slate-950 shadow-sm dark:border-slate-600 dark:bg-slate-900 dark:text-white'
                      : 'border-transparent text-slate-500 hover:border-slate-200 hover:bg-slate-100/70 hover:text-slate-900 dark:text-slate-400 dark:hover:border-slate-700 dark:hover:bg-slate-800 dark:hover:text-white'
                  "
                  :aria-selected="claimApprovalFilter === tab.key"
                  @click.prevent="claimApprovalFilter = tab.key"
                >
                  <span>{{ tab.title }}</span>
                  <span
                    class="rounded-full px-2 py-0.5 text-[10px]"
                    :class="
                      claimApprovalFilter === tab.key
                        ? 'bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900'
                        : 'bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-300'
                    "
                  >
                    {{ tab.count }}
                  </span>
                </button>
              </div>
            </div>

            <button v-if="!accountantPreflight.exportable" type="button" class="text-sm font-semibold text-amber-800 underline dark:text-amber-300" @click="exportBlockersOpen = true">Review all {{ accountantPreflight.blocking_count }} export blockers, including imports and documents</button>

            <div
              v-if="visibleClaimSections.length === 0"
              class="rounded-2xl border border-dashed border-slate-300 px-4 py-8 text-center text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400"
            >
              No claims are currently in this group.
            </div>

            <section
              v-for="section in visibleClaimSections"
              :key="section.key"
              class="space-y-4"
            >
              <div
                class="rounded-2xl border px-4 py-3"
                :class="section.tone"
              >
                <div class="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <div class="text-sm font-semibold text-slate-900 dark:text-white">
                      {{ section.title }}
                    </div>
                    <div class="mt-1 text-sm text-slate-600 dark:text-slate-300">
                      {{ section.description }}
                    </div>
                  </div>
                  <span
                    class="rounded-full px-2.5 py-1 text-[11px] font-semibold"
                    :class="section.countTone"
                  >
                    {{ section.claims.length }} claims
                  </span>
                </div>
              </div>

              <article
                v-for="claim in section.claims"
                :key="claim.id"
                :id="`payroll-claim-${claim.id}`"
                tabindex="-1"
                :class="highlightedExportClaimId === Number(claim.id) ? 'ring-2 ring-amber-500' : ''"
                class="rounded-3xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-950/80"
              >
              <div v-if="exportClaimIssues(claim).length" class="mb-3 rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-100">
                <strong>Export blocked · claim #{{ claim.id }}</strong>
                <ul class="list-disc pl-5"><li v-for="issue in exportClaimIssues(claim)" :key="issue">{{ issue }}</li></ul>
              </div>
              <div class="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
                <div class="min-w-0">
                  <div class="flex flex-wrap items-center gap-2">
                    <h3 class="text-lg font-semibold text-slate-900 dark:text-white">
                      {{ claim.staff?.name || claim.source_staff_name || "Unmatched staff" }}
                    </h3>
                    <span
                      class="rounded-full px-2.5 py-1 text-[11px] font-semibold capitalize"
                      :class="statusTone(claim.status)"
                    >
                      {{ claim.status === "accepted" && !claimHasLockedReviewDecision(claim) ? "approved" : claim.status.replace("_", " ") }}
                    </span>
                    <span
                      v-if="claimHasLockedReviewDecision(claim)"
                      class="rounded-full bg-sky-100 px-2.5 py-1 text-[11px] font-semibold text-sky-800 dark:bg-sky-900/50 dark:text-sky-200"
                      title="This individual review decision will survive claim rebuilds until it is reset"
                    >
                      🔒 Locked decision
                    </span>
                  </div>
                  <div class="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-500 dark:text-slate-400">
                    <RouterLink
                      v-if="claim.reference?.id"
                      :to="{ name: 'reference-view', params: { id: claim.reference.id } }"
                      class="font-medium text-sky-700 transition hover:text-sky-900 hover:underline dark:text-sky-300 dark:hover:text-sky-200"
                    >
                      {{ claim.reference_code || claim.source_reference || "No reference" }}
                    </RouterLink>
                    <span v-else>{{ claim.reference_code || claim.source_reference || "No reference" }}</span>
                    <span>{{ claim.reference_type || claim.rule_code || "No rule" }}</span>
                    <span>{{ formatDate(claim.job_date?.date || claim.source_date) }}</span>
                    <span v-if="claim.product">{{ claim.product.short || claim.product.name }}</span>
                    <a
                      v-if="claim.source_document?.view_url"
                      :href="claim.source_document.view_url"
                      target="_blank"
                      rel="noopener"
                      class="font-medium text-sky-700 hover:underline dark:text-sky-300"
                    >
                      View {{ claim.source_document.name }}
                    </a>
                  </div>
                  <div
                    v-if="claim.override_notes"
                    class="mt-3 rounded-2xl bg-slate-50 px-3 py-2 text-sm text-slate-600 dark:bg-slate-900 dark:text-slate-300"
                  >
                    <span class="font-medium text-slate-700 dark:text-slate-200">
                      Reviewer note:
                    </span>
                    {{ claim.override_notes }}
                  </div>
                  <section
                    class="mt-3 rounded-2xl border px-4 py-3"
                    :class="claimCalculationTone(claim)"
                  >
                    <div class="flex flex-col gap-2 lg:flex-row lg:items-start lg:justify-between">
                      <div>
                        <div class="text-sm font-semibold">
                          {{ claimCalculationHeadline(claim) }}
                        </div>
                        <div class="mt-1 text-sm">
                          {{ claimCalculationBody(claim) }}
                        </div>
                      </div>
                      <div class="text-xs font-medium uppercase tracking-wide opacity-80">
                        {{ claimRuleLabel(claim) }}
                      </div>
                    </div>
                    <div class="mt-3 grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
                      <div class="rounded-xl border border-current/10 bg-white/60 px-3 py-2 dark:bg-slate-950/30">
                        <div class="text-[11px] uppercase tracking-wide opacity-70">
                          Written total
                        </div>
                        <div class="mt-1 text-sm font-semibold">
                          {{ formatHoursValue(claim.source_claimed_hours) }} h
                        </div>
                      </div>
                      <div class="rounded-xl border border-current/10 bg-white/60 px-3 py-2 dark:bg-slate-950/30">
                        <div class="text-[11px] uppercase tracking-wide opacity-70">
                          Calculated from form
                        </div>
                        <div class="mt-1 text-sm font-semibold">
                          {{ formatHoursValue(claim.calculated_hours ?? claim.claimed_hours) }} h
                        </div>
                      </div>
                      <div class="rounded-xl border border-current/10 bg-white/60 px-3 py-2 dark:bg-slate-950/30">
                        <div class="text-[11px] uppercase tracking-wide opacity-70">
                          Expected Job
                        </div>
                        <div class="mt-1 text-sm font-semibold">
                          {{ formatHoursValue(claim.expected_job_hours) }} h
                        </div>
                      </div>
                      <div class="rounded-xl border border-current/10 bg-white/60 px-3 py-2 dark:bg-slate-950/30">
                        <div class="text-[11px] uppercase tracking-wide opacity-70">
                          Expected Driving
                        </div>
                        <div class="mt-1 text-sm font-semibold">
                          {{ formatHoursValue(claim.expected_driving_hours) }} h
                        </div>
                      </div>
                      <div class="rounded-xl border border-current/10 bg-white/60 px-3 py-2 dark:bg-slate-950/30">
                        <div class="text-[11px] uppercase tracking-wide opacity-70">
                          Expected Total
                        </div>
                        <div class="mt-1 text-sm font-semibold">
                          {{ formatHoursValue(claim.expected_total_hours) }} h
                        </div>
                      </div>
                    </div>
                    <div class="mt-3 flex flex-col gap-1 text-xs opacity-90">
                      <div>
                        {{ claimDifferenceSummary(claim) || "No comparison available yet." }}
                      </div>
                      <div v-if="claimTravelSummary(claim)">
                        {{ claimTravelSummary(claim) }}
                      </div>
                    </div>
                  </section>
                  <div class="mt-4 grid gap-3 sm:grid-cols-3">
                    <div class="block">
                      <div class="flex items-center justify-between gap-2">
                        <span class="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
                          Staff
                        </span>
                        <button
                          v-if="claimEdit(claim).staff_id"
                          type="button"
                          class="text-xs font-medium text-slate-500 transition hover:text-slate-900 dark:text-slate-400 dark:hover:text-white"
                          @click.prevent="clearClaimStaff(claim)"
                        >
                          Clear
                        </button>
                      </div>
                      <SearchableInput
                        class="mt-1"
                        :model-value="claimEdit(claim).staff_query"
                        :output="staffSearchOutput"
                        url="/api/staff/search?query="
                        placeholder="Search staff"
                        :clear-on-select="false"
                        @update:modelValue="claimEdit(claim).staff_query = $event"
                        @selected="setClaimStaff(claim, $event)"
                      />
                    </div>
                    <div class="block">
                      <div class="flex items-center justify-between gap-2">
                        <span class="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
                          Job Date
                        </span>
                        <span class="flex items-center gap-3">
                          <label
                            class="flex cursor-pointer items-center gap-1 text-xs font-medium text-slate-500 dark:text-slate-400"
                            title="Search outside the current and two preceding payroll periods"
                          >
                            <input v-model="claimEdit(claim).search_all_job_dates" type="checkbox" class="h-3.5 w-3.5 rounded border-slate-300" />
                            All dates
                          </label>
                          <button
                            v-if="claimEdit(claim).job_date_id"
                            type="button"
                            class="text-xs font-medium text-slate-500 transition hover:text-slate-900 dark:text-slate-400 dark:hover:text-white"
                            @click.prevent="clearClaimJobDate(claim)"
                          >
                            Clear
                          </button>
                        </span>
                      </div>
                      <SearchableInput
                        class="mt-1"
                        :model-value="claimEdit(claim).job_date_query"
                        :output="jobDateSearchOutput"
                        :url="claimJobDateSearchUrl(activeRun, claimEdit(claim).search_all_job_dates)"
                        placeholder="Search job date"
                        :clear-on-select="false"
                        :search-on-focus="true"
                        @update:modelValue="updateClaimJobDateQuery(claim, $event)"
                        @selected="setClaimJobDate(claim, $event)"
                      />
                      <div
                        v-if="claimEdit(claim).job_date_id && isOutsidePayrollPeriod(claimEdit(claim).job_date_date, activeRun)"
                        class="mt-1 text-xs font-semibold text-amber-700 dark:text-amber-300"
                      >
                        ↩ Late claim from outside this payroll period
                      </div>
                    </div>
                    <label class="block">
                      <span class="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
                        Product
                      </span>
                      <select
                        v-model="claimEdit(claim).product_id"
                        class="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-sky-400 focus:outline-none focus:ring-4 focus:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:border-sky-500 dark:focus:ring-sky-500/20"
                      >
                        <option value="">No product</option>
                        <option
                          v-for="product in availableProducts"
                          :key="product.id"
                          :value="product.id"
                        >
                          {{ product.label }}
                        </option>
                      </select>
                    </label>
                    <label class="block">
                      <span class="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
                        Rate code
                      </span>
                      <input
                        v-model="claimEdit(claim).rate_code"
                        type="text"
                        maxlength="32"
                        class="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm uppercase text-slate-900 shadow-sm focus:border-sky-400 focus:outline-none focus:ring-4 focus:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:border-sky-500 dark:focus:ring-sky-500/20"
                        placeholder="PTS"
                      />
                    </label>
                  </div>
                </div>

                <div class="grid min-w-full gap-3 sm:grid-cols-2 xl:min-w-[32rem] xl:grid-cols-4">
                  <div class="rounded-2xl bg-slate-50 px-3 py-3 dark:bg-slate-900">
                    <div class="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
                      Claimed
                    </div>
                    <div class="mt-1 font-semibold text-slate-900 dark:text-white">
                      {{ claim.claimed_hours ?? "—" }}
                    </div>
                    <div v-if="claim.claimed_work_hours !== null || claim.claimed_driving_hours !== null" class="mt-1 text-[10px] text-slate-500 dark:text-slate-400">
                      Work {{ claim.claimed_work_hours ?? 0 }} · Drive {{ claim.claimed_driving_hours ?? 0 }}
                    </div>
                  </div>
                  <div class="rounded-2xl bg-slate-50 px-3 py-3 dark:bg-slate-900">
                    <div class="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
                      Job
                    </div>
                    <div class="mt-1 font-semibold text-slate-900 dark:text-white">
                      {{ claim.expected_job_hours ?? "—" }}
                    </div>
                  </div>
                  <div class="rounded-2xl bg-slate-50 px-3 py-3 dark:bg-slate-900">
                    <div class="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
                      Driving
                    </div>
                    <div class="mt-1 font-semibold text-slate-900 dark:text-white">
                      {{ claim.expected_driving_hours ?? "—" }}
                    </div>
                  </div>
                  <div class="rounded-2xl bg-slate-50 px-3 py-3 dark:bg-slate-900">
                    <div class="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
                      Accepted
                    </div>
                    <div class="mt-1 font-semibold text-slate-900 dark:text-white">
                      {{ claim.accepted_hours ?? "—" }}
                    </div>
                  </div>
                </div>
              </div>

              <div class="mt-5 grid gap-3 lg:grid-cols-[20rem_1fr_auto]">
                <div v-if="(claim.payment_basis || 'hourly') === 'hourly'" class="grid grid-cols-2 gap-2">
                  <label class="block">
                    <span class="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">Work hours</span>
                    <input
                      v-model="claimEdit(claim).accepted_work_hours"
                      type="number"
                      min="0"
                      step="0.25"
                      class="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                      @input="claimEdit(claim).accepted_work_hours_touched = true"
                    />
                  </label>
                  <label class="block">
                    <span class="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">Driving hours</span>
                    <input
                      v-model="claimEdit(claim).accepted_driving_hours"
                      type="number"
                      min="0"
                      step="0.25"
                      class="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                      @input="claimEdit(claim).accepted_driving_hours_touched = true"
                    />
                  </label>
                </div>
                <label v-else class="block">
                  <span class="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    Accepted amount
                  </span>
                  <input
                    v-model="claimEdit(claim).accepted_amount"
                    type="number"
                    min="0"
                    step="0.01"
                    class="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                  />
                </label>
                <label class="block">
                  <span class="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    Review Notes
                  </span>
                  <input
                    v-model="claimEdit(claim).override_notes"
                    type="text"
                    class="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-sky-400 focus:outline-none focus:ring-4 focus:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:border-sky-500 dark:focus:ring-sky-500/20"
                    placeholder="Reason for override or rejection"
                  />
                </label>
                <div class="grid gap-2 sm:grid-cols-2 xl:grid-cols-5 xl:min-w-[34rem] lg:self-end">
                  <button
                    v-if="claimIsClaimingLess(claim)"
                    type="button"
                    class="btn accent"
                    :disabled="savingClaimIds.has(claim.id)"
                    title="Accept the lower submitted hours with review note OK"
                    @click.prevent="acceptLowerClaim(claim)"
                  >
                    Accept lower
                  </button>
                  <button
                    v-else
                    type="button"
                    class="btn"
                    :disabled="savingClaimIds.has(claim.id)"
                    @click.prevent="updateClaim(claim, 'accepted')"
                  >
                    Accept
                  </button>
                  <button
                    type="button"
                    class="btn accent"
                    :disabled="savingClaimIds.has(claim.id)"
                    @click.prevent="updateClaim(claim, 'overridden')"
                  >
                    Override
                  </button>
                  <button
                    type="button"
                    class="btn"
                    :disabled="savingClaimIds.has(claim.id)"
                    @click.prevent="updateClaim(claim, 'rejected')"
                  >
                    Reject
                  </button>
                  <button
                    type="button"
                    class="btn"
                    :disabled="savingClaimIds.has(claim.id)"
                    @click.prevent="updateClaim(claim, 'excluded')"
                  >
                    Exclude
                  </button>
                  <button
                    v-if="claimHasLockedReviewDecision(claim)"
                    type="button"
                    class="btn border-rose-300 text-rose-700 dark:border-rose-800 dark:text-rose-300"
                    :disabled="savingClaimIds.has(claim.id)"
                    title="Clear this individual decision and allow a future claim rebuild to recalculate it"
                    @click.prevent="resetClaimDecision(claim)"
                  >
                    Reset decision
                  </button>
                </div>
              </div>
              </article>
            </section>
          </div>
        </div>
      </section>
    </template>

    <template v-else-if="workspace === 'final'">
      <section
        class="box border border-slate-200 dark:border-slate-700 dark:bg-slate-900/70"
      >
        <div
          class="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between"
        >
          <div>
            <h2 class="text-2xl font-semibold text-slate-900 dark:text-white">
              Final Payroll
            </h2>
            <p class="mt-1 max-w-3xl text-sm text-slate-500 dark:text-slate-400">
              Sorted payroll summary for the selected run, grouped by staff name
              and approved hours at each rate of pay.
            </p>
          </div>
          <div class="flex flex-col gap-2 sm:flex-row">
            <button
              v-if="activeRun?.accountant_export_download_url && accountantPreflight.exportable"
              type="button"
              class="btn"
              :disabled="downloadingAccountantWorkbook"
              @click.prevent="downloadAccountantWorkbook()"
            >
              {{ downloadingAccountantWorkbook ? "Preparing XLSX..." : "Export Accountant Workbook" }}
            </button>
            <button
              v-else-if="activeRun?.accountant_export_download_url"
              class="btn border-amber-400 text-amber-800 dark:border-amber-600 dark:text-amber-200"
              type="button"
              :disabled="downloadingAccountantWorkbook"
              :title="accountantPreflight.errors.join('\n')"
              @click.prevent="downloadAccountantWorkbook(true)"
            >
              {{ downloadingAccountantWorkbook ? "Preparing..." : "⚠ Export Draft Workbook" }}
            </button>
            <button
              class="btn"
              :disabled="loadingRuns || loadingRun"
              title="Reload server data without discarding unresolved local edits"
              @click.prevent="refreshRunSafely"
            >
              Refresh Run
            </button>
          </div>
        </div>

        <div
          v-if="!activeRun"
          class="mt-6 rounded-2xl border border-dashed border-slate-300 px-4 py-8 text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400"
        >
          Select or create a payroll run before reviewing the final payroll summary.
        </div>

        <div v-else class="mt-6 space-y-6">
          <div
            v-if="!accountantPreflight.exportable"
            class="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/20 dark:text-amber-100"
          >
            <div class="font-semibold">Accountant export is blocked</div>
            <button type="button" class="mt-2 font-semibold underline" @click="exportBlockersOpen = true">Review all blockers and correct them</button>
            <ul class="mt-2 list-disc space-y-1 pl-5">
              <li v-for="error in accountantPreflight.errors" :key="error">
                {{ error }}
              </li>
            </ul>
          </div>
          <div class="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <div class="rounded-3xl border border-slate-200 bg-white px-5 py-4 dark:border-slate-800 dark:bg-slate-950/80">
              <div class="text-[11px] font-medium uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">
                Approved Claims
              </div>
              <div class="mt-2 text-3xl font-semibold text-slate-900 dark:text-white">
                {{ acceptedClaimsCount }}
              </div>
            </div>
            <div class="rounded-3xl border border-slate-200 bg-white px-5 py-4 dark:border-slate-800 dark:bg-slate-950/80">
              <div class="text-[11px] font-medium uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">
                Staff
              </div>
              <div class="mt-2 text-3xl font-semibold text-slate-900 dark:text-white">
                {{ payrollSummaryRows.length }}
              </div>
            </div>
            <div class="rounded-3xl border border-slate-200 bg-white px-5 py-4 dark:border-slate-800 dark:bg-slate-950/80 sm:col-span-2">
              <div class="text-[11px] font-medium uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">
                Approved Hours
              </div>
              <div class="mt-2 text-3xl font-semibold text-slate-900 dark:text-white">
                {{ acceptedHoursTotal }}
              </div>
            </div>
          </div>

          <div
            v-if="payrollSummaryRows.length === 0"
            class="rounded-2xl border border-dashed border-slate-300 px-4 py-8 text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400"
          >
            No approved claims are available for this run yet. Approve claims in
            Claim Approval before using the final payroll summary.
          </div>

          <div v-else class="space-y-4">
            <article
              v-for="row in payrollSummaryRows"
              :key="row.staff_name"
              class="rounded-3xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-950/80 lg:hidden"
            >
              <div class="flex items-start justify-between gap-3">
                <div>
                  <div class="text-lg font-semibold text-slate-900 dark:text-white">
                    <RouterLink
                      v-if="row.staff_id"
                      :to="{ name: 'staff-view', params: { id: row.staff_id } }"
                      class="transition hover:text-sky-700 hover:underline dark:hover:text-sky-300"
                    >
                      {{ row.staff_name }}
                    </RouterLink>
                    <span v-else>{{ row.staff_name }}</span>
                  </div>
                  <div class="mt-1 text-sm text-slate-500 dark:text-slate-400">
                    {{ row.claims_count }} approved claims
                  </div>
                </div>
                <div class="text-right">
                  <div class="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    Total
                  </div>
                  <div class="mt-1 text-lg font-semibold text-slate-900 dark:text-white">
                    {{ formatHoursValue(row.total_hours) }} h
                  </div>
                </div>
              </div>

              <div class="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-5">
                <div
                  v-for="bucket in payrollSummaryBuckets"
                  :key="`${row.staff_name}-${bucket.key}`"
                  class="rounded-2xl bg-slate-50 px-3 py-3 dark:bg-slate-900"
                >
                  <div class="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    {{ bucket.label }}
                  </div>
                  <div class="mt-1 font-semibold text-slate-900 dark:text-white">
                    {{ bucket.key === "OTHER" ? formatOtherSummary(row) : `${formatHoursValue(row[bucket.key])} h` }}
                  </div>
                </div>
              </div>

              <div class="mt-4 grid grid-cols-3 gap-3 rounded-2xl bg-sky-50 p-3 dark:bg-sky-950/20">
                <div>
                  <div class="text-[10px] uppercase tracking-wide text-sky-700 dark:text-sky-300">Gross estimate</div>
                  <div class="mt-1 font-semibold text-slate-900 dark:text-white">{{ formatEstimatedMoney(row.estimate?.gross_pay) }}</div>
                </div>
                <div>
                  <div class="text-[10px] uppercase tracking-wide text-sky-700 dark:text-sky-300">Estimated to pay</div>
                  <div class="mt-1 font-semibold text-sky-900 dark:text-sky-100">{{ formatEstimatedMoney(row.estimate?.estimated_net_pay) }}</div>
                </div>
                <div>
                  <div class="text-[10px] uppercase tracking-wide text-sky-700 dark:text-sky-300">Employer cost</div>
                  <div class="mt-1 font-semibold text-slate-900 dark:text-white">{{ formatEstimatedMoney(row.estimate?.total_employer_cost) }}</div>
                </div>
              </div>

              <div class="mt-4 flex flex-wrap justify-end gap-2">
                <button
                  type="button"
                  class="btn"
                  :class="row.payroll_checked ? 'border-emerald-400 bg-emerald-50 text-emerald-800 dark:border-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-200' : ''"
                  :disabled="!row.staff_id || savingStaffCheckIds.has(row.staff_id)"
                  :title="row.payroll_checked ? `Checked${row.payroll_checked_by ? ` by ${row.payroll_checked_by}` : ''}${row.payroll_checked_at ? ` on ${formatDateTime(row.payroll_checked_at)}` : ''}. Click to undo.` : 'Confirm that this staff payroll total has been checked'"
                  @click.prevent="setPayrollStaffChecked(row)"
                >
                  {{ row.payroll_checked ? "✓ Checked" : "Mark Checked" }}
                </button>
                <button
                  type="button"
                  class="btn"
                  @click.prevent="togglePayrollSummaryRow(row.staff_name)"
                >
                  {{ payrollSummaryRowExpanded(row.staff_name) ? "Hide Claims" : "Show Claims" }}
                </button>
              </div>

              <div
                v-if="payrollSummaryRowExpanded(row.staff_name)"
                class="mt-3 overflow-hidden rounded-xl border border-slate-200 dark:border-slate-800"
              >
                <div v-if="row.estimate" class="grid gap-2 border-b border-slate-200 bg-sky-50 px-3 py-3 text-xs dark:border-slate-800 dark:bg-sky-950/20 sm:grid-cols-3">
                  <span>Wages <strong>{{ formatEstimatedMoney(row.estimate.base_wages) }}</strong></span>
                  <span>Holiday pay <strong>{{ formatEstimatedMoney(row.estimate.holiday_pay) }}</strong></span>
                  <span>Deductions <strong>{{ formatEstimatedMoney(row.estimate.estimated_deductions) }}</strong></span>
                  <span>Employer NI <strong>{{ formatEstimatedMoney(row.estimate.employer_ni) }}</strong></span>
                  <span>Employer pension <strong>{{ formatEstimatedMoney(row.estimate.employer_pension) }}</strong></span>
                  <span>{{ deductionMethodLabel(row.estimate.deduction_method) }}</span>
                  <p v-for="warning in row.estimate.warnings" :key="warning" class="text-amber-800 dark:text-amber-200 sm:col-span-3">{{ warning }}</p>
                </div>
                <div class="flex flex-wrap items-center gap-x-4 gap-y-1 bg-slate-100 px-3 py-2 text-xs dark:bg-slate-900">
                  <span class="font-semibold text-slate-700 dark:text-slate-200">Rate totals</span>
                  <span
                    v-for="total in payrollSummaryRateTotals(row)"
                    :key="`${row.staff_name}-mobile-total-${total.key}`"
                    class="whitespace-nowrap text-slate-600 dark:text-slate-300"
                  >
                    {{ total.label }}
                    <strong class="ml-1 text-slate-900 dark:text-white">{{ formatPayrollRateTotal(total) }}</strong>
                  </span>
                </div>
                <div class="overflow-x-auto">
                  <table class="min-w-[760px] w-full text-xs">
                    <thead class="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500 dark:bg-slate-950/80 dark:text-slate-400">
                      <tr>
                        <th class="px-2 py-1.5 text-left">Date</th>
                        <th class="px-2 py-1.5 text-left">Job Reference</th>
                        <th class="px-2 py-1.5 text-left">Role</th>
                        <th class="px-2 py-1.5 text-left">Rate</th>
                        <th class="px-2 py-1.5 text-right">Claimed</th>
                        <th class="px-2 py-1.5 text-right">Expected</th>
                        <th class="px-2 py-1.5 text-right">Work</th>
                        <th class="px-2 py-1.5 text-right">Drive</th>
                        <th class="px-2 py-1.5 text-right">Accepted</th>
                      </tr>
                    </thead>
                    <tbody class="divide-y divide-slate-100 bg-white dark:divide-slate-800 dark:bg-slate-950/40">
                      <tr v-for="claim in row.claims" :key="`${row.staff_name}-${claim.id}`">
                        <td class="whitespace-nowrap px-2 py-1.5 text-slate-600 dark:text-slate-300">{{ formatDate(claim.date) }}</td>
                        <td class="max-w-[14rem] truncate px-2 py-1.5 font-medium text-slate-900 dark:text-white">
                          <RouterLink
                            v-if="claim.reference_id"
                            :to="{ name: 'reference-view', params: { id: claim.reference_id } }"
                            class="hover:text-sky-700 hover:underline dark:hover:text-sky-300"
                          >{{ claim.reference_code }}</RouterLink>
                          <span v-else>{{ claim.reference_code }}</span>
                        </td>
                        <td class="px-2 py-1.5 text-slate-600 dark:text-slate-300">{{ claim.product_label }}</td>
                        <td class="whitespace-nowrap px-2 py-1.5 text-slate-600 dark:text-slate-300" :title="claim.rate_inferred ? 'Rate inferred from the matched product' : ''">{{ claim.rate_code }}{{ claim.rate_inferred ? " *" : "" }}</td>
                        <td class="whitespace-nowrap px-2 py-1.5 text-right">{{ formatHoursValue(claim.claimed_hours) }}</td>
                        <td class="whitespace-nowrap px-2 py-1.5 text-right">{{ formatHoursValue(claim.expected_total_hours) }}</td>
                        <td class="whitespace-nowrap px-2 py-1.5 text-right">{{ formatHoursValue(claim.work_hours) }}</td>
                        <td class="whitespace-nowrap px-2 py-1.5 text-right">{{ formatHoursValue(claim.driving_hours) }}</td>
                        <td class="whitespace-nowrap px-2 py-1.5 text-right font-semibold">{{ formatAcceptedClaimValue(claim) }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </article>

            <div
              class="hidden overflow-hidden rounded-3xl border border-slate-200 dark:border-slate-800 lg:block"
            >
              <table class="w-full text-sm">
                <thead class="bg-slate-50 dark:bg-slate-900/80">
                  <tr class="text-left text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    <th class="px-4 py-3">Staff</th>
                    <th class="px-4 py-3 text-right">FA</th>
                    <th class="px-4 py-3 text-right">Driving</th>
                    <th class="px-4 py-3 text-right">PTS</th>
                    <th class="px-4 py-3 text-right">EMT</th>
                    <th class="px-4 py-3 text-right">Other</th>
                    <th class="px-4 py-3 text-right">Total</th>
                    <th class="px-4 py-3 text-right">Gross Est.</th>
                    <th class="px-4 py-3 text-right">Est. To Pay</th>
                    <th class="px-4 py-3 text-right">Employer Cost</th>
                    <th class="px-4 py-3 text-right">Claims</th>
                    <th class="px-4 py-3 text-right">Checked</th>
                  </tr>
                </thead>
                <tbody class="bg-white dark:bg-slate-900/60">
                  <template
                    v-for="row in payrollSummaryRows"
                    :key="`${row.staff_name}-desktop`"
                  >
                  <tr
                    class="border-t border-slate-100 align-top odd:bg-white even:bg-slate-50/70 dark:border-slate-800 dark:odd:bg-slate-900/60 dark:even:bg-slate-950/80"
                  >
                    <td class="px-4 py-3 font-medium text-slate-900 dark:text-white">
                      <div class="flex items-center gap-3">
                        <button
                          type="button"
                          class="btn px-3 py-1 text-xs"
                          @click.prevent="togglePayrollSummaryRow(row.staff_name)"
                        >
                          {{ payrollSummaryRowExpanded(row.staff_name) ? "Hide" : "Show" }}
                        </button>
                        <div>
                          <RouterLink
                            v-if="row.staff_id"
                            :to="{ name: 'staff-view', params: { id: row.staff_id } }"
                            class="transition hover:text-sky-700 hover:underline dark:hover:text-sky-300"
                          >
                            {{ row.staff_name }}
                          </RouterLink>
                          <span v-else>{{ row.staff_name }}</span>
                        </div>
                      </div>
                    </td>
                    <td class="px-4 py-3 text-right text-slate-900 dark:text-white">
                      {{ formatHoursValue(row.FA) }}
                    </td>
                    <td class="px-4 py-3 text-right text-slate-900 dark:text-white">
                      {{ formatHoursValue(row.DRIVING) }}
                    </td>
                    <td class="px-4 py-3 text-right text-slate-900 dark:text-white">
                      {{ formatHoursValue(row.PTS) }}
                    </td>
                    <td class="px-4 py-3 text-right text-slate-900 dark:text-white">
                      {{ formatHoursValue(row.EMT) }}
                    </td>
                    <td class="px-4 py-3 text-right text-slate-900 dark:text-white">
                      {{ formatOtherSummary(row) }}
                    </td>
                    <td class="px-4 py-3 text-right font-semibold text-slate-900 dark:text-white">
                      {{ formatHoursValue(row.total_hours) }}
                    </td>
                    <td class="whitespace-nowrap px-4 py-3 text-right text-slate-900 dark:text-white">
                      {{ formatEstimatedMoney(row.estimate?.gross_pay) }}
                    </td>
                    <td class="whitespace-nowrap px-4 py-3 text-right font-semibold text-sky-800 dark:text-sky-200">
                      {{ formatEstimatedMoney(row.estimate?.estimated_net_pay) }}
                    </td>
                    <td class="whitespace-nowrap px-4 py-3 text-right text-slate-900 dark:text-white">
                      {{ formatEstimatedMoney(row.estimate?.total_employer_cost) }}
                    </td>
                    <td class="px-4 py-3 text-right text-slate-500 dark:text-slate-400">
                      {{ row.claims_count }}
                    </td>
                    <td class="px-4 py-3 text-right">
                      <button
                        type="button"
                        class="btn whitespace-nowrap px-3 py-1 text-xs"
                        :class="row.payroll_checked ? 'border-emerald-400 bg-emerald-50 text-emerald-800 dark:border-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-200' : ''"
                        :disabled="!row.staff_id || savingStaffCheckIds.has(row.staff_id)"
                        :title="row.payroll_checked ? `Checked${row.payroll_checked_by ? ` by ${row.payroll_checked_by}` : ''}${row.payroll_checked_at ? ` on ${formatDateTime(row.payroll_checked_at)}` : ''}. Click to undo.` : 'Confirm that this staff payroll total has been checked'"
                        @click.prevent="setPayrollStaffChecked(row)"
                      >
                        {{ row.payroll_checked ? "✓ Checked" : "Mark Checked" }}
                      </button>
                    </td>
                  </tr>
                  <tr
                    v-if="payrollSummaryRowExpanded(row.staff_name)"
                    class="border-t border-slate-100 bg-slate-50/70 dark:border-slate-800 dark:bg-slate-950/80"
                  >
                    <td colspan="12" class="p-0">
                      <div v-if="row.estimate" class="flex flex-wrap items-center gap-x-5 gap-y-1 border-b border-sky-200 bg-sky-50 px-3 py-2 text-xs dark:border-sky-900/50 dark:bg-sky-950/20">
                        <span class="font-semibold text-sky-900 dark:text-sky-100">Pay estimate</span>
                        <span>Wages <strong>{{ formatEstimatedMoney(row.estimate.base_wages) }}</strong></span>
                        <span>Holiday <strong>{{ formatEstimatedMoney(row.estimate.holiday_pay) }}</strong></span>
                        <span>Deductions <strong>{{ formatEstimatedMoney(row.estimate.estimated_deductions) }}</strong></span>
                        <span>Employer NI <strong>{{ formatEstimatedMoney(row.estimate.employer_ni) }}</strong></span>
                        <span>Employer pension <strong>{{ formatEstimatedMoney(row.estimate.employer_pension) }}</strong></span>
                        <span class="text-slate-500 dark:text-slate-400">{{ deductionMethodLabel(row.estimate.deduction_method) }}</span>
                        <span v-if="row.estimate.unpriced_hours" class="font-semibold text-amber-800 dark:text-amber-200">{{ formatHoursValue(row.estimate.unpriced_hours) }} unpriced hours</span>
                        <span v-for="warning in row.estimate.warnings" :key="warning" class="basis-full text-amber-800 dark:text-amber-200">{{ warning }}</span>
                      </div>
                      <div class="flex flex-wrap items-center gap-x-5 gap-y-1 border-b border-slate-200 bg-slate-100 px-3 py-2 text-xs dark:border-slate-800 dark:bg-slate-900">
                        <span class="font-semibold text-slate-700 dark:text-slate-200">Rate totals</span>
                        <span
                          v-for="total in payrollSummaryRateTotals(row)"
                          :key="`${row.staff_name}-desktop-total-${total.key}`"
                          class="whitespace-nowrap text-slate-600 dark:text-slate-300"
                        >
                          {{ total.label }}
                          <strong class="ml-1 text-slate-900 dark:text-white">{{ formatPayrollRateTotal(total) }}</strong>
                        </span>
                      </div>
                      <table class="w-full table-fixed text-xs">
                        <thead class="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500 dark:bg-slate-950/70 dark:text-slate-400">
                          <tr>
                            <th class="w-28 px-2 py-1.5 text-left">Date</th>
                            <th class="px-2 py-1.5 text-left">Job Reference</th>
                            <th class="w-28 px-2 py-1.5 text-left">Role</th>
                            <th class="w-20 px-2 py-1.5 text-left">Rate</th>
                            <th class="w-20 px-2 py-1.5 text-right">Claimed</th>
                            <th class="w-20 px-2 py-1.5 text-right">Expected</th>
                            <th class="w-20 px-2 py-1.5 text-right">Work</th>
                            <th class="w-20 px-2 py-1.5 text-right">Drive</th>
                            <th class="w-24 px-2 py-1.5 text-right">Accepted</th>
                          </tr>
                        </thead>
                        <tbody class="divide-y divide-slate-100 bg-white dark:divide-slate-800 dark:bg-slate-950/30">
                          <tr
                            v-for="claim in row.claims"
                            :key="`${row.staff_name}-desktop-${claim.id}`"
                            class="hover:bg-sky-50/60 dark:hover:bg-sky-950/20"
                          >
                            <td class="whitespace-nowrap px-2 py-1.5 text-slate-600 dark:text-slate-300">{{ formatDate(claim.date) }}</td>
                            <td class="truncate px-2 py-1.5 font-medium text-slate-900 dark:text-white" :title="claim.reference_code">
                              <RouterLink
                                v-if="claim.reference_id"
                                :to="{ name: 'reference-view', params: { id: claim.reference_id } }"
                                class="hover:text-sky-700 hover:underline dark:hover:text-sky-300"
                              >{{ claim.reference_code }}</RouterLink>
                              <span v-else>{{ claim.reference_code }}</span>
                            </td>
                            <td class="truncate px-2 py-1.5 text-slate-600 dark:text-slate-300" :title="claim.product_label">{{ claim.product_label }}</td>
                            <td class="whitespace-nowrap px-2 py-1.5 text-slate-600 dark:text-slate-300" :title="claim.rate_inferred ? 'Rate inferred from the matched product' : ''">{{ claim.rate_code }}{{ claim.rate_inferred ? " *" : "" }}</td>
                            <td class="whitespace-nowrap px-2 py-1.5 text-right">{{ formatHoursValue(claim.claimed_hours) }}</td>
                            <td class="whitespace-nowrap px-2 py-1.5 text-right">{{ formatHoursValue(claim.expected_total_hours) }}</td>
                            <td class="whitespace-nowrap px-2 py-1.5 text-right">{{ formatHoursValue(claim.work_hours) }}</td>
                            <td class="whitespace-nowrap px-2 py-1.5 text-right">{{ formatHoursValue(claim.driving_hours) }}</td>
                            <td class="whitespace-nowrap px-2 py-1.5 text-right font-semibold text-slate-900 dark:text-white">{{ formatAcceptedClaimValue(claim) }}</td>
                          </tr>
                        </tbody>
                      </table>
                    </td>
                  </tr>
                  </template>
                </tbody>
              </table>
            </div>

            <section class="rounded-3xl border border-sky-200 bg-sky-50/70 p-5 dark:border-sky-900/60 dark:bg-sky-950/20">
              <div class="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h3 class="text-lg font-semibold text-sky-950 dark:text-sky-100">Estimated payroll cost</h3>
                  <p class="mt-1 text-sm text-sky-800/80 dark:text-sky-200/80">Planning estimate using approved claims and the current payroll assumptions.</p>
                </div>
                <div class="text-right">
                  <div class="text-xs uppercase tracking-wide text-sky-700 dark:text-sky-300">Total including employer contributions</div>
                  <div class="mt-1 text-3xl font-semibold text-sky-950 dark:text-white">{{ formatEstimatedMoney(activeRun.cost_estimate?.totals?.total_employer_cost) }}</div>
                </div>
              </div>
              <div class="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <div class="rounded-2xl bg-white/80 p-4 dark:bg-slate-950/60">
                  <div class="text-xs uppercase tracking-wide text-slate-500">Employee wages</div>
                  <div class="mt-1 text-xl font-semibold">{{ formatEstimatedMoney(activeRun.cost_estimate?.totals?.employee_wages) }}</div>
                </div>
                <div v-if="activeRun.cost_estimate?.totals?.contractor_payments" class="rounded-2xl bg-white/80 p-4 dark:bg-slate-950/60">
                  <div class="text-xs uppercase tracking-wide text-slate-500">Contractor payments</div>
                  <div class="mt-1 text-xl font-semibold">{{ formatEstimatedMoney(activeRun.cost_estimate.totals.contractor_payments) }}</div>
                </div>
                <div class="rounded-2xl bg-white/80 p-4 dark:bg-slate-950/60">
                  <div class="text-xs uppercase tracking-wide text-slate-500">Holiday pay</div>
                  <div class="mt-1 text-xl font-semibold">{{ formatEstimatedMoney(activeRun.cost_estimate?.totals?.holiday_pay) }}</div>
                </div>
                <div class="rounded-2xl bg-white/80 p-4 dark:bg-slate-950/60">
                  <div class="text-xs uppercase tracking-wide text-slate-500">Gross payroll</div>
                  <div class="mt-1 text-xl font-semibold">{{ formatEstimatedMoney(activeRun.cost_estimate?.totals?.gross_pay) }}</div>
                </div>
                <div class="rounded-2xl bg-white/80 p-4 dark:bg-slate-950/60">
                  <div class="text-xs uppercase tracking-wide text-slate-500">Estimated staff take-home</div>
                  <div class="mt-1 text-xl font-semibold text-sky-800 dark:text-sky-200">{{ formatEstimatedMoney(activeRun.cost_estimate?.totals?.estimated_net_pay) }}</div>
                </div>
                <div class="rounded-2xl bg-white/80 p-4 dark:bg-slate-950/60">
                  <div class="text-xs uppercase tracking-wide text-slate-500">Employee deductions</div>
                  <div class="mt-1 text-xl font-semibold">{{ formatEstimatedMoney(activeRun.cost_estimate?.totals?.estimated_deductions) }}</div>
                </div>
                <div class="rounded-2xl bg-white/80 p-4 dark:bg-slate-950/60">
                  <div class="text-xs uppercase tracking-wide text-slate-500">Employer NI</div>
                  <div class="mt-1 text-xl font-semibold">{{ formatEstimatedMoney(activeRun.cost_estimate?.totals?.employer_ni) }}</div>
                </div>
                <div class="rounded-2xl bg-white/80 p-4 dark:bg-slate-950/60">
                  <div class="text-xs uppercase tracking-wide text-slate-500">Employer pension</div>
                  <div class="mt-1 text-xl font-semibold">{{ formatEstimatedMoney(activeRun.cost_estimate?.totals?.employer_pension) }}</div>
                </div>
                <div v-if="activeRun.cost_estimate?.totals?.unpriced_hours" class="rounded-2xl border border-amber-200 bg-amber-50 p-4 dark:border-amber-900/50 dark:bg-amber-950/20">
                  <div class="text-xs uppercase tracking-wide text-amber-700 dark:text-amber-300">Unpriced hours</div>
                  <div class="mt-1 text-xl font-semibold text-amber-900 dark:text-amber-100">{{ formatHoursValue(activeRun.cost_estimate.totals.unpriced_hours) }} h</div>
                </div>
                <div v-if="activeRun.cost_estimate?.totals?.inferred_rate_hours" class="rounded-2xl border border-amber-200 bg-amber-50 p-4 dark:border-amber-900/50 dark:bg-amber-950/20">
                  <div class="text-xs uppercase tracking-wide text-amber-700 dark:text-amber-300">Rates inferred from product</div>
                  <div class="mt-1 text-xl font-semibold text-amber-900 dark:text-amber-100">{{ formatHoursValue(activeRun.cost_estimate.totals.inferred_rate_hours) }} h</div>
                </div>
              </div>
              <ul class="mt-4 list-disc space-y-1 pl-5 text-xs text-sky-900/80 dark:text-sky-100/80">
                <li v-for="warning in activeRun.cost_estimate?.warnings || []" :key="warning">{{ warning }}</li>
              </ul>
            </section>
          </div>
        </div>
      </section>
    </template>
    <template v-else-if="workspace === 'unclaimed'">
      <section
        class="box border border-slate-200 dark:border-slate-700 dark:bg-slate-900/70"
      >
        <div class="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 class="text-2xl font-semibold text-slate-900 dark:text-white">
              Missing Claims Check
            </h2>
            <p class="mt-1 max-w-4xl text-sm text-slate-500 dark:text-slate-400">
              Checks submitted claims by staff, job reference and work date. Role and shift times do not affect receipt. Verify payable hours separately in Claim Approval.
            </p>
          </div>
          <div class="flex flex-wrap items-center gap-2">
            <button
              type="button"
              class="btn"
              :disabled="loadingClaimRepairs || !activeRun"
              @click.prevent="openClaimRepairReview"
            >
              {{ loadingClaimRepairs ? "Scanning..." : "Review matching problems" }}
            </button>
            <button type="button" class="btn" :disabled="!activeRun" @click.prevent="workspace = 'claims'">Verify hours in Claim Approval</button>
            <button
              type="button"
              class="btn"
              :disabled="loadingUnclaimedWork || !activeRun"
              @click.prevent="loadUnclaimedWork"
            >
              {{ loadingUnclaimedWork ? "Checking..." : "Refresh Check" }}
            </button>
          </div>
        </div>

        <div
          v-if="!activeRun"
          class="mt-6 rounded-2xl border border-dashed border-slate-300 px-4 py-8 text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400"
        >
          Select a payroll run to check its pay period.
        </div>
        <div
          v-else-if="unclaimedWorkError"
          class="mt-6 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-4 text-sm text-rose-700 dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-300"
        >
          {{ unclaimedWorkError }}
        </div>
        <div v-else class="mt-6 space-y-6">
          <div class="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
            <div class="rounded-2xl border border-slate-200 bg-white px-5 py-4 dark:border-slate-800 dark:bg-slate-950/80">
              <div class="text-[11px] font-medium uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">Scheduled</div>
              <div class="mt-2 text-3xl font-semibold text-slate-900 dark:text-white">{{ unclaimedWorkReport.summary.scheduled }}</div>
            </div>
            <div class="rounded-2xl border border-emerald-200 bg-emerald-50 px-5 py-4 dark:border-emerald-900/40 dark:bg-emerald-950/20">
              <div class="text-[11px] font-medium uppercase tracking-[0.2em] text-emerald-700 dark:text-emerald-300">Claim Found</div>
              <div class="mt-2 text-3xl font-semibold text-emerald-900 dark:text-emerald-100">{{ unclaimedWorkReport.summary.claimed }}</div>
            </div>
            <div
              class="rounded-2xl border px-5 py-4"
              :class="unclaimedWorkReport.summary.missing_employees
                ? 'border-rose-200 bg-rose-50 dark:border-rose-900/40 dark:bg-rose-950/20'
                : 'border-emerald-200 bg-emerald-50 dark:border-emerald-900/40 dark:bg-emerald-950/20'"
            >
              <div class="text-[11px] font-medium uppercase tracking-[0.2em] text-slate-600 dark:text-slate-300">Missing Employees</div>
              <div class="mt-2 text-3xl font-semibold text-slate-900 dark:text-white">{{ unclaimedWorkReport.summary.missing_employees }}</div>
            </div>
            <div class="rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4 dark:border-amber-900/40 dark:bg-amber-950/20">
              <div class="text-[11px] font-medium uppercase tracking-[0.2em] text-amber-700 dark:text-amber-300">Awaiting Timesheets</div>
              <div class="mt-2 text-3xl font-semibold text-amber-900 dark:text-amber-100">{{ unclaimedWorkReport.summary.missing_timesheets }}</div>
            </div>
            <div class="rounded-2xl border border-sky-200 bg-sky-50 px-5 py-4 dark:border-sky-900/40 dark:bg-sky-950/20">
              <div class="text-[11px] font-medium uppercase tracking-[0.2em] text-sky-700 dark:text-sky-300">Contractor Jobs</div>
              <div class="mt-2 text-3xl font-semibold text-sky-900 dark:text-sky-100">{{ unclaimedWorkReport.summary.contractor_assignments }}</div>
            </div>
          </div>

          <div v-if="loadingUnclaimedWork" class="rounded-2xl border border-dashed border-slate-300 px-4 py-8 text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400">
            Checking accepted jobs and submitted claims for {{ periodLabel(activeRun) }}...
          </div>

          <section v-else class="space-y-3">
            <div>
              <h3 class="text-lg font-semibold text-slate-900 dark:text-white">Employees with no claim</h3>
              <p class="mt-1 text-sm text-slate-500 dark:text-slate-400">One row per employee and Neptune job date. Multiple roles on the same job are combined.</p>
            </div>
            <div
              v-if="missingEmployeeWork.length === 0"
              class="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-5 text-sm font-medium text-emerald-800 dark:border-emerald-900/40 dark:bg-emerald-950/20 dark:text-emerald-200"
            >
              {{ missingTimesheetWork.length
                ? 'Every outstanding employee assignment is categorised as awaiting a timesheet.'
                : 'Every employee assignment in this pay period has a submitted claim.' }}
            </div>
            <div v-else class="overflow-x-auto rounded-2xl border border-rose-200 dark:border-rose-900/40">
              <table class="min-w-[1120px] w-full text-sm">
                <thead class="bg-rose-50 dark:bg-rose-950/30">
                  <tr class="text-left text-xs uppercase tracking-wide text-slate-600 dark:text-slate-300">
                    <th class="px-4 py-3">Staff</th>
                    <th class="px-4 py-3">Date</th>
                    <th class="px-4 py-3">Job Reference</th>
                    <th class="px-4 py-3">Scheduled</th>
                    <th class="px-4 py-3">Role</th>
                    <th class="px-4 py-3 text-right">Expected</th>
                    <th class="px-4 py-3">Status</th>
                    <th class="px-4 py-3 text-right">Action</th>
                  </tr>
                </thead>
                <tbody class="bg-white dark:bg-slate-900/60">
                  <tr v-for="record in missingEmployeeWork" :key="`${record.job_date_id}-${record.staff_id}`" class="border-t border-rose-100 dark:border-rose-900/30">
                    <td class="px-4 py-3 font-medium text-slate-900 dark:text-white">
                      <RouterLink :to="{ name: 'staff-view', params: { id: record.staff_id }, query: { tab: 'employment' } }" class="hover:text-sky-700 hover:underline dark:hover:text-sky-300">{{ record.staff_name }}</RouterLink>
                    </td>
                    <td class="whitespace-nowrap px-4 py-3">{{ formatDate(record.date) }}</td>
                    <td class="px-4 py-3">
                      <RouterLink :to="{ name: 'reference-view', params: { id: record.reference_id } }" class="font-medium text-sky-700 hover:underline dark:text-sky-300">{{ record.reference_code }}</RouterLink>
                      <div class="max-w-xs truncate text-xs text-slate-500 dark:text-slate-400">{{ record.reference_name }}</div>
                    </td>
                    <td class="whitespace-nowrap px-4 py-3">{{ formatScheduledTime(record) }}</td>
                    <td class="px-4 py-3">
                      {{ record.roles.join(', ') }}
                      <div v-if="record.products?.length" class="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                        Product: {{ record.products.join(', ') }}
                      </div>
                    </td>
                    <td class="whitespace-nowrap px-4 py-3 text-right">{{ record.expected_hours === null ? '—' : `${formatHoursValue(record.expected_hours)} h` }}</td>
                    <td class="px-4 py-3">
                      <span class="rounded-full bg-rose-100 px-2.5 py-1 text-xs font-semibold text-rose-700 dark:bg-rose-900/30 dark:text-rose-200">{{ record.possible_claim ? 'Possible claim — check date / match' : 'No claim found' }}</span>
                      <p v-if="record.claim_match_warning" class="mt-2 max-w-xs text-xs text-amber-700 dark:text-amber-300">{{ record.claim_match_warning }}</p>
                      <button v-if="record.possible_claim" type="button" class="mt-2 text-xs font-semibold underline" @click.prevent="openClaimRepairReview">Review matching problems</button>
                      <button v-if="record.possible_claim" type="button" class="mt-2 block text-xs font-semibold underline" @click.prevent="workspace = 'claims'">Confirm job link in Claim Approval</button>
                      <button v-if="record.possible_claim" type="button" class="mt-2 block text-xs font-semibold underline" @click.prevent="workspace = 'timesheets'">Check imported work dates</button>
                    </td>
                    <td class="px-4 py-3 text-right">
                      <button
                        type="button"
                        class="btn whitespace-nowrap"
                        :disabled="activeRun?.status !== 'draft' || categorizingMissingTimesheet(record)"
                        :title="activeRun?.status !== 'draft' ? 'Missing timesheets can only be categorised while this payroll run is in draft.' : 'Move this assignment out of missing claims until a timesheet is submitted.'"
                        @click.prevent="setMissingTimesheetCategory(record, true)"
                      >
                        {{ categorizingMissingTimesheet(record) ? 'Saving...' : 'Timesheet not submitted' }}
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section v-if="!loadingUnclaimedWork && missingTimesheetWork.length" class="space-y-3">
            <div>
              <h3 class="text-lg font-semibold text-slate-900 dark:text-white">Timesheets not submitted — defer to a later pay run</h3>
              <p class="mt-1 text-sm text-slate-500 dark:text-slate-400">Finance has completed the current run for these jobs. Pay them in the next available payroll run after the employee submits a timesheet.</p>
            </div>
            <div
              v-if="unclaimedWorkReport.summary.missing_timesheet_claims_received"
              class="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-800 dark:border-emerald-900/40 dark:bg-emerald-950/20 dark:text-emerald-200"
            >
              {{ unclaimedWorkReport.summary.missing_timesheet_claims_received }} deferred assignment(s) now have a submitted claim. Verify they are included in the appropriate later payroll run.
            </div>
            <div class="overflow-x-auto rounded-2xl border border-amber-200 dark:border-amber-900/40">
              <table class="min-w-[1120px] w-full text-sm">
                <thead class="bg-amber-50 dark:bg-amber-950/30">
                  <tr class="text-left text-xs uppercase tracking-wide text-slate-600 dark:text-slate-300">
                    <th class="px-4 py-3">Staff</th>
                    <th class="px-4 py-3">Date</th>
                    <th class="px-4 py-3">Job Reference</th>
                    <th class="px-4 py-3">Role</th>
                    <th class="px-4 py-3 text-right">Expected</th>
                    <th class="px-4 py-3">Submission</th>
                    <th class="px-4 py-3">Categorised</th>
                    <th class="px-4 py-3 text-right">Action</th>
                  </tr>
                </thead>
                <tbody class="bg-white dark:bg-slate-900/60">
                  <tr v-for="record in missingTimesheetWork" :key="`missing-timesheet-${record.job_date_id}-${record.staff_id}`" class="border-t border-amber-100 dark:border-amber-900/30">
                    <td class="px-4 py-3 font-medium text-slate-900 dark:text-white">
                      <RouterLink :to="{ name: 'staff-view', params: { id: record.staff_id }, query: { tab: 'employment' } }" class="hover:text-sky-700 hover:underline dark:hover:text-sky-300">{{ record.staff_name }}</RouterLink>
                    </td>
                    <td class="whitespace-nowrap px-4 py-3">{{ formatDate(record.date) }}</td>
                    <td class="px-4 py-3">
                      <RouterLink :to="{ name: 'reference-view', params: { id: record.reference_id } }" class="font-medium text-sky-700 hover:underline dark:text-sky-300">{{ record.reference_code }}</RouterLink>
                      <div class="max-w-xs truncate text-xs text-slate-500 dark:text-slate-400">{{ record.reference_name }}</div>
                    </td>
                    <td class="px-4 py-3">
                      {{ record.roles.join(', ') }}
                      <div v-if="record.products?.length" class="mt-0.5 text-xs text-slate-500 dark:text-slate-400">Product: {{ record.products.join(', ') }}</div>
                    </td>
                    <td class="whitespace-nowrap px-4 py-3 text-right">{{ record.expected_hours === null ? '—' : `${formatHoursValue(record.expected_hours)} h` }}</td>
                    <td class="px-4 py-3">
                      <span
                        class="rounded-full px-2.5 py-1 text-xs font-semibold"
                        :class="record.has_claim ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200' : 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200'"
                      >{{ record.has_claim ? 'Timesheet received — verify later run' : 'Awaiting submission' }}</span>
                    </td>
                    <td class="whitespace-nowrap px-4 py-3 text-xs text-slate-500 dark:text-slate-400">
                      <div>{{ record.missing_timesheet_marked_by || 'Finance' }}</div>
                      <div v-if="record.missing_timesheet_marked_at">{{ formatDateTime(record.missing_timesheet_marked_at) }}</div>
                    </td>
                    <td class="px-4 py-3 text-right">
                      <button
                        type="button"
                        class="btn whitespace-nowrap"
                        :disabled="activeRun?.status !== 'draft' || categorizingMissingTimesheet(record)"
                        :title="activeRun?.status !== 'draft' ? 'This category is locked because the payroll run is no longer in draft.' : 'Return this assignment to the normal missing claims check.'"
                        @click.prevent="setMissingTimesheetCategory(record, false)"
                      >
                        {{ categorizingMissingTimesheet(record) ? 'Saving...' : 'Return to missing claims' }}
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section v-if="!loadingUnclaimedWork && contractorWork.length" class="space-y-3">
            <div>
              <h3 class="text-lg font-semibold text-slate-900 dark:text-white">Contractors — invoices outside payroll</h3>
              <p class="mt-1 text-sm text-slate-500 dark:text-slate-400">These assignments are excluded from the missing employee count. A payroll claim against a contractor is highlighted for review.</p>
            </div>
            <div
              v-if="unclaimedWorkReport.summary.contractor_payroll_claims"
              class="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-medium text-amber-800 dark:border-amber-900/40 dark:bg-amber-950/20 dark:text-amber-200"
            >
              {{ unclaimedWorkReport.summary.contractor_payroll_claims }} contractor assignment(s) have a timesheet/payroll claim. Check whether they should invoice instead.
            </div>
            <div class="overflow-x-auto rounded-2xl border border-sky-200 dark:border-sky-900/40">
              <table class="min-w-[900px] w-full text-sm">
                <thead class="bg-sky-50 dark:bg-sky-950/30">
                  <tr class="text-left text-xs uppercase tracking-wide text-slate-600 dark:text-slate-300">
                    <th class="px-4 py-3">Contractor</th>
                    <th class="px-4 py-3">Date</th>
                    <th class="px-4 py-3">Job Reference</th>
                    <th class="px-4 py-3">Role</th>
                    <th class="px-4 py-3">Vehicle</th>
                    <th class="px-4 py-3">Check</th>
                  </tr>
                </thead>
                <tbody class="bg-white dark:bg-slate-900/60">
                  <tr v-for="record in contractorWork" :key="`contractor-${record.job_date_id}-${record.staff_id}`" class="border-t border-sky-100 dark:border-sky-900/30">
                    <td class="px-4 py-3 font-medium text-slate-900 dark:text-white"><RouterLink :to="{ name: 'staff-view', params: { id: record.staff_id }, query: { tab: 'employment' } }" class="hover:text-sky-700 hover:underline dark:hover:text-sky-300">{{ record.staff_name }}</RouterLink></td>
                    <td class="whitespace-nowrap px-4 py-3">{{ formatDate(record.date) }}</td>
                    <td class="px-4 py-3"><RouterLink :to="{ name: 'reference-view', params: { id: record.reference_id } }" class="font-medium text-sky-700 hover:underline dark:text-sky-300">{{ record.reference_code }}</RouterLink><div class="max-w-xs truncate text-xs text-slate-500 dark:text-slate-400">{{ record.reference_name }}</div></td>
                    <td class="px-4 py-3">
                      {{ record.roles.join(', ') }}
                      <div v-if="record.products?.length" class="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                        Product: {{ record.products.join(', ') }}
                      </div>
                    </td>
                    <td class="px-4 py-3">{{ record.vehicles.join(', ') || '—' }}</td>
                    <td class="px-4 py-3"><span class="rounded-full px-2.5 py-1 text-xs font-semibold" :class="record.has_claim ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200' : 'bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-200'">{{ record.has_claim ? 'Payroll claim found — review' : 'Invoice expected' }}</span></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
        </div>
      </section>
    </template>
    </template>
  </div>

  <div
    v-if="claimRepairOpen && !isSettingsPage"
    class="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 p-4 backdrop-blur"
    @click.self="closeClaimRepairReview"
  >
    <div class="flex max-h-[90vh] w-full max-w-6xl flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-900">
      <div class="flex items-start justify-between gap-4 border-b border-slate-200 px-6 py-5 dark:border-slate-800">
        <div>
          <div class="text-xs font-semibold uppercase tracking-[0.2em] text-sky-600 dark:text-sky-300">
            Payroll safety check
          </div>
          <h3 class="mt-2 text-2xl font-semibold text-slate-900 dark:text-white">
            Review claim matching problems
          </h3>
          <p class="mt-1 max-w-3xl text-sm text-slate-500 dark:text-slate-400">
            Only claims with one matching staff assignment on the same reference, date and product can be selected. Applying a repair reopens the claim for approval.
          </p>
        </div>
        <button
          type="button"
          class="btn"
          :disabled="applyingClaimRepairs"
          @click.prevent="closeClaimRepairReview"
        >
          Close
        </button>
      </div>

      <div class="min-h-0 flex-1 overflow-y-auto px-6 py-5">
        <div
          v-if="loadingClaimRepairs"
          class="rounded-2xl border border-dashed border-slate-300 px-4 py-10 text-center text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400"
        >
          Checking stored claim matches against current personnel assignments...
        </div>

        <div
          v-else-if="claimRepairError"
          class="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-4 text-sm text-rose-700 dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-300"
        >
          <div>{{ claimRepairError }}</div>
          <button type="button" class="btn mt-3" @click.prevent="loadClaimMatchRepairs">
            Scan again
          </button>
        </div>

        <div
          v-else-if="claimRepairPreview.summary.issues === 0"
          class="rounded-2xl border border-emerald-200 bg-emerald-50 px-5 py-8 text-center dark:border-emerald-900/40 dark:bg-emerald-950/20"
        >
          <div class="text-lg font-semibold text-emerald-900 dark:text-emerald-100">
            No claim matching problems found
          </div>
          <p class="mt-1 text-sm text-emerald-700 dark:text-emerald-300">
            Every linked claim currently points to a job date containing that staff assignment.
          </p>
        </div>

        <div v-else class="space-y-5">
          <div class="grid gap-3 sm:grid-cols-3">
            <div class="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-800 dark:bg-slate-950/70">
              <div class="text-[11px] uppercase tracking-wide text-slate-500">Problems found</div>
              <div class="mt-1 text-2xl font-semibold text-slate-900 dark:text-white">{{ claimRepairPreview.summary.issues }}</div>
            </div>
            <div class="rounded-2xl border border-sky-200 bg-sky-50 px-4 py-3 dark:border-sky-900/40 dark:bg-sky-950/20">
              <div class="text-[11px] uppercase tracking-wide text-sky-700 dark:text-sky-300">Unambiguous</div>
              <div class="mt-1 text-2xl font-semibold text-sky-900 dark:text-sky-100">{{ claimRepairPreview.summary.repairable }}</div>
            </div>
            <div class="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 dark:border-amber-900/40 dark:bg-amber-950/20">
              <div class="text-[11px] uppercase tracking-wide text-amber-700 dark:text-amber-300">Manual review</div>
              <div class="mt-1 text-2xl font-semibold text-amber-900 dark:text-amber-100">{{ claimRepairPreview.summary.manual_review }}</div>
            </div>
          </div>

          <div
            v-if="activeRun?.status !== 'draft'"
            class="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-900/40 dark:bg-amber-950/20 dark:text-amber-200"
          >
            This payroll run is no longer a draft. Matching problems can be reviewed, but repairs cannot be applied.
          </div>

          <div class="space-y-3">
            <article
              v-for="repair in claimRepairRows"
              :key="repair.payroll_run_claim_id"
              class="rounded-2xl border px-4 py-4"
              :class="repair.repairable
                ? 'border-sky-200 bg-white dark:border-sky-900/50 dark:bg-slate-950/60'
                : 'border-amber-200 bg-amber-50/40 dark:border-amber-900/40 dark:bg-amber-950/10'"
            >
              <div class="flex items-start gap-3">
                <input
                  type="checkbox"
                  class="mt-1 h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                  :checked="selectedClaimRepairIds.has(repair.payroll_run_claim_id)"
                  :disabled="!repair.repairable || applyingClaimRepairs || activeRun?.status !== 'draft'"
                  @change="toggleClaimRepairSelection(repair)"
                />
                <div class="min-w-0 flex-1">
                  <div class="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <div class="font-semibold text-slate-900 dark:text-white">
                        {{ repair.staff.name }}
                      </div>
                      <div class="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                        Payroll claim #{{ repair.payroll_run_claim_id }} · Source claim #{{ repair.source_claim_id }} · {{ repair.claim.product_name || 'No product' }}
                      </div>
                    </div>
                    <span
                      class="rounded-full px-2.5 py-1 text-xs font-semibold"
                      :class="repair.repairable
                        ? 'bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-200'
                        : 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200'"
                    >
                      {{ repair.repairable ? 'Ready to repair' : 'Manual review only' }}
                    </span>
                  </div>

                  <div class="mt-4 grid gap-3 lg:grid-cols-2">
                    <div class="rounded-2xl border border-rose-200 bg-rose-50/70 px-4 py-3 dark:border-rose-900/40 dark:bg-rose-950/20">
                      <div class="text-[11px] font-semibold uppercase tracking-wide text-rose-700 dark:text-rose-300">Current incorrect match</div>
                      <div class="mt-2 font-medium text-slate-900 dark:text-white">
                        {{ repair.current_job_date.reference.code }} · job date #{{ repair.current_job_date.id }}
                      </div>
                      <div class="mt-1 text-sm text-slate-600 dark:text-slate-300">
                        {{ formatDate(repair.current_job_date.date) }} · {{ repair.current_job_date.start_time || '—' }}–{{ repair.current_job_date.finish_time || '—' }}
                      </div>
                      <div v-if="repair.current_job_date.location" class="mt-1 text-xs text-slate-500 dark:text-slate-400">
                        {{ repair.current_job_date.location }}
                      </div>
                    </div>

                    <div
                      v-if="repair.suggested_job_date"
                      class="rounded-2xl border border-emerald-200 bg-emerald-50/70 px-4 py-3 dark:border-emerald-900/40 dark:bg-emerald-950/20"
                    >
                      <div class="text-[11px] font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-300">Proposed match</div>
                      <div class="mt-2 font-medium text-slate-900 dark:text-white">
                        {{ repair.suggested_job_date.reference.code }} · job date #{{ repair.suggested_job_date.id }}
                      </div>
                      <div class="mt-1 text-sm text-slate-600 dark:text-slate-300">
                        {{ formatDate(repair.suggested_job_date.date) }} · {{ repair.suggested_job_date.start_time || '—' }}–{{ repair.suggested_job_date.finish_time || '—' }}
                      </div>
                      <div class="mt-1 text-xs text-slate-500 dark:text-slate-400">
                        {{ repair.suggested_job_date.products.map((product) => product.name).join(', ') || 'Assigned personnel' }}
                      </div>
                      <div v-if="repair.suggested_job_date.location" class="mt-1 text-xs text-slate-500 dark:text-slate-400">
                        {{ repair.suggested_job_date.location }}
                      </div>
                    </div>

                    <div
                      v-else
                      class="rounded-2xl border border-dashed border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950/20 dark:text-amber-200"
                    >
                      No unique replacement could be selected automatically.
                      <span v-if="repair.candidate_job_dates.length">
                        {{ repair.candidate_job_dates.length }} possible matches were found.
                      </span>
                    </div>
                  </div>

                  <div v-if="repair.warnings.length" class="mt-3 space-y-1">
                    <div
                      v-for="warning in repair.warnings"
                      :key="warning.code"
                      class="text-xs text-amber-700 dark:text-amber-300"
                    >
                      • {{ warning.message }}
                    </div>
                  </div>
                  <div v-if="repair.blocking_reasons.length" class="mt-3 space-y-1">
                    <div
                      v-for="reason in repair.blocking_reasons"
                      :key="reason"
                      class="text-xs text-rose-700 dark:text-rose-300"
                    >
                      • {{ reason }}
                    </div>
                  </div>
                </div>
              </div>
            </article>
          </div>
        </div>
      </div>

      <div class="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 px-6 py-4 dark:border-slate-800">
        <button
          type="button"
          class="btn"
          :disabled="!repairableClaimRows.length || applyingClaimRepairs || activeRun?.status !== 'draft'"
          @click.prevent="toggleAllRepairableClaims"
        >
          {{ allRepairableClaimsSelected ? 'Clear selection' : 'Select all unambiguous' }}
        </button>
        <div class="flex items-center gap-3">
          <span class="text-sm text-slate-500 dark:text-slate-400">
            {{ selectedClaimRepairCount }} selected
          </span>
          <button
            type="button"
            class="btn"
            :disabled="selectedClaimRepairCount === 0 || applyingClaimRepairs || activeRun?.status !== 'draft'"
            @click.prevent="applySelectedClaimRepairs"
          >
            {{ applyingClaimRepairs ? 'Applying repairs...' : 'Repair selected matches' }}
          </button>
        </div>
      </div>
    </div>
  </div>

  <div
    v-if="auditOpen && !isSettingsPage"
    class="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/70 p-4 backdrop-blur"
    @click.self="closeAudit"
  >
    <div
      class="w-full max-w-3xl rounded-3xl border border-slate-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-900"
    >
      <div
        class="flex items-start justify-between gap-4 border-b border-slate-200 px-6 py-5 dark:border-slate-800"
      >
        <div class="min-w-0">
          <div class="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">
            Delivery Audit
          </div>
          <h3 class="mt-2 text-xl font-semibold text-slate-900 dark:text-white">
            {{ auditData?.payslip?.employee_name || "Payslip audit" }}
          </h3>
          <div class="mt-1 text-sm text-slate-500 dark:text-slate-400">
            {{ auditData?.payslip?.recipient_email || "No staff email on file" }}
          </div>
        </div>
        <button type="button" class="btn" @click.prevent="closeAudit">
          Close
        </button>
      </div>

      <div class="max-h-[75vh] overflow-y-auto px-6 py-5">
        <div
          v-if="auditLoading"
          class="text-sm text-slate-500 dark:text-slate-400"
        >
          Loading audit trail...
        </div>
        <div
          v-else-if="!auditData?.payslip?.deliveries?.length"
          class="rounded-2xl border border-dashed border-slate-300 px-4 py-6 text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400"
        >
          No delivery activity has been recorded for this payslip yet.
        </div>
        <div v-else class="space-y-4">
          <article
            v-for="delivery in auditData.payslip.deliveries"
            :key="delivery.id"
            class="rounded-3xl border border-slate-200 bg-slate-50 px-4 py-4 dark:border-slate-800 dark:bg-slate-950/70"
          >
            <div class="flex flex-wrap items-start justify-between gap-3">
              <div class="min-w-0">
                <div class="text-sm font-semibold text-slate-900 dark:text-white">
                  {{ delivery.recipient_email }}
                </div>
                <div class="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  Attempt {{ delivery.id }} · {{ delivery.provider }}
                  <span v-if="delivery.sent_by"> · Sent by {{ delivery.sent_by }}</span>
                </div>
              </div>
              <span
                class="rounded-full px-2.5 py-1 text-[11px] font-semibold capitalize"
                :class="auditTone(delivery.status)"
              >
                {{ delivery.status.replace("_", " ") }}
              </span>
            </div>

            <div class="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
              <div class="rounded-2xl bg-white px-3 py-3 dark:bg-slate-900">
                <div class="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  Sent
                </div>
                <div class="mt-1 text-sm font-medium text-slate-900 dark:text-white">
                  {{ formatDateTime(delivery.sent_at) }}
                </div>
              </div>
              <div class="rounded-2xl bg-white px-3 py-3 dark:bg-slate-900">
                <div class="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  Delivered
                </div>
                <div class="mt-1 text-sm font-medium text-slate-900 dark:text-white">
                  {{ formatDateTime(delivery.delivered_at) }}
                </div>
              </div>
              <div class="rounded-2xl bg-white px-3 py-3 dark:bg-slate-900">
                <div class="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  Last Open
                </div>
                <div class="mt-1 text-sm font-medium text-slate-900 dark:text-white">
                  {{ formatDateTime(delivery.last_opened_at) }}
                </div>
              </div>
              <div class="rounded-2xl bg-white px-3 py-3 dark:bg-slate-900">
                <div class="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  Opens
                </div>
                <div class="mt-1 text-sm font-medium text-slate-900 dark:text-white">
                  {{ delivery.opened_count ?? 0 }}
                </div>
              </div>
            </div>

            <div
              v-if="delivery.last_error"
              class="mt-4 rounded-2xl border border-rose-200 bg-rose-50 px-3 py-3 text-sm text-rose-700 dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-300"
            >
              {{ delivery.last_error }}
            </div>

            <div class="mt-4">
              <div class="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">
                Events
              </div>
              <div class="mt-3 space-y-2">
                <div
                  v-for="event in delivery.events"
                  :key="event.id"
                  class="flex items-start justify-between gap-3 rounded-2xl border border-slate-200 bg-white px-3 py-3 dark:border-slate-800 dark:bg-slate-900"
                >
                  <div>
                    <div class="text-sm font-medium capitalize text-slate-900 dark:text-white">
                      {{ event.event.replace("_", " ") }}
                    </div>
                    <div class="mt-1 text-xs text-slate-500 dark:text-slate-400">
                      {{ formatDateTime(event.occurred_at) }}
                    </div>
                  </div>
                  <span
                    class="rounded-full px-2.5 py-1 text-[11px] font-semibold capitalize"
                    :class="auditTone(event.event)"
                  >
                    {{ event.event.replace("_", " ") }}
                  </span>
                </div>
              </div>
            </div>
          </article>
        </div>
      </div>
    </div>
  </div>

  <aside
    v-if="timesheetImagePreview"
    class="fixed inset-y-0 right-0 z-50 flex flex-col border-l border-slate-300 bg-slate-950 shadow-2xl dark:border-slate-700"
    :style="{ width: `${timesheetImagePreviewWidth}px` }"
    aria-label="Original timesheet image preview"
  >
    <button
      type="button"
      class="absolute inset-y-0 -left-1.5 z-20 w-3 cursor-col-resize touch-none bg-transparent focus:outline-none"
      title="Drag to resize preview pane"
      aria-label="Resize image preview pane"
      @pointerdown.prevent="startTimesheetImagePreviewResize"
    >
      <span class="absolute inset-y-0 left-1/2 w-px bg-slate-400/80 dark:bg-slate-600"></span>
    </button>

    <header class="flex shrink-0 items-center justify-between gap-2 border-b border-slate-700 bg-slate-900 px-3 py-2 text-white">
      <div class="min-w-0">
        <div class="truncate text-xs font-semibold" :title="timesheetImagePreview.name">
          {{ timesheetImagePreview.name }}
        </div>
        <div class="text-[10px] text-slate-400">Original timesheet</div>
      </div>
      <button
        type="button"
        class="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-lg text-slate-300 transition hover:bg-slate-700 hover:text-white"
        title="Close preview (Esc)"
        aria-label="Close image preview"
        @click.prevent="closeTimesheetImagePreview"
      >×</button>
    </header>

    <div class="flex shrink-0 flex-wrap items-center justify-center gap-1 border-b border-slate-700 bg-slate-900/95 px-2 py-2 text-white">
      <button type="button" class="inline-flex h-8 min-w-8 items-center justify-center rounded-lg bg-slate-800 px-2 text-sm hover:bg-slate-700 disabled:opacity-40" :disabled="timesheetImageZoom <= 0.25" title="Zoom out" aria-label="Zoom out" @click.prevent="adjustTimesheetImageZoom(-0.25)">−</button>
      <span class="min-w-14 text-center text-xs tabular-nums text-slate-300">{{ Math.round(timesheetImageZoom * 100) }}%</span>
      <button type="button" class="inline-flex h-8 min-w-8 items-center justify-center rounded-lg bg-slate-800 px-2 text-sm hover:bg-slate-700 disabled:opacity-40" :disabled="timesheetImageZoom >= 4" title="Zoom in" aria-label="Zoom in" @click.prevent="adjustTimesheetImageZoom(0.25)">＋</button>
      <span class="mx-1 h-5 w-px bg-slate-700"></span>
      <button type="button" class="inline-flex h-8 min-w-8 items-center justify-center rounded-lg bg-slate-800 px-2 text-sm hover:bg-slate-700" title="Rotate left" aria-label="Rotate image left" @click.prevent="rotateTimesheetImage(-90)">↶</button>
      <button type="button" class="inline-flex h-8 min-w-8 items-center justify-center rounded-lg bg-slate-800 px-2 text-sm hover:bg-slate-700" title="Rotate right" aria-label="Rotate image right" @click.prevent="rotateTimesheetImage(90)">↷</button>
      <button type="button" class="inline-flex h-8 items-center justify-center rounded-lg bg-slate-800 px-2 text-[11px] font-semibold hover:bg-slate-700" title="Reset zoom and rotation" @click.prevent="resetTimesheetImageView">Reset</button>
    </div>

    <div class="min-h-0 flex-1 overflow-auto bg-slate-800 p-4">
      <div
        v-if="timesheetImagePreviewError"
        class="m-auto max-w-sm rounded-xl border border-rose-500/40 bg-slate-900 p-4 text-center text-sm text-slate-200"
      >
        <div>This image format cannot be previewed by the browser.</div>
        <a :href="timesheetImagePreview.url" target="_blank" rel="noopener" class="mt-3 inline-flex rounded-lg bg-sky-600 px-3 py-2 font-semibold text-white hover:bg-sky-500">Open original</a>
      </div>
      <div v-else class="flex min-h-full min-w-full items-center justify-center">
        <img
          :src="timesheetImagePreview.url"
          :alt="timesheetImagePreview.name"
          draggable="false"
          class="block h-auto max-w-none select-none shadow-2xl transition-transform duration-150"
          :style="{
            width: `${timesheetImageZoom * 100}%`,
            transform: `rotate(${timesheetImageRotation}deg)`,
            transformOrigin: 'center center',
          }"
          @error="timesheetImagePreviewError = true"
        />
      </div>
    </div>
  </aside>

  <div
    v-if="toast.visible"
    class="fixed bottom-6 right-6 z-50 flex items-center gap-2 rounded-lg px-4 py-3 text-sm font-medium text-white shadow-xl"
    :class="toast.tone === 'error' ? 'bg-rose-600' : 'bg-emerald-600'"
  >
    <svg
      v-if="toast.tone !== 'error'"
      class="h-4 w-4"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M5 13l4 4L19 7"
      />
    </svg>
    <svg
      v-else
      class="h-4 w-4"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
    <span>{{ toast.message }}</span>
  </div>
</template>
